/**
 * useBimScene
 *
 * Sets up a Three.js scene with a model loader inside a container element — an
 * IFC through web-ifc, a GLB/glTF through three's own GLTFLoader (card
 * t_14696d19). The scene is cached per HUB CONNECTION KEY, not per dashboard:
 * the model is the hub's (`t_hub_bim_model`), so the 3D page, an embedded view
 * and a live swap share one parsed scene, and a hub switch gets a fresh one.
 * Rendering is suspended whenever the view is not active so idle pages do not
 * keep a permanent RAF loop alive.
 */

import { ref, computed, shallowRef, watch, onUnmounted } from 'vue';
import { routedFetch } from '../services/apiRouter';
import { serverNow } from '../services/clockSync';
import { getHubSessionKeys } from '../config/runtime';
import {
  loadHubBimViewSettings,
  saveHubBimViewSettings,
  type HubBimRoomViewFilter,
  type HubBimViewSettings,
} from '../services/bimViewSettings';
import { useAuthStore } from '../stores/auth';
import { autoSceneLighting, localCivilDate, localSolarHour } from '../utils/bimAutoLighting';
import {
  paintBimDim,
  paintBimPrecipitation,
  paintBimSky,
  paintMoon,
  paintSunDisc,
  planBimSky,
  sunDirectionAt,
  MOON_DISC_EXTENT,
  SUN_DISC_EXTENT,
  type BimSkyDisc,
  type BimSkyPlan,
} from '../utils/bimSky';
import { ifcFrameGroupOffset } from '../utils/bimFrame';
import { filterUnclippedHits } from '../utils/bimClip';
import { isRenderStall, lightPresetOf, LIGHT_PRESET_FACTOR, type LightPreset } from '../utils/bimFixtureLights';
import { BIM_ROOM_LABEL_MODES, type BimRoomLabelMode } from '../utils/bimRoomPins';
import type { PoseFacts } from '../utils/bimPoseGuard';
import { isGltfFormat, isUsdzFormat, resolveBimModelFormat, type BimModelFormat } from '../utils/bimModelFormat';
import { buildGltfModel, buildUsdModel, type BuiltBimModel } from '../utils/bimSceneBuild';
import type { BimObjectInfo } from '../utils/bimObjects';
import {
  SIM_CAM_DISTANCE_M,
  NAV_EYE_HEIGHT_M,
  NAV_EYE_Y_SMOOTHING,
  NAV_PROBE_INTERVAL_MS,
  NAV_PROBE_SKIP_M,
  NAV_STEP_UP_MAX_M,
  NAV_SUPPORT_RADIUS_M,
  NAV_SUPPORT_RING,
  NAV_VELOCITY_SMOOTHING,
  NAV_WHEEL_WALK_DECAY_PER_S,
  NAV_VIEW_TOGGLE_KEY,
  NAV_WALK_RADIUS_M,
  NAV_WALL_KNEE_M,
  NAV_WALL_WAIST_M,
  NAV_JUMP_KEYS,
  applyLookDelta,
  applyTurn,
  approach,
  approachPoint,
  applyDroneOrbit,
  applyDroneLift,
  bodyHeadingYaw,
  clampDronePitch,
  clampPitch,
  droneCameraLook,
  droneDragDeltas,
  droneZoomFromPressed,
  easeDroneOrbitOffset,
  turnYawDegByDrag,
  yawDegFromLook,
  zoomDroneDistanceByKeys,
  zoomDroneDistanceByWheel,
  wheelWalkImpulse,
  defaultWalkPose,
  floorLevelsFromSurfaces,
  horizontalNormal,
  isFirstPersonMode,
  isFloorFinishType,
  isPlateLikeSurfaceBox,
  isWaterSurface,
  isPovCamera,
  isStill,
  isThirdPersonMode,
  isWalkerMode,
  isWalkSurfaceName,
  isWalkSurfaceType,
  isWallMeshType,
  droneSteerFromPressed,
  simViewOpeningLook,
  isDroneView,
  levelLook,
  lookFromDirection,
  navInputForMode,
  turnYawDeg,
  withoutDroneLetters,
  navSpeed,
  normalizeDirection,
  droneCameraPose,
  fovByWheel,
  oppositeSimView,
  pickFootprintSupport,
  povCameraPose,
  isBimNavMode,
  isBimSimView,
  readStoredSimView,
  readStoredNavMode,
  resolveWalkFeetY,
  slideAlongWall,
  startJump,
  stepJump,
  storeSimView,
  storeNavMode,
  walkCameraY,
  walkCutBand,
  walkDirection,
  wallBlocksStep,
  WALK_CUT_HEAD_MARGIN_M,
  type BimSimView,
  type BimNavMode,
  type NavJump,
  type NavLook,
  type NavMoveInput,
  type NavPoint,
} from '../utils/bimNav';
import { BIM_AVATAR_TURN_RATE_DEG, approachYawDeg } from '../utils/bimAvatars';
import type { PoseBox } from '../utils/bimPoseGuard';
import type { ViewpointPose } from '../utils/bimViewpointPresets';
import type { BimViewHandoff } from '../utils/bimViewHandoff';
import { BIM_CAMERA_SETTLE_MS, cameraMotionHidesOverlays, cameraMotionIsNoise } from '../utils/bimCameraMotion';
import * as THREE from 'three';

/**
 * The model-transparency slider's own range, in percent (owner, 2026-10-17:
 * *"can we hve model transparency set like ve have model cut ?"*). The floor is
 * deliberately not 0: a transparent-mode that draws exactly the opaque model
 * would read as a broken control. It is the CUT's own pattern — the mode picks
 * the material blend, the slider picks how much of it shows.
 */
export const BIM_TRANSPARENCY_MIN_PERCENT = 10;
export const BIM_TRANSPARENCY_MAX_PERCENT = 90;
export const BIM_TRANSPARENCY_DEFAULT_PERCENT = 40;

/**
 * The authored look of every material transparency has touched: what switching
 * the mode off (and a model swap) restores. A shared material appears once;
 * `opacity` is the authored value, so ghosting multiplies rather than replaces
 * it (glass stays glassier than the wall around it).
 */
interface MaterialGhostLook {
  opacity: number;
  transparent: boolean;
  depthWrite: boolean;
}

const materialGhostLook = new WeakMap<THREE.Material, MaterialGhostLook>();

/** Horizontal azimuth (radians) of the most recently active BIM camera.
 *  Updated on every camera change; read by OttoDog to align facing direction. */
export const bimCameraAzimuth = ref(0);
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { CSS2DRenderer, CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js';

export interface IfcElementInfo {
  expressID: number;
  typeName: string;
  name: string;
  /** The line's own `Description` ('' when the IFC carries none). */
  description: string;
  storeyName: string;
  storeyElevation: number;
}

export interface BimSceneState {
  scene: THREE.Scene;
  /**
   * Drawn after `scene` with the section cut lifted (see `renderOverlayScene`).
   * The move/rotate gizmos live here: `TransformControls` helpers obey the
   * renderer's GLOBAL clipping planes with no per-material opt-out, so a ceiling
   * cut sliced the arrows the operator was dragging (owner, 2026-10-20: *"the
   * move widget in 3d controls are cut off by the ceiling cut plane.. they
   * shouldnt be cut at all"*).
   */
  overlayScene: THREE.Scene;
  /**
   * Drawn BEFORE `scene`, with the section cut lifted (see `renderSkyBodies`):
   * the sun and the moon. They are not part of the building, so the cut has no
   * business slicing them — and, like the gizmo helpers, a sprite obeys the
   * renderer's GLOBAL planes with no per-material opt-out.
   */
  skyScene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  /**
   * The operator's intensity preset, as a multiplier the light pool applies
   * (`LIGHT_PRESET_FACTOR`). A NUMBER, so the layer reads it without a watcher.
   */
  lightIntensityFactor: number;
  /**
   * How hot a lit fixture's OWN body reads — the emissive diffuser and its additive halo,
   * which the light itself does not touch.
   *
   * Owner, 2026-10-21: *"still too bright.. i set the lux to 1, it does reduce, but still
   * much too bright"*. Lux 1 dimmed the pool and the room barely changed, because what he
   * was looking at is the fixture glowing, not the light it throws. This is that dial.
   */
  glowScale: number;
  /**
   * EMA of the RENDERED frame time, ms. A FUNCTION on purpose: it is read by the
   * fixture-light budget (`adaptiveLightBudget`) and it changes every frame, so a
   * number here would write into reactive state 60 times a second.
   */
  frameMs: () => number;
  renderer: THREE.WebGLRenderer;
  labelRenderer: CSS2DRenderer;
  controls: OrbitControls;
  raycaster: THREE.Raycaster;
  grid: THREE.GridHelper;
  sun: THREE.DirectionalLight;
  hemi: THREE.HemisphereLight;
  ambient: THREE.AmbientLight;
}

// ─── Module-level scene cache ─────────────────────────────────────────────────
// Keyed by the hub's connection key. Survives component unmount/remount so the
// model never reloads on navigation, and it is shared by every surface that
// shows the model (the 3D page, an embed) — one parsed house, one renderer.

function createEntry() {
  // The active navigation mode, restored per user (card t_9cf7e787). Read once
  // per dashboard: the scene outlives the component, so the mode survives a
  // navigation exactly as the camera does.
  const navMode = ref<BimNavMode>(
    (() => {
      try {
        return readStoredNavMode(typeof localStorage === 'undefined' ? null : localStorage);
      } catch {
        return 'orbit' as BimNavMode;
      }
    })(),
  );
  // SIMULATION mode's camera view (card t_01ebef40) — the DRONE over the body (what the
  // mode opens in) or POV through its eyes. Stored per user like the mode itself,
  // and read once with it.
  const simView = ref<BimSimView>(
    (() => {
      try {
        return readStoredSimView(typeof localStorage === 'undefined' ? null : localStorage);
      } catch {
        return 'drone' as BimSimView;
      }
    })(),
  );
  const pointerLocked = ref(false);

  // ── The backdrop canvas: ONE shape, for ever ───────────────────────────────
  // It used to be 1×512 for the gradient and the viewport's aspect for the weather
  // sky, i.e. the canvas was RESIZED every time the backdrop switched painters.
  // A canvas that is already the source of a live texture cannot change size on
  // that texture's fast path: Chromium copies it with
  // `glCopySubTextureCHROMIUM`, which fails with
  //   `GL_INVALID_VALUE: Offset overflows texture dimensions`
  // and the texture keeps its old pixels — the sky was painted on the canvas and
  // the screen kept showing the gradient ("only blue, no matter the time of day",
  // owner 2026-10-23). One shape means one texture size, and every later paint is
  // a same-size upload.
  const gradCanvas = document.createElement('canvas');
  gradCanvas.width = BACKDROP_WIDTH;
  gradCanvas.height = BACKDROP_HEIGHT;
  const gradCtx = gradCanvas.getContext('2d')!;
  // No texture: this canvas is a DOM element behind the (transparent) renderer,
  // so the weather sky animates in 2D and the HOUSE does not re-render for it.

  return {
    state: shallowRef<BimSceneState | null>(null),
    modelLoaded: ref(false),
    modelLoading: ref(false),
    modelError: ref<string | null>(null),
    modelMeshes: shallowRef<THREE.Mesh[]>([]),
    /**
     * Bumped on every finished load — a first load and a live swap alike. The
     * layout reads it to re-seat everything that was derived from the previous
     * meshes (the avatar outline, the widget occlusion cache). See
     * `loadModelFromUrl`'s `preserveView`.
     */
    modelRevision: ref(0),
    /**
     * The URL whose bytes are on screen right now (`null` until a model loads).
     * The scene is cached across navigations, so this is the one fact that
     * survives a remount and tells the live poll whether the screen is the
     * revision the hub serves (card t_4e78c7af).
     */
    loadedModelUrl: ref<string | null>(null),
    /**
     * The format of the model on screen — `'ifc'`, `'glb'` or `'gltf'` (card
     * t_14696d19). The loader's own split, reported to the layout and the rigs:
     * it is what says whether the element list above is supposed to be empty.
     */
    modelFormat: ref<BimModelFormat | null>(null),
    /** The group every loaded mesh is parented to (null until a model loads). */
    modelGroup: shallowRef<THREE.Group | null>(null),
    cameraSerial: ref(0),
  /** EMA of the rendered frame time, ms — read by the fixture-light budget. */
  frameMsAvg: 0,
    cameraInteracting: ref(false),
    /**
     * The view's own MOTION gate (card t_72106dc1): true from the first frame
     * whose camera move passed the threshold until `BIM_CAMERA_SETTLE_MS` after
     * the last one. The layout hides the widget overlay layer off this ref and
     * stops re-projecting it — 69 pins over a 20 MB model, re-projected every
     * interaction frame, is the lag the owner reported (*"while moving through
     * model, or orbiting we should immediately hide the widgets"*).
     */
    cameraMoving: ref(false),
    cameraSettleTimer: null as ReturnType<typeof setTimeout> | null,
    /** The pose the last settled move is measured from (the overlay's own frame). */
    cameraSettledPos: new THREE.Vector3(),
    cameraSettledQuat: new THREE.Quaternion(),
    /** The previous frame's pose — what "is this frame moving?" is measured against. */
    cameraLastPos: new THREE.Vector3(),
    cameraLastQuat: new THREE.Quaternion(),
    gridVisible: ref(true),
    /**
     * The OBJECT layer of a GLB/glTF model (card t_cb0aa19b): the list a device
     * anchor and the panel read, plus the key → meshes map. Empty for an IFC.
     */
    bimObjects: ref<BimObjectInfo[]>([]),
    objectMeshes: ref<Map<string, THREE.Mesh[]>>(new Map()),
    elementMeshes: ref<Map<number, THREE.Mesh[]>>(new Map()),
    /**
     * Elements the LIVE DEVICE LAYER is standing in for (card t_f81aa9a1): every
     * baked shade twin the app renders itself. Owned by the layer, so it must
     * survive a re-serve and a model swap without the twin popping back over its
     * live group.
     */
    liveHiddenElements: ref<Set<number>>(new Set()),
    ifcElements: ref<IfcElementInfo[]>([]),
    modelBoundsY: ref({ min: 0, max: 10 }),
    clipEnabled: ref(false),
    clipHeightY: ref(0),
    /** The bottom half of the cut range — the storey's floor. */
    clipBottomY: ref(0),
    clipPlane: new THREE.Plane(new THREE.Vector3(0, -1, 0), 0),
    /**
     * The cut's second plane (owner, 2026-10-18: *"we would need to implement
     * bottom cut as well"*): normal UP, `constant = -bottomY`, so three keeps
     * `y >= bottomY` — the mirror of `clipPlane`, which keeps `y <= topY`.
     * Together they show one storey rather than only opening the house from the
     * top.
     */
    clipPlaneBottom: new THREE.Plane(new THREE.Vector3(0, 1, 0), 0),
    /** The model's see-through mode and its percent (the cut's own pair). */
    transparencyEnabled: ref(false),
    transparencyPercent: ref(BIM_TRANSPARENCY_DEFAULT_PERCENT),
    /** The sun's shadows (the renderer's shadow map) — a view filter, not a look. */
    shadowsEnabled: ref(true),
    /** The devices' own lights: fixture glow and their pools (both layers). */
    lightsEnabled: ref(true),
    /**
     * A VISITING surface's own answer for the two filters — a room view has its
     * own shadows/lights switches, and standing in a room must not rewrite the
     * 3D page's taste (nor the page's taste, the room's). `null` = nobody is
     * borrowing, the page's values stand.
     */
    shadowsBorrow: ref<boolean | null>(null),
    lightsBorrow: ref<boolean | null>(null),
    /** The same borrow, for the sun's daylight and the weather sky. */
    sunBorrow: ref<boolean | null>(null),
    weatherBorrow: ref<boolean | null>(null),
    /** The ROOM pages' filter — one blob, every room page (see `BimRoomViewFilter`). */
    roomView: ref<HubBimRoomViewFilter>({ ...DEFAULT_ROOM_VIEW_FILTER }),
    animationId: { value: 0 },
    flyToAnimationId: { value: 0 },
    cameraSerialFrameId: { value: 0 },
    lastFrameTime: 0,
    needsRender: true,
    suspended: true,
    cameraInteractionTimeout: null as ReturnType<typeof setTimeout> | null,
    wheelListener: null as ((e: WheelEvent) => void) | null,
    timeOfDay: ref(12),
    sunEnabled: ref(true),
    /**
     * Whether the sun's time follows the clock. On by default — a 3D view of a
     * real house should open on the real hour, not on a stored one — and turned
     * off the moment the operator moves the slider or picks a preset, because a
     * time the user chose is theirs.
     */
    sunClockSynced: ref(true),
    /** Whether exposure and interior fill follow the sun (see `autoSceneLighting`). */
    autoLighting: ref(true),
    /** Whether the live weather is painted behind the model instead of the gradient. */
    weatherSkyEnabled: ref(false),
    exposure: ref(1.0),
    /** 'low' | 'mid' | 'high' — how hard the fixtures drive the room (LIGHT_PRESET_FACTOR). */
    lightPreset: ref<LightPreset>('mid'),
    /** 0…1.5, applied to a fixture's emissive body and its halo. */
    glowScale: ref(0.35),
    ambientBoost: ref(1.0),
    /** The live sky's weather: the OWM code, and the moon's phase (0–1). */
    weatherSky: { condition: null as string | null, moonPhase: null as number | null },
    /**
     * The bodies the last sky plan put up. The sun and moon sprites are placed
     * from this, not from the painted canvas — so the plan decides *what* is in
     * the sky and the scene decides *where*.
     */
    weatherSkyDiscs: [] as BimSkyDisc[],
    /**
     * What the last sky plan has falling (`null` when nothing is). The sky's
     * animation loop draws only this over the cached still sky, so moving rain
     * costs a blit and a few strokes rather than the whole picture.
     */
    weatherSkyPrecipitation: null as BimSkyPlan['precipitation'],
    /** The hour's dark film, re-applied over the drops each frame (see `paintBimDim`). */
    weatherSkyDim: 0,
    /** The still sky the falling drops are drawn over — repainted only when the plan changes. */
    skyStillCanvas: null as HTMLCanvasElement | null,
    /** The falling drops' animation: its rAF handle, its clock and the time it has run. */
    precipitationFrameId: { value: 0 },
    precipitationLastFrameAt: 0,
    precipitationSeconds: 0,
    /** The motion preference, asked once (the list follows the system setting). */
    reducedMotionQuery: null as MediaQueryList | null,
    /** The 60 s tick that moves the sun with the clock; null when not synced. */
    sunClockTimer: null as ReturnType<typeof setInterval> | null,
    /**
     * The hub's copy of the view settings — the paced write and the one read.
     *
     * Owner, 2026-10-29: *"sun offset is not preserved on server"*. `localStorage`
     * stays as the render cache (the first frame is painted from it), and this is
     * the sync: one debounced PUT per pause in the operator's editing, and one GET
     * per visit that adopts what the house already says.
     */
    hubSync: {
      /** The pending write; null when nothing is waiting. */
      timer: null as ReturnType<typeof setTimeout> | null,
      /** A PUT is running — a change that lands now queues exactly one more. */
      inFlight: false,
      /** A change arrived while a PUT was running. */
      again: false,
      /** A read is in the air — a second init must not double-fetch. */
      reading: false,
      /** The operator changed something — a late read must not undo it. */
      touched: false,
      /** Adopting the hub's values: the setters must not echo them back. */
      applying: false,
      /** The last blob handed to the hub, so an unchanged tick writes nothing. */
      lastPushed: '',
    },
    northAngle: ref(0), // degrees, clockwise — rotates the entire sun orbit
    cameraAzimuth: ref(0), // degrees, updated each frame from camera look direction
    /** Whether the sun's ray is drawn into the model (the panel's toggle). */
    showSunRay: ref(false),
    /** Whether the compass overlay is drawn on the canvas (the View panel's toggle). */
    showCompassOverlay: ref(true),
    /**
     * The LAYOUT's half of the view settings — the room pills' mode and the view
     * filter. It is a ref so `BimLayout` can follow the hub's copy: the values
     * are stored, patched and adopted with the rest of the blob.
     */
    uiPrefs: ref<BimUiViewPrefs>({ ...DEFAULT_UI_VIEW_PREFS }),
    /** The ray itself: a plain line from the sun's position to the model's centre. */
    sunRay: null as THREE.Line | null,
    /**
     * The sun and moon as real objects in the sky, out along the sun's own
     * direction — so they sit where the light and the sun arrow say, and move
     * with the view (see `updateSkyDiscs`).
     */
    sunDisc: null as THREE.Sprite | null,
    moonDisc: null as THREE.Sprite | null,
    /** The moon phase the moon's texture was drawn for, so it is drawn once. */
    moonDiscPhase: null as number | null,
    latitude: ref(46.0),
    solarDate: ref(new Date().toISOString().slice(0, 10)),
    modelCenter: new THREE.Vector3(0, 0, 0),
    modelCenterInfo: ref({ x: 0, y: 0, z: 0 }),
    modelSizeInfo: ref({ x: 0, y: 0, z: 0 }),
    pressedKeys: new Set<string>(),
    keydownHandler: null as ((e: KeyboardEvent) => void) | null,
    keyupHandler: null as ((e: KeyboardEvent) => void) | null,
    keyboardListenersAttached: false,

    // ─── Navigation modes (card t_9cf7e787; FLOAT retired, t_493c5f58) ───────
    // ORBIT is the turntable this viewer has always been; GRAVITY is walking at
    // eye height, clamped to the model's own floors; SIMULATION walks the same way
    // with the camera off the driven body. The pure rules live in
    // utils/bimNav.ts; `groundMode` stays as the old spelling of GRAVITY (saved
    // viewpoints carry it).
    navMode,
    groundMode: computed(() => navMode.value === 'gravity'),
    /**
     * SIMULATION mode's camera view (card t_01ebef40): 'drone' (over the body, the view
     * the mode opens in) or 'pov' (through its eyes). Persisted like the mode, so
     * a user's choice survives a reload.
     */
    simView,
    /** The head direction the first-person modes look along. */
    look: { yaw: 0, pitch: 0 } as NavLook,
    /** The current movement velocity (m/s), damped toward the target. */
    velocity: { x: 0, y: 0, z: 0 } as NavPoint,
    /** Where the walker's feet are (null until a walk resolves them). */
    feetY: null as number | null,
    /** The walking surface the last probe measured (null = nothing underfoot). */
    supportY: null as number | null,
    /** What that surface IS — the mesh's own element, for the rigs' probe table. */
    supportHit: null as { name: string | null; typeName: string | null; expressID: number | null } | null,
    /** The probe cache: the (x, z, feetY) the cached support answer belongs to. */
    supportProbe: null as { x: number; z: number; y: number; at: number } | null,
    /**
     * The meshes a support probe may land on (card t_9cf7e787 + t_e86ce88e):
     * the floor finishes, the stair structure … and every PLATE-LIKE surface
     * the model carries (the site's terrain plates, decks and driveway slabs —
     * the surfaces the walker's "pin me to the nearest surface beneath" ask is
     * about). The probe is re-built per load, and only the support probe reads
     * it, so a wider set costs nothing until a walker walks.
     */
    walkSurfaces: [] as THREE.Mesh[],
    /** The same set, split for the log: which meshes matched by type/name. */
    walkSurfaceTyped: [] as THREE.Mesh[],
    /** …and which ones only by their plate-like box (the terrain class). */
    walkSurfacePlates: [] as THREE.Mesh[],
    /**
     * The model's WATER SURFACES (owner, 2026-10-21: *"would be good if our walk and simulation
     * modes would allow to sink in"*): a pool sheet is a horizontal plate, so the support probe
     * would happily walk the body ACROSS it. These meshes are skipped by identity in
     * `sampleWalkSupport` (`utils/bimNav.isWaterSurface`), which is what sends the probe on down
     * to the basin floor — a DROP, which `resolveWalkFeetY` follows.
     */
    waterMeshes: [] as THREE.Mesh[],
    /** The water's own height (world Y) — the level the body sinks below, for the rigs. */
    waterLevelY: null as number | null,
    /**
     * The meshes that BLOCK the walk (card t_565b83bf): the building's WALLS, read
     * off the loaded model's own element types. Doors are not in here on purpose:
     * measured on the served demo house they are not meshes at all but GAPS the
     * wall booleans cut out (14 openings 0.8–1.6 m wide), so walking through one
     * exercises nothing but this list's absence — and window glass is not here
     * either (owner, 2026-10-18: *"can only go through doors and windows"*). Cheap
     * by construction: 36 wall meshes / ~1.1 k triangles on this model, which is
     * why the forward probe may run every walking frame.
     */
    wallMeshes: [] as THREE.Mesh[],
    /** How many walk steps a wall refused (a separate count from `walkBlocked`). */
    wallsBlocked: 0,
    /**
     * The last wall the forward probe met — the rigs' own reading (the distance
     * is what decides between "blocked" and "clear").
     */
    wallHit: null as { name: string | null; distance: number; at: number } | null,
    /**
     * SIMULATION's jump (card t_565b83bf): the FAB's Space/J, as a hop. `null` while
     * the body is on the floor.
     */
    jump: null as NavJump | null,
    /** How many walk steps were refused (a surface more than one step up). */
    walkBlocked: 0,
    /** The model's own fallback levels (storeys + floor finishes), sorted. */
    walkLevels: [] as number[],
    /** The distance to re-seat the orbit target at when a first-person mode ends. */
    orbitDistance: 0,
    /**
     * The driven avatar's own pose in SIMULATION mode (card t_a41a75f6): its feet and
     * the facing it walks in. GRAVITY keeps the camera itself as the walker
     * (nothing to show); SIMULATION moves this point and hangs the camera behind it.
     */
    driven: { x: 0, y: 0, z: 0, yawDeg: 0, moving: false, running: false },
    /**
     * What the WHEEL asks the walker to do, in the same −1…1 the letter keys write
     * (owner, 2026-10-14: *"scroll should move the camera forwaerd, backward"*).
     * GRAVITY only, and it is an impulse rather than a position: it bleeds off at
     * `NAV_WHEEL_WALK_DECAY_PER_S`, so one notch is a step and stopping the wheel
     * stops the walking.
     */
    wheelWalkForward: 0,
    /**
     * Where GRAVITY last stood (owner, 2026-10-14: *"gravity mode should have its own
     * position.. if in orbit and you click on gravity and you are fare away from model
     * you are stuck cant move.. Graviti should change to last graviti position"*). A
     * walk has a place, and it is not wherever the turntable happens to be parked; this
     * is what re-entering the mode returns to. `null` until the first walk starts, and
     * then the model's own spawn (`defaultWalkPose`) is used instead.
     */
    walkerPose: null as (NavPoint & NavLook) | null,
    /**
     * Where the TURNTABLE was when the walk took the camera — the second of the two
     * places (owner, 2026-10-14: *"orbit and gravity should be 2 different pressets..
     * when exit the ground show the orbit postition"*). The walk's exit hands the camera
     * back to it, so stepping out of the body returns to the view the operator was
     * studying rather than to the back of the walker's head. One-shot: spent on the way
     * out, so a later mode change cannot resurrect a place orbit has left.
     */
    orbitPose: null as { x: number; y: number; z: number; targetX: number; targetY: number; targetZ: number } | null,
    /**
     * How far the DRONE camera asks to stand from its body. The wheel is the one
     * thing that writes it (owner, 2026-10-13: *"zoom with scoll"*), and the
     * distance is the drone's own promise — it never bends for a wall.
     */
    droneDistance: SIM_CAM_DISTANCE_M,
    /**
     * The drone's manual ORBIT, in radians (owner, 2026-10-14: *"make drone view
     * camera stay behind the person"*). The camera's yaw is the BODY's heading
     * plus this, so the offset is what the A/D letters move — the drag turns the
     * character itself instead — and it eases back to directly behind while the
     * character walks (`easeDroneOrbitOffset`).
     */
    droneOrbitOffset: 0,
    /** The lens the camera ships with — the POV zoom restores it on the way out. */
    baseFov: 60,
    /**
     * The section band SIMULATION borrowed, and where it had it (2026-10-13; the
     * whole mode, 2026-10-20). While a simulation runs the MODE is the plane's
     * author: the DRONE view cuts at the ceiling of the storey the body is in
     * (`driveDroneCut`), and the POV view cuts NOTHING — it is the walk's own view
     * from the body's eyes (owner, 2026-10-20: *"in simulate pov view there should
     * be no vertical cuts.. same as in walking"*). The operator's own band is
     * parked here on the way in and handed back untouched on the way out.
     */
    simCut: { saved: null as { enabled: boolean; height: number; bottom: number } | null, at: Number.NaN },
    /**
     * The building's STOREYS, as the layout's level document states them (owner,
     * 2026-10-19: *"cap to the floor levels"*). The drone's cut is the ceiling of
     * the storey the driven body stands in, so the scene needs the spans, not just
     * the floor lines it walks on (`walkLevels`). Written by the layout
     * (`setStoreySpans`); empty until a document arrives, and then nothing is cut.
     */
    storeySpans: [] as { floorY: number; ceilingY: number }[],
    /**
     * The mode SIMULATION was entered FROM (owner, 2026-10-19: *"entering
     * simulation and then exiting should restore the previous mode"*). Read and
     * spent by `exitNavMode`, so the way out is the way in — the WALK stays where
     * it was instead of dropping onto the turntable. `null` outside a simulation.
     */
    returnMode: null as BimNavMode | null,
    /**
     * The way back to the body's START (owner, 2026-10-20: *"we need a button in
     * simulate nav bar to reset the avatar position to original position.. also
     * assign key "R" to this"*). The spawn is the LAYOUT's to resolve (it owns the
     * people rows), so the scene only asks: the key and the bar's button both land
     * in this callback, which the layout fills with `resetDrivenPosition`.
     */
    onDrivenReset: null as (() => void) | null,
    /**
     * Where SIMULATION's POV camera stands over the driven body's feet (owner,
     * 2026-10-20: *"the camera height in simulate pov view should be in the models
     * eye level.. calculate eye model height -5 %"*). The LAYOUT measures it from
     * the avatar's own body (`povEyeHeightForModel`) and writes it here; the
     * standing height is only the fallback while no body is measured.
     */
    drivenEyeHeight: NAV_EYE_HEIGHT_M,
    /**
     * The band the WALK widened, and whether it is widened right now (owner,
     * 2026-10-18: *"you can fall into a cut wall … you fall into an cant get out?"*).
     * While a walking mode is active the cut is widened until it contains the
     * walker's feet — the floor he stands on must stay drawn. The OPERATOR's own band
     * is never overwritten: it lives in the layout's sliders (`localClipY` /
     * `localClipBottomY`), and the walk's exit re-applies it (`cutRangeAt`), so a
     * band changed mid-walk is what comes back.
     */
    walkCut: { lifted: false },
    /**
     * The last pose a walk step was ACCEPTED at — the walk's own rescue point. A
     * body that ends up inside a wall (a stale spawn, a model swap, an older
     * build's cut rule) cannot walk out: every direction is refused from in there,
     * so it is put back here instead. Seeded on mode entry and on every accepted
     * step.
     */
    lastFree: null as { x: number; z: number; feetY: number } | null,
    /** How many times the walk had to be pulled out of geometry. */
    rescues: 0,
    /** The last rescue test's verdict — the rigs' reading of "is the body inside a wall". */
    insideWall: false,
    /** When the rescue test last ran (throttled: one parity cast per 200 ms). */
    rescueCheckedAt: 0,
    /** The roof cutaway's lid elements while the driven walker is inside. */
    cutHiddenElements: ref<Set<number>>(new Set()),
    /**
     * Elements the DEVICE-MODEL layer is standing in for (card t_07645b98):
     * every baked fixture/shade twin the app draws its own model for. Its own
     * bucket, exactly like the live shade layer's and the roof cutaway's lid —
     * the three layers own disjoint ids and must not clobber each other's hides.
     */
    modelHiddenElements: ref<Set<number>>(new Set()),
    pointerLocked,
    /** The editing surfaces keep the pointer free (set by the layout). */
    pointerLockAllowed: { value: true },
    dragLook: { active: false, pointerId: -1, lastX: 0, lastY: 0, moved: 0 },
    pointerHandlers: null as {
      element: HTMLElement;
      onPointerDown: (ev: PointerEvent) => void;
      onPointerMove: (ev: PointerEvent) => void;
      onPointerUp: (ev: PointerEvent) => void;
      onWheel: (ev: WheelEvent) => void;
    } | null,
    // Gradient background
    gradCanvas,
    gradCtx,
    /** Frames spent waiting for the container to exist on a re-visit (see `init`). */
    attachRetries: 0,
    /** The ground the camera may not go below, cached per model revision. */
    groundFloorY: 0.35,
    groundFloorRevision: -1,
    /**
     * A ring buffer of the camera's own trajectory, recorded only while a rig asks
     * for it (`__bimTrace`). "The whole model is thrown around violently" and "the
     * camera spins, then I am under the grass" are the same sentence until the
     * positions are on paper: this is the paper (owner, 2026-10-24).
     */
    trace: [] as Array<{ t: number; cam: number[]; target: number[]; anim: boolean; mode: string }>,
    /** Stacks captured by `__bimTrap` (debug only). */
    trapStacks: [] as Array<{ name: string; at: number; stack: string[] }> | null,
    /** The last direction the orbit had, so a collapsed radius can be pushed back out. */
    lastOrbitDir: null as THREE.Vector3 | null,
    /** Has this scene ever been given a pose? A revisit keeps the one it left. */
    framedOnce: false,
    /** The last camera flight this scene was asked for (see the geometry hook). */
    lastFlight: null as null | { at: number; pos: number[]; target: number[]; durationMs: number },
    traceOn: false,
    envTexture: null as THREE.Texture | null,
    skyColor: new THREE.Color(0x1a1a2e),
    horizonColor: new THREE.Color(0x1a1a2e),
    bottomColor: new THREE.Color(0x111827),
    /**
     * Has the layout ANSWERED the weather question (either way)?
     *
     * On the CACHED entry, not in the composable's closure: the scene outlives a
     * component remount (module-level `sceneCache`), and a fresh closure started
     * `false` — so the remount's `init()` painted the plain GRADIENT over a canvas
     * the previous closure had already painted the weather sky into, and the sky
     * never came back (owner, 2026-10-23: *"i see bg.. only blue.. no matter the
     * time of day.. the weather sky is not working"*). The answer is a fact about
     * the SCENE, exactly like the condition it describes.
     */
    weatherAnswered: false,
    /** Set by `useBimScene` once its own `dispose()` exists — `disposeAllBimScenes` calls it. */
    disposeScene: null as (() => void) | null,
  };
}

type Entry = ReturnType<typeof createEntry>;
const sceneCache = new Map<string, Entry>();

/**
 * Tear down every cached scene.
 *
 * The cache is keyed by the hub's connection key, so switching hubs must not
 * leave another house's model reachable — or its GPU memory alive. Called from
 * the stores that already reset themselves when the connection changes.
 */
export function disposeAllBimScenes(): void {
  for (const entry of Array.from(sceneCache.values())) entry.disposeScene?.();
  sceneCache.clear();
}

/**
 * The pose the hub's cached scene is standing in right now — or `null` when it has
 * never been given one (a cold app, a model still loading, a hub switch).
 *
 * The scene outlives the surface that framed it, so this is the house's OWN view:
 * the camera the 3D page left behind, read by a surface that is a WINDOW on the
 * house rather than a visit (the dashboard's model card). Owner, 2026-10-30:
 * *"dashboard always goes to home view when page revisited.. why dont we sync the
 * model here with the model on page"* — a warm scene already answers with the view
 * the owner is in, and a surface that re-places its own pinned view over it is
 * throwing that away on every navigation.
 *
 * `framedOnce` is the gate on purpose: a scene whose camera is still the loader's
 * default has no view worth handing on, so the caller falls back to its own.
 */
export function cachedBimPose(sceneKey: string): ViewpointPose | null {
  const entry = sceneCache.get(sceneKey);
  const s = entry?.state.value;
  if (!entry?.framedOnce || !s) return null;
  const { x, y, z } = s.camera.position;
  const target = s.controls.target;
  return { position: { x, y, z }, target: { x: target.x, y: target.y, z: target.z } };
}

// ─── three's shader-error check (owner, 2026-10-23: *"everything is very slow"*) ──
//
// `renderer.debug.checkShaderErrors` makes three run, for EVERY program it builds,
// a synchronous driver round trip: `getProgramParameter(LINK_STATUS)`,
// `getProgramParameter(VALIDATE_STATUS)` and both shader info logs. This house is
// 685 meshes with a material per finish, so the page builds hundreds of programs
// on its first frames — and on ANGLE/D3D11 those calls block the main thread. A
// CPU profile of the opening (Edge, Quadro P2200, the same 16 texture units the
// owner's app reports) put 94 % of a 55 s window's self time in three's
// `onFirstUse`, i.e. IN the check; the page ran at ~1 fps until the last program
// had been validated.
//
// It is a DIAGNOSTIC, so it is OFF by default and available on demand: `?bimShaderCheck=1`
// on the page's URL, `localStorage['bim-shader-check'] = '1'`, or the runtime door
// `window.__bimShaderCheck(true)` — which also flips the live renderer, so a rig
// can turn it on when a blank scene needs reading (a shader that exceeds the GPU's
// limits still reports itself: ANGLE logs "Trying to use N texture units" and the
// browser logs `useProgram: program not valid` whether or not three checks).
const SHADER_CHECK_STORAGE_KEY = 'bim-shader-check';
let shaderCheckOverride: boolean | null = null;

/** Does this session want three's per-program shader-error check? */
function readShaderCheckPreference(): boolean {
  if (shaderCheckOverride !== null) return shaderCheckOverride;
  try {
    if (new URLSearchParams(window.location.search).get('bimShaderCheck') === '1') return true;
    return window.localStorage.getItem(SHADER_CHECK_STORAGE_KEY) === '1';
  } catch {
    // A storage that refuses reads (private mode): the fast default stands.
    return false;
  }
}

// ─── Sun/lighting settings persistence ───────────────────────────────────────

/**
 * The cached copy of the view settings — the RENDER CACHE, beside the hub's own.
 *
 * The key keeps its old `bim-sun-` name on purpose: every existing device's
 * cache keeps working, and the blob it holds simply grew (see
 * `BimViewSettings`). It still holds exactly one object, per hub connection.
 */
const BIM_VIEW_CACHE_KEY = (id: string) => `bim-sun-${id}`;
/** Base intensity of the RoomEnvironment fill; scaled by the "Interior fill" slider. */
const BIM_ENV_BASE = 0.3;
/**
 * The orbit's polar range. Not 0 (a camera exactly above its target has no azimuth,
 * so a drag spins the scene around the view axis) and not π (below the horizon the
 * camera ends up under the terrain). See the controls' construction.
 */
const ORBIT_MIN_POLAR_ANGLE = 0.09;
/** Below this the orbit has no azimuth (see `keepOrbitRadius`). */
const MIN_ORBIT_RADIUS = 0.5;
const ORBIT_MAX_POLAR_ANGLE = Math.PI * 0.495;
/**
 * The backdrop canvas's own shape — the gradient and the weather sky BOTH paint
 * into this one canvas, so its size is fixed and never follows the viewport (see
 * the canvas's creation for the GL error a resize causes).
 */
const BACKDROP_WIDTH = 1024;
const BACKDROP_HEIGHT = 512;

/**
 * How often the falling drops are re-drawn.
 *
 * The backdrop is a canvas texture, so a moving sky means a repaint AND a
 * texture upload per frame — 30 a second is smooth for streaks this thin and is
 * half the uploads the display rate would ask for. The loop only runs while the
 * live sky is on and something is actually falling.
 */
const PRECIPITATION_FRAME_MS = 1000 / 30;

/**
 * How often the sun steps while it is synced to the clock. A minute is the
 * slider's own resolution (15-minute steps, and the sun moves a quarter of a
 * degree in that time) and the backdrop is a canvas repaint plus one frame —
 * cheap enough that the sun can simply be right, without a smoothing rule.
 */
const SUN_CLOCK_TICK_MS = 60_000;

/**
 * How long a change waits before it is pushed to the HUB.
 *
 * The blob is written to `localStorage` on every change — that is the render
 * cache, and it is one synchronous write. The hub is the SHARED copy, so a
 * slider drag (dozens of changes) becomes one PUT per pause, and the last value
 * always wins.
 */
const HUB_SAVE_DEBOUNCE_MS = 900;

/** A glow scale is a fraction of the authored emissive, never a blown-out one. */
function clampGlowScale(value: number): number {
  if (!Number.isFinite(value)) return 0.35;
  return Math.min(1.5, Math.max(0.1, value));
}

function normalizeDegrees(deg: number): number {
  return ((deg % 360) + 360) % 360;
}

/**
 * Which pin layers draw, and what they narrow to — the layout's half of the
 * view settings (owner, 2026-10-29: *"all bim settings should live on hub"*).
 *
 * `roomLabels` is the tri-state plus `auto`, the country the app is in until the
 * operator picks one (`resolveRoomLabelMode` then answers by navigation mode).
 */
interface BimUiViewPrefs {
  roomLabels: BimRoomLabelMode | 'auto';
  showRooms: boolean;
  showPeople: boolean;
  showDevices: boolean;
  viewDeviceType: string;
  viewDeviceRoom: string;
}

const DEFAULT_UI_VIEW_PREFS: BimUiViewPrefs = {
  roomLabels: 'auto',
  showRooms: true,
  showPeople: true,
  showDevices: true,
  viewDeviceType: '',
  viewDeviceRoom: '',
};

/**
 * A room page's filter before the operator has said anything — everything the
 * house has, this room's own devices first (owner, 2026-10-29: *"why dont we
 * show everything on room view"* … *"This room's devices (current), switchable
 * to all"*). Mirrors the hub's `DEFAULT_BIM_VIEW_SETTINGS.roomView`.
 */
const DEFAULT_ROOM_VIEW_FILTER: HubBimRoomViewFilter = {
  people: true,
  rooms: true,
  devicesOn: true,
  devices: 'room',
  shadows: true,
  lights: true,
  sun: true,
  weather: false,
};

/**
 * Can this device draw shadows at all?
 *
 * A phone cannot afford them (the framebuffer and the shadow map are the two
 * cheapest wins — see the renderer's own note in `init`), so the operator's
 * switch may only turn the map further OFF on one. This is the same test the
 * renderer's build runs, named once so the setter agrees with it.
 */
const SHADOWS_SUPPORTED = !(typeof navigator !== 'undefined' && (navigator.maxTouchPoints ?? 0) > 0);

/** The settings shape this build writes — the newest version, named once. */
const BIM_VIEW_VERSION = 5;

function currentBimViewSettings(e: Entry): HubBimViewSettings {
  const ui = e.uiPrefs.value;
  return {
    version: BIM_VIEW_VERSION,
    timeOfDay: e.timeOfDay.value,
    sunEnabled: e.sunEnabled.value,
    exposure: e.exposure.value,
    ambientBoost: e.ambientBoost.value,
    northAngle: normalizeDegrees(e.northAngle.value),
    latitude: e.latitude.value,
    solarDate: e.solarDate.value,
    sunClockSynced: e.sunClockSynced.value,
    autoLighting: e.autoLighting.value,
    weatherSkyEnabled: e.weatherSkyEnabled.value,
    lightPreset: e.lightPreset.value,
    glowScale: clampGlowScale(e.glowScale.value),
    showSunRay: e.showSunRay.value,
    compass: e.showCompassOverlay.value,
    navMode: e.navMode.value,
    simView: e.simView.value,
    roomLabels: ui.roomLabels,
    showRooms: ui.showRooms,
    showPeople: ui.showPeople,
    showDevices: ui.showDevices,
    viewDeviceType: ui.viewDeviceType,
    viewDeviceRoom: ui.viewDeviceRoom,
    transparencyEnabled: e.transparencyEnabled.value,
    transparencyPercent: e.transparencyPercent.value,
    clipEnabled: e.clipEnabled.value,
    clipHeightY: e.clipHeightY.value,
    clipBottomY: e.clipBottomY.value,
    shadows: e.shadowsEnabled.value,
    lights: e.lightsEnabled.value,
    roomView: { ...e.roomView.value },
  };
}

/** Does this scope have a cached view? */
function hasBimViewCache(id: string): boolean {
  try {
    return localStorage.getItem(BIM_VIEW_CACHE_KEY(id)) !== null;
  } catch {
    return false;
  }
}

/**
 * A change the hub has not acknowledged yet.
 *
 * Written into the cached blob the moment a change is queued (`pending: true`)
 * and cleared when the PUT lands, so a visit that ends before the write leaves
 * the operator's value marked: the next load PUSHES it instead of adopting the
 * hub's older copy. No clocks are compared — the flag IS the ordering.
 */
function hasPendingBimViewCache(id: string): boolean {
  try {
    const raw = localStorage.getItem(BIM_VIEW_CACHE_KEY(id));
    if (!raw) return false;
    return (JSON.parse(raw) as { pending?: boolean }).pending === true;
  } catch {
    return false;
  }
}

function loadBimViewCache(id: string, e: Entry): void {
  try {
    const raw = localStorage.getItem(BIM_VIEW_CACHE_KEY(id));
    if (!raw) return;
    const s: Partial<HubBimViewSettings> = JSON.parse(raw);
    const northAngleNeedsMigration = typeof s.version !== 'number' || s.version < 3;
    if (typeof s.timeOfDay === 'number') e.timeOfDay.value = s.timeOfDay;
    if (typeof s.sunEnabled === 'boolean') e.sunEnabled.value = s.sunEnabled;
    if (typeof s.exposure === 'number') e.exposure.value = s.exposure;
    if (typeof s.ambientBoost === 'number') e.ambientBoost.value = s.ambientBoost;
    if (typeof s.northAngle === 'number')
      e.northAngle.value = northAngleNeedsMigration
        ? normalizeDegrees(s.northAngle - 180)
        : normalizeDegrees(s.northAngle);
    if (typeof s.latitude === 'number') e.latitude.value = s.latitude;
    if (typeof s.solarDate === 'string') e.solarDate.value = s.solarDate;
    if (typeof s.sunClockSynced === 'boolean') e.sunClockSynced.value = s.sunClockSynced;
    if (typeof s.autoLighting === 'boolean') e.autoLighting.value = s.autoLighting;
    e.lightPreset.value = lightPresetOf(s.lightPreset);
    if (typeof s.glowScale === 'number') e.glowScale.value = clampGlowScale(s.glowScale);
    if (typeof s.weatherSkyEnabled === 'boolean') e.weatherSkyEnabled.value = s.weatherSkyEnabled;
    if (typeof s.showSunRay === 'boolean') e.showSunRay.value = s.showSunRay;
    if (typeof s.compass === 'boolean') e.showCompassOverlay.value = s.compass;
    if (isBimNavMode(s.navMode)) e.navMode.value = s.navMode;
    else e.navMode.value = readStoredNavMode(typeof localStorage === 'undefined' ? null : localStorage);
    if (isBimSimView(s.simView)) e.simView.value = s.simView;
    else e.simView.value = readStoredSimView(typeof localStorage === 'undefined' ? null : localStorage);
    if (typeof s.transparencyEnabled === 'boolean') e.transparencyEnabled.value = s.transparencyEnabled;
    if (typeof s.transparencyPercent === 'number') e.transparencyPercent.value = s.transparencyPercent;
    // The section band, from the cache so the FIRST frame is already cut: the
    // planes themselves are set once the scene exists (`init` applies them).
    if (typeof s.clipEnabled === 'boolean') e.clipEnabled.value = s.clipEnabled;
    if (typeof s.clipHeightY === 'number') e.clipHeightY.value = s.clipHeightY;
    if (typeof s.clipBottomY === 'number') e.clipBottomY.value = s.clipBottomY;
    if (typeof s.shadows === 'boolean') e.shadowsEnabled.value = s.shadows;
    if (typeof s.lights === 'boolean') e.lightsEnabled.value = s.lights;
    e.roomView.value = { ...DEFAULT_ROOM_VIEW_FILTER, ...storedRoomViewFilter(s.roomView) };
    e.uiPrefs.value = { ...DEFAULT_UI_VIEW_PREFS, ...storedUiPrefs(s) };

    if (northAngleNeedsMigration || (s.version ?? 0) < BIM_VIEW_VERSION) {
      localStorage.setItem(BIM_VIEW_CACHE_KEY(id), JSON.stringify(currentBimViewSettings(e)));
    }
  } catch {
    /* ignore */
  }
}

/** The layout's half of a stored blob, field by field (a hostile row ruins none). */
function storedUiPrefs(s: Partial<HubBimViewSettings>): Partial<BimUiViewPrefs> {
  const prefs: Partial<BimUiViewPrefs> = {};
  if (
    s.roomLabels === 'auto' ||
    (typeof s.roomLabels === 'string' && (BIM_ROOM_LABEL_MODES as readonly string[]).includes(s.roomLabels))
  )
    prefs.roomLabels = s.roomLabels;
  if (typeof s.showRooms === 'boolean') prefs.showRooms = s.showRooms;
  if (typeof s.showPeople === 'boolean') prefs.showPeople = s.showPeople;
  if (typeof s.showDevices === 'boolean') prefs.showDevices = s.showDevices;
  if (typeof s.viewDeviceType === 'string') prefs.viewDeviceType = s.viewDeviceType;
  if (typeof s.viewDeviceRoom === 'string') prefs.viewDeviceRoom = s.viewDeviceRoom;
  return prefs;
}

/** The room pages' half of a stored blob, field by field. */
function storedRoomViewFilter(value: unknown): Partial<HubBimRoomViewFilter> {
  if (!value || typeof value !== 'object') return {};
  const row = value as Partial<HubBimRoomViewFilter>;
  const filter: Partial<HubBimRoomViewFilter> = {};
  if (typeof row.people === 'boolean') filter.people = row.people;
  if (typeof row.rooms === 'boolean') filter.rooms = row.rooms;
  if (typeof row.devicesOn === 'boolean') filter.devicesOn = row.devicesOn;
  if (row.devices === 'room' || row.devices === 'all') filter.devices = row.devices;
  if (typeof row.shadows === 'boolean') filter.shadows = row.shadows;
  if (typeof row.lights === 'boolean') filter.lights = row.lights;
  if (typeof row.sun === 'boolean') filter.sun = row.sun;
  if (typeof row.weather === 'boolean') filter.weather = row.weather;
  return filter;
}

function saveBimViewCache(id: string, e: Entry, pending = false): void {
  try {
    localStorage.setItem(BIM_VIEW_CACHE_KEY(id), JSON.stringify({ ...currentBimViewSettings(e), pending }));
  } catch {
    /* ignore */
  }
}

/**
 * The cached scene for one hub connection — the entry the key already names, or
 * the one the SAME connection was built under a moment ago.
 *
 * `sceneKey` is `activeHubKey()`, which answers the LAN endpoint
 * (`local:host:port/api`) until a login resolves the hub's own id and rewrites
 * it to `hub:<uuid>` (so the key survives an address change). The house built
 * under the first spelling is the house the second one means — and a miss is not
 * a small thing: the model is fetched (11 MB) and parsed again and every shader
 * program is compiled again. Measured on the demo house, the visit after that
 * upgrade spent 26 s in one long task — 40 s of long tasks in all — rebuilding a
 * house it already had. `getHubSessionKeys()` is the app's own list of the
 * spellings this connection answers to, most specific first, so the scene moves
 * with it instead of being rebuilt.
 */
function getEntry(id: string): Entry {
  const existing = sceneCache.get(id);
  if (existing) return existing;
  // `getHubSessionKeys()` is the app's own list of the spellings this connection
  // answers to, most specific first; `activeHubKey()`'s own last resort — the bare
  // `local`, before either identity is known — is part of that vocabulary too.
  // Only the ADDRESS spellings are adopted (never another hub's id): a `hub:` key
  // means a named hub, and the scene under one must not answer for another.
  for (const alias of [...getHubSessionKeys(), 'local']) {
    if (alias === id || alias.startsWith('hub:')) continue;
    const adopted = sceneCache.get(alias);
    if (!adopted || !adopted.state.value) continue;
    sceneCache.delete(alias);
    sceneCache.set(id, adopted);
    // The sun/weather settings were read under the address key; file them under
    // the name the entry now answers to, so the next open finds them directly.
    saveBimViewCache(id, adopted);
    return adopted;
  }
  const entry = createEntry();
  loadBimViewCache(id, entry);
  sceneCache.set(id, entry);
  return entry;
}

// ─── Solar position helpers ────────────────────────────────────────────────────

function getDayOfYear(dateStr: string): number {
  const d = new Date(dateStr + 'T12:00:00');
  const start = new Date(d.getFullYear(), 0, 0);
  return Math.floor((d.getTime() - start.getTime()) / 86_400_000);
}

/**
 * Computes true solar elevation and azimuth (from north, clockwise) using the
 * standard spherical-astronomy formulas (accurate to ~0.5°).
 * @param hourLST  local solar time (0–24); 12 = solar noon
 * @param latDeg   site latitude in degrees (-90 … +90)
 * @param dayOfYear day-of-year (1–365)
 */
function computeSolarPosition(
  hourLST: number,
  latDeg: number,
  dayOfYear: number,
): { elevDeg: number; elevRad: number; azimFromNorthRad: number } {
  const D2R = Math.PI / 180;
  // Solar declination
  const decl = -23.45 * D2R * Math.cos((2 * Math.PI * (dayOfYear + 10)) / 365);
  // Hour angle: 0 at noon, negative = morning, positive = afternoon
  const H = (hourLST - 12) * 15 * D2R;
  const lat = latDeg * D2R;

  // Elevation
  const sinElev = Math.sin(lat) * Math.sin(decl) + Math.cos(lat) * Math.cos(decl) * Math.cos(H);
  const elevRad = Math.asin(Math.max(-1, Math.min(1, sinElev)));
  const cosElev = Math.cos(elevRad);

  // Azimuth from south, clockwise: sin(As) and cos(As)
  let azimFromNorthRad = Math.PI; // default: due south (180°) when sun at zenith
  if (cosElev > 1e-6) {
    const sinAs = (Math.cos(decl) * Math.sin(H)) / cosElev;
    const cosAs = (Math.sin(elevRad) * Math.sin(lat) - Math.sin(decl)) / (cosElev * Math.cos(lat));
    const fromSouth = Math.atan2(sinAs, cosAs);
    // from-north = from-south + 180°
    azimFromNorthRad = fromSouth + Math.PI;
  }

  return { elevDeg: elevRad / D2R, elevRad, azimFromNorthRad };
}

/**
 * The express ID an IFC reference names: web-ifc answers a line reference as a
 * bare type number in the old paths and as `{ value: <number> }` — the
 * typed-handle form — in the newer ones. Either spelling names the same line;
 * anything else names none, and a caller must not invent one.
 */
function expressIDOf(reference: unknown): number | undefined {
  if (typeof reference === 'number') return reference;
  const value = (reference as { value?: unknown } | null | undefined)?.value;
  return typeof value === 'number' ? value : undefined;
}

// ─────────────────────────────────────────────────────────────────────────────

/** How many dashboard views have a BIM scene mounted right now (see the geometry hook). */
let liveBimViews = 0;

/**
 * Who a model URL belongs to — which decides the transport AND the credential.
 *
 *   · `hub` — the hub's own model. Its local token, over `routedFetch` (the
 *     WebRTC channel in cloud mode). The default, and what every surface used
 *     before Torik Plan existed.
 *   · `cloud` — a CLOUD project's model (Torik Plan). Fetched directly with the
 *     cloud access token; it is not a hub path, so it must not go over the
 *     channel. Renews the token once on a 401, the same way `cloudFetch` does.
 *   · `none` — a presigned object URL, which carries its own signature and must
 *     not receive an `Authorization` header from either side.
 */
export type BimModelBearer = 'hub' | 'cloud' | 'none';

export interface BimLoadModelOptions {
  /** A live swap: keep the rendered model and the camera until the new one lands. */
  preserveView?: boolean;
  /** `none` leaves the opening pose to the caller (the dashboard's first preset). */
  framing?: 'auto' | 'none';
  /**
   * Whose model this is, and therefore how it is fetched. Defaults to `hub`, so
   * every existing caller keeps its behaviour byte for byte.
   */
  bearer?: BimModelBearer;
}

export function useBimScene(
  sceneKey: string,
  container: () => HTMLElement | null,
  options: {
    /** The one-time carry of settings stored under the pre-hub-scope key. */
    legacyStorageKey?: string;
    /**
     * A surface that only LOOKS at the house (the dashboard's model card).
     *
     * The hub's view settings are still adopted — the sun, the sky, the filters are
     * the house's — but not its NAV MODE: a walk belongs to the 3D page, and a card
     * that adopted it would re-seat its camera at the body's spawn on every visit
     * (owner, 2026-10-30: *"if we go to walk mode on page. on dash the view gets
     * taken far to default fit view"*).
     */
    lookOnly?: boolean;
  } = {},
) {
  const e = getEntry(sceneKey);

  // One-time carry: before the hub scope existed these settings were keyed by the
  // 3D DASHBOARD's id. The page hands that id over as `legacyStorageKey`, so the
  // operator's sun/weather survives the upgrade once and is then re-keyed.
  if (
    options.legacyStorageKey &&
    options.legacyStorageKey !== sceneKey &&
    !hasBimViewCache(sceneKey) &&
    hasBimViewCache(options.legacyStorageKey)
  ) {
    loadBimViewCache(options.legacyStorageKey, e);
    saveBimViewCache(sceneKey, e);
  }

  const {
    state,
    modelLoaded,
    modelLoading,
    modelError,
    modelMeshes,
    modelRevision,
    loadedModelUrl,
    modelFormat,
    modelGroup,
    cameraSerial,
    cameraInteracting,
    cameraMoving,
    lightPreset,
    glowScale,
    gridVisible,
    elementMeshes,
    ifcElements,
    modelBoundsY,
    clipEnabled,
    clipHeightY,
    clipBottomY,
    clipPlane,
    clipPlaneBottom,
    transparencyEnabled,
    transparencyPercent,
    animationId,
    navMode,
    groundMode,
    simView,
    pointerLocked,
    sunEnabled,
    exposure,
    ambientBoost,
    northAngle,
    cameraAzimuth,
    showSunRay,
    latitude,
    solarDate,
    modelCenterInfo,
    modelSizeInfo,
  } = e;

  /**
   * Every change: the CACHE now (it is what the next first frame is painted
   * from), and the HUB in a moment — see `queueHubSave`.
   *
   * A change that came FROM the hub (`applying`) is not a change at all: it is
   * neither re-cached with a `pending` mark nor sent back.
   */
  const save = () => {
    if (e.hubSync.applying) return;
    saveBimViewCache(sceneKey, e, true);
    queueHubSave();
  };

  // The compass is a plain toggle in two panels (the View tab's and the Sun
  // panel's), so it is filed here rather than at each door. SYNC on purpose: an
  // adopted value is written while `applying` is still true, and a deferred
  // watcher would run once the guard is down and queue a pointless echo.
  watch(e.showCompassOverlay, () => save(), { flush: 'sync' });

  /**
   * Push the view's settings to the hub, paced.
   *
   * Owner, 2026-10-29: *"sun offset is not preserved on server"* — the house's
   * sun now follows the house, not the browser it was set in. A slider drag is
   * dozens of changes, so the hub gets one PUT per pause; the LAST value always
   * wins, because a change that lands while a PUT is running queues one more.
   * An echo of the hub's own values (`applying`) is never pushed back.
   */
  function queueHubSave(): void {
    const sync = e.hubSync;
    if (sync.applying) return;
    sync.touched = true;
    if (sync.timer !== null) clearTimeout(sync.timer);
    sync.timer = setTimeout(() => {
      sync.timer = null;
      void pushHubSave();
    }, HUB_SAVE_DEBOUNCE_MS);
  }

  async function pushHubSave(): Promise<void> {
    const sync = e.hubSync;
    if (sync.inFlight) {
      sync.again = true;
      return;
    }
    const blob = currentBimViewSettings(e);
    const wire = JSON.stringify(blob);
    // The synced clock re-applies the same values every minute; a hub write for a
    // blob that has not changed is noise.
    if (wire === sync.lastPushed) return;
    sync.inFlight = true;
    try {
      await saveHubBimViewSettings(blob as HubBimViewSettings);
      sync.lastPushed = wire;
      // Acknowledged: the cache is no longer a pending change.
      saveBimViewCache(sceneKey, e);
    } catch {
      // Offline, or a hub that predates the endpoint. The cache keeps the value
      // AND its `pending` mark, so the next visit sends it.
    } finally {
      sync.inFlight = false;
      if (sync.again) {
        sync.again = false;
        void pushHubSave();
      }
    }
  }

  /**
   * Read the hub's copy and let it speak for the house.
   *
   * It runs on every visit to the 3D page, not once per session: another device
   * may have moved the sun while this one was elsewhere. Three things outrank
   * it — a change the operator is making right now (`touched`), a change a
   * previous visit could not deliver (`pending`), and a hub with nothing stored
   * (the cache is then the best answer there is). All three end the same way:
   * the local values stand and are pushed instead.
   */
  async function adoptHubViewSettings(): Promise<void> {
    const sync = e.hubSync;
    if (sync.reading) return;
    if (hasPendingBimViewCache(sceneKey)) {
      sync.touched = true;
      void pushHubSave();
      return;
    }
    sync.reading = true;
    try {
      const stored = await loadHubBimViewSettings();
      if (!stored.settings || sync.touched) return;
      applyHubViewSettings(stored.settings, { navMode: options.lookOnly !== true });
      sync.lastPushed = JSON.stringify(currentBimViewSettings(e));
      saveBimViewCache(sceneKey, e);
    } catch {
      /* the cache stands */
    } finally {
      sync.reading = false;
    }
  }

  /**
   * Has this VISIT placed a pose of its own? Set by `placeCamera`, which is the
   * deliberate placements' one door (a preset recall, the whole-model fit, a card's
   * live pose) — never the loader's own framing.
   *
   * The hub's copy is adopted at init and the page's opening view comes a moment
   * later, so the two race. A view that has ALREADY been applied outranks the
   * hub's stored MODE: the arrival names its own (`flyToViewpoint` restores the
   * preset's), and re-seating it from the hub afterwards would put the page into a
   * walk it did not open on — which is exactly what "the cut is gone until I press
   * the view again" looked like (owner, 2026-10-30). On a RETURNING visit nothing
   * has been placed yet, so the hub's mode still opens the house as it was left.
   */
  let placedPoseThisVisit = false;

  /**
   * Put the hub's values on the scene — without echoing them back to it.
   *
   * `options.navMode: false` is the LOOK-ONLY surface's rule (the dashboard's
   * model card): the walking/turntable/simulation mode is the HOUSE's, chosen on
   * the 3D page, and adopting it would put a window into a walk — the camera
   * re-seated at the body's spawn on a surface that only wants to look at the
   * house (owner, 2026-10-30: *"if we go to walk mode on page. on dash the view
   * gets taken far to default fit view"*). The sun, the sky and the filters are
   * still the house's, and still adopted.
   */
  function applyHubViewSettings(v: HubBimViewSettings, options: { navMode?: boolean } = {}): void {
    const sync = e.hubSync;
    sync.applying = true;
    try {
      e.timeOfDay.value = v.timeOfDay;
      e.sunClockSynced.value = v.sunClockSynced;
      e.autoLighting.value = v.autoLighting;
      e.northAngle.value = normalizeDegrees(v.northAngle);
      e.latitude.value = v.latitude;
      e.solarDate.value = v.solarDate;
      e.showSunRay.value = v.showSunRay;
      e.showCompassOverlay.value = v.compass;
      // The layout's half: the room pills and the view filter follow it too.
      e.uiPrefs.value = {
        roomLabels: v.roomLabels,
        showRooms: v.showRooms,
        showPeople: v.showPeople,
        showDevices: v.showDevices,
        viewDeviceType: v.viewDeviceType,
        viewDeviceRoom: v.viewDeviceRoom,
      };
      // The setters below re-light, re-paint and re-draw exactly as an operator's
      // own change would; `applying` keeps them from writing back.
      setSunEnabled(v.sunEnabled);
      setExposure(v.exposure);
      setAmbientBoost(v.ambientBoost);
      setLightPreset(v.lightPreset);
      setGlowScale(v.glowScale);
      setWeatherSkyEnabled(v.weatherSkyEnabled);
      updateSunRay();
      if (v.transparencyEnabled) setModelTransparency(v.transparencyPercent);
      else disableModelTransparency();
      // The section band — the house's own cut, the see-through's sibling. Applied
      // only when the blob DECLARES one (a hub that predates it says nothing, and
      // an unrecorded setting is not a setting), and only while this visit has not
      // placed a view of its own: a preset (or a handed pose) names its own band,
      // and the hub's older copy must not cut it back to last week's level.
      if (!placedPoseThisVisit && typeof v.clipEnabled === 'boolean') {
        if (v.clipEnabled && typeof v.clipHeightY === 'number' && Number.isFinite(v.clipHeightY)) {
          setClipRange(v.clipHeightY, typeof v.clipBottomY === 'number' ? v.clipBottomY : clipBottomY.value);
        } else if (clipEnabled.value) {
          disableClip();
        }
      }
      // The two filters, and the room pages' own — adopted with the rest, so a
      // second device opens on the same shadows and the same lights.
      setShadowsEnabled(v.shadows);
      setLightsEnabled(v.lights);
      e.roomView.value = { ...DEFAULT_ROOM_VIEW_FILTER, ...storedRoomViewFilter(v.roomView) };
      if (v.sunClockSynced) {
        syncSunClock();
        startSunClock();
      } else {
        stopSunClock();
        applySunTime(e.timeOfDay.value);
      }
      // The mode last: it re-seats the camera, and every pose above is already in.
      setSimView(v.simView);
      if (options.navMode !== false && !placedPoseThisVisit) setNavMode(v.navMode);
      invalidate();
    } finally {
      sync.applying = false;
    }
  }

  /**
   * The LAYOUT's write path — the room pills' mode and the view filter.
   *
   * One blob, one writer: this patches the same settings the sun does, so a
   * change here is cached and pushed by the same paced rule. A patch that
   * changes nothing writes nothing, which is what keeps the layout's own
   * follow-the-blob watchers from echoing back.
   */
  function patchViewSettings(patch: Partial<BimUiViewPrefs>): void {
    const next = { ...e.uiPrefs.value, ...patch };
    const same = (Object.keys(next) as (keyof BimUiViewPrefs)[]).every((key) => next[key] === e.uiPrefs.value[key]);
    if (same) return;
    e.uiPrefs.value = next;
    save();
  }

  /** The visit is ending: send what is still waiting, once, and let it go. */
  function flushHubSave(): void {
    const sync = e.hubSync;
    if (sync.timer !== null) {
      clearTimeout(sync.timer);
      sync.timer = null;
    }
    if (sync.touched) void pushHubSave();
  }

  const NAV_KEYS = new Set([
    'w',
    'a',
    's',
    'd',
    'q',
    'e',
    'r',
    'f',
    '+',
    '-',
    '=',
    'arrowup',
    'arrowdown',
    'arrowleft',
    'arrowright',
  ]);
  const DEFAULT_ROTATE_SPEED = 1;
  // Orbit damping: the tail after the mouse stops. 0.08 coasted so long that the
  // view kept creeping and the overlays stayed hidden (owner, 2026-10-20: *"it takes
  // too much time to settle"*); 0.14 stops in about half the time and still glides.
  const DEFAULT_DAMPING_FACTOR = 0.14;

  /**
   * Apply (and persist) a lighting preset — Low / Mid / High. The pool reads
   * `lightIntensityFactor` on its next pass, so this is a value change, not a
   * rebuild.
   */
  /** The fixture-glow dial: 0 (body barely lit) … 1.5 (as authored, hot). */
  function setGlowScale(value: number): void {
    e.glowScale.value = clampGlowScale(value);
    if (state.value) state.value.glowScale = e.glowScale.value;
    save();
    invalidate();
  }

  function setLightPreset(preset: LightPreset): void {
    e.lightPreset.value = lightPresetOf(preset);
    if (state.value) state.value.lightIntensityFactor = LIGHT_PRESET_FACTOR[e.lightPreset.value];
    save();
    invalidate();
  }

  /**
   * The two filters AS THEY ARE RIGHT NOW — a visiting surface's own answer while
   * one is mounted (a room view), the page's otherwise. Everything that DRAWS
   * reads these; the settings blob writes the page's preferences
   * (`e.shadowsEnabled` / `e.lightsEnabled`) whatever a visitor is borrowing.
   */
  const shadowsOn = computed(() => e.shadowsBorrow.value ?? e.shadowsEnabled.value);
  const lightsOn = computed(() => e.lightsBorrow.value ?? e.lightsEnabled.value);
  /** …and the same pair for the sun's daylight and the weather sky. */
  const sunOn = computed(() => e.sunBorrow.value ?? e.sunEnabled.value);
  const weatherOn = computed(() => e.weatherBorrow.value ?? e.weatherSkyEnabled.value);

  /**
   * The sun's shadows, as a FILTER rather than a look.
   *
   * Owner, 2026-10-29: *"maybe we could aslo have show shadows/light filters on
   * both views"*. Shadow maps are the single most expensive thing the house
   * draws, and on a small room page they can be the difference between a moving
   * view and a still one — so the operator gets the switch, and it is the
   * renderer's own (every light, every mesh, no per-material bookkeeping).
   *
   * Touch devices ship with the map off (`!touchDevice` at build time); this
   * can only ever turn it further off, never on for them.
   */
  function setShadowsEnabled(on: boolean): void {
    const next = Boolean(on);
    if (e.shadowsEnabled.value === next) return;
    e.shadowsEnabled.value = next;
    applyShadowsEnabled();
    save();
    invalidate();
  }

  function applyShadowsEnabled(): void {
    const renderer = state.value?.renderer;
    if (!renderer) return;
    renderer.shadowMap.enabled = shadowsOn.value && SHADOWS_SUPPORTED;
    // A program is compiled with the shadow code only while the map is on: three
    // keys its cache on that flag, so the next frame recompiles by itself.
    invalidate();
  }

  /**
   * A VISITING surface's answer for the two light filters (a room view), with no
   * write to the page's own settings: `null` hands the page's values back. The
   * blob keeps writing the page's preferences either way, so a room's taste can
   * never overwrite the house's.
   */
  function borrowShadows(on: boolean | null): void {
    const next = typeof on === 'boolean' ? on : null;
    if (e.shadowsBorrow.value === next) return;
    e.shadowsBorrow.value = next;
    applyShadowsEnabled();
  }

  function borrowLights(on: boolean | null): void {
    const next = typeof on === 'boolean' ? on : null;
    if (e.lightsBorrow.value === next) return;
    e.lightsBorrow.value = next;
    invalidate();
  }

  /**
   * The sun's daylight, borrowed by a visiting surface (a room view's own "Sun"
   * switch) — no write to the page's setting, exactly like the two light filters.
   */
  function borrowSun(on: boolean | null): void {
    const next = typeof on === 'boolean' ? on : null;
    if (e.sunBorrow.value === next) return;
    e.sunBorrow.value = next;
    applySunEnabled();
  }

  /** …and the weather sky, borrowed the same way (a DOM backdrop, so no repaint cost). */
  function borrowWeather(on: boolean | null): void {
    const next = typeof on === 'boolean' ? on : null;
    if (e.weatherBorrow.value === next) return;
    e.weatherBorrow.value = next;
    paintBackground();
    updateSkyDiscs();
  }

  /**
   * The devices' own lights — the fixture glow and the pools it throws.
   *
   * One switch for both layers on purpose (owner: *"Device lights: fixture glow
   * + light pools"*): a lamp that glows but lights nothing, or lights a room
   * with a dark body, is half a lamp. The layers read this through the scene's
   * own ref, so the page and every room view agree.
   */
  function setLightsEnabled(on: boolean): void {
    const next = Boolean(on);
    if (e.lightsEnabled.value === next) return;
    e.lightsEnabled.value = next;
    save();
    invalidate();
  }

  /**
   * The ROOM pages' filter — one writer for every room page.
   *
   * It rides the same blob as the sun, so a room page's taste is the house's:
   * open another room and the switches are where they were left. A patch that
   * changes nothing writes nothing (same rule as `patchViewSettings`).
   */
  function patchRoomViewFilter(patch: Partial<HubBimRoomViewFilter>): void {
    const next = { ...e.roomView.value, ...patch };
    const same = (Object.keys(next) as (keyof HubBimRoomViewFilter)[]).every((key) => next[key] === e.roomView.value[key]);
    if (same) return;
    e.roomView.value = next;
    save();
    invalidate();
  }
  const CAMERA_INTERACTION_IDLE_MS = 120;

  function updateCameraAzimuthRefs(camera: THREE.PerspectiveCamera) {
    const dir = new THREE.Vector3();
    camera.getWorldDirection(dir);
    const azimuthRad = Math.atan2(dir.x, dir.z);
    e.cameraAzimuth.value = azimuthRad * (180 / Math.PI);
    bimCameraAzimuth.value = azimuthRad;
  }

  function renderScene() {
    const s = state.value;
    if (!s || e.suspended) return;
    updateCameraAzimuthRefs(s.camera);
    // A pass that drew first owns the frame's clear; the model then draws OVER
    // it rather than wiping it (`autoClear` goes back on for the next frame).
    s.renderer.autoClear = !renderSkyBodies(s);
    s.renderer.render(s.scene, s.camera);
    renderOverlayScene(s);
    s.labelRenderer.render(s.scene, s.camera);
    s.renderer.autoClear = true;
  }

  /**
   * The sky's own bodies, drawn first, with the section cut lifted.
   *
   * A sun and a moon are not part of the building, so a cut must never slice
   * them (owner, 2026-10-29: *"the sun (and the moon) is cut by a cut plane..
   * can we exempt it from being cut"*). The renderer's planes are GLOBAL and a
   * sprite has no per-material opt-out, so the two discs live in their own
   * scene (`skyScene`) and are drawn like a sky: before the model. They write
   * no depth, so the building still covers them, and the model's own
   * transparent surfaces still blend over them — the same picture they made
   * when they shared one scene with it.
   *
   * Returns whether it drew: the frame is then already cleared.
   */
  function renderSkyBodies(s: BimSceneState): boolean {
    if (!e.sunDisc?.visible && !e.moonDisc?.visible) return false;
    const clippingPlanes = s.renderer.clippingPlanes;
    s.renderer.clippingPlanes = [];
    s.renderer.render(s.skyScene, s.camera);
    s.renderer.clippingPlanes = clippingPlanes;
    return true;
  }

  /**
   * The gizmos' own pass, over the finished frame: the renderer's clipping
   * planes are handed an empty list for this one call (and given straight back),
   * so the ceiling cut never slices a handle. The planes themselves are not
   * touched — the same instances go back before anything else reads them, and
   * the next frame's main render sees them unchanged.
   */
  function renderOverlayScene(s: BimSceneState) {
    if (s.overlayScene.children.length === 0) return;
    const clippingPlanes = s.renderer.clippingPlanes;
    const autoClear = s.renderer.autoClear;
    s.renderer.clippingPlanes = [];
    s.renderer.autoClear = false;
    s.renderer.render(s.overlayScene, s.camera);
    s.renderer.autoClear = autoClear;
    s.renderer.clippingPlanes = clippingPlanes;
  }

  function stopRenderLoop() {
    if (!animationId.value) return;
    cancelAnimationFrame(animationId.value);
    animationId.value = 0;
  }

  function stopCameraSerialFrame() {
    if (!e.cameraSerialFrameId.value) return;
    cancelAnimationFrame(e.cameraSerialFrameId.value);
    e.cameraSerialFrameId.value = 0;
  }

  function clearCameraInteractionTimeout() {
    if (!e.cameraInteractionTimeout) return;
    clearTimeout(e.cameraInteractionTimeout);
    e.cameraInteractionTimeout = null;
  }

  function queueCameraSerialUpdate() {
    if (e.cameraSerialFrameId.value) return;
    e.cameraSerialFrameId.value = requestAnimationFrame(() => {
      e.cameraSerialFrameId.value = 0;
      if (e.suspended) return;
      cameraSerial.value++;
    });
  }

  function markCameraInteraction() {
    cameraInteracting.value = true;
    clearCameraInteractionTimeout();
    e.cameraInteractionTimeout = setTimeout(() => {
      e.cameraInteractionTimeout = null;
      cameraInteracting.value = false;
    }, CAMERA_INTERACTION_IDLE_MS);
  }

  /**
   * ─── The view's motion gate (card t_72106dc1) ─────────────────────────────
   *
   * Everything below reads the CAMERA'S OWN POSE, not the input events: the
   * turntable's damping, a drag-look in a first-person mode, a WASD walk, a
   * wheel/pinch zoom and the load-fit's own placement all end up here, and an
   * event that moves nothing (a click on the canvas) must not blank the layer.
   *
   * `cameraMoving` is latched: the FIRST frame past the threshold sets it, and
   * only the settle timer clears it — `BIM_CAMERA_SETTLE_MS` after the last
   * frame that moved at all, so damping's tail keeps the layer away and a
   * micro-drag (a nudge under the threshold) never touches it.
   */

  /** Remember the pose the NEXT move will be measured against. */
  function resetCameraSettlePose() {
    const s = state.value;
    if (!s) return;
    e.cameraSettledPos.copy(s.camera.position);
    e.cameraSettledQuat.copy(s.camera.quaternion);
    e.cameraLastPos.copy(s.camera.position);
    e.cameraLastQuat.copy(s.camera.quaternion);
  }

  function clearCameraSettleTimer() {
    if (!e.cameraSettleTimer) return;
    clearTimeout(e.cameraSettleTimer);
    e.cameraSettleTimer = null;
  }

  /** The view stopped: the overlay layer may come back, measured afresh from here. */
  function settleCameraMotion() {
    e.cameraSettleTimer = null;
    cameraMoving.value = false;
    resetCameraSettlePose();
  }

  function scheduleCameraSettle() {
    if (e.cameraSettleTimer) clearTimeout(e.cameraSettleTimer);
    e.cameraSettleTimer = setTimeout(settleCameraMotion, BIM_CAMERA_SETTLE_MS);
  }

  /**
   * One frame's verdict: is the view moving? Called from the render loop after
   * the frame's own camera updates, so the frame a key-driven walk moves is the
   * frame the layer goes away.
   *
   * Two different deltas, on purpose:
   *   · the move accumulated since the settled pose decides whether the layer
   *     goes away — a nudge of a few pixels never gets there, an orbit or a
   *     walk crosses it at once;
   *   · the frame-over-frame delta decides whether the view is STILL moving —
   *     so the hold ends when the movement stops being visible, not when the
   *     camera happens to be back near where it settled (damping eases for
   *     about a second above that floor and then lets the layer return).
   */
  function detectCameraMotion() {
    const s = state.value;
    if (!s) return;
    const angleRad = s.camera.quaternion.angleTo(e.cameraSettledQuat);
    const metres = s.camera.position.distanceTo(e.cameraSettledPos);
    const frameAngleRad = s.camera.quaternion.angleTo(e.cameraLastQuat);
    const frameMetres = s.camera.position.distanceTo(e.cameraLastPos);
    e.cameraLastQuat.copy(s.camera.quaternion);
    e.cameraLastPos.copy(s.camera.position);

    if (cameraMotionHidesOverlays(angleRad, metres)) cameraMoving.value = true;
    if (!cameraMotionIsNoise(frameAngleRad, frameMetres)) scheduleCameraSettle();
  }

  /**
   * Switch the viewer's navigation mode (card t_9cf7e787) and hand the camera
   * over cleanly between the two regimes:
   *
   *   · ORBIT keeps the turntable in charge — the mode this page has always had;
   *   · the first-person modes take the pointer and drive the camera directly,
   *     so OrbitControls is switched OFF (its `update()` would `lookAt` the
   *     target and square the head back up on the very next frame);
   *   · coming back from a first-person mode, the turntable is re-seated on the
   *     view the walker left — the target is put a few metres ahead along the
   *     line of sight, so the house stays in frame instead of the camera
   *     spinning around a stale target.
   */
  function setNavMode(mode: BimNavMode) {
    const previous = navMode.value;
    const wasFirstPerson = isFirstPersonMode(previous);
    if (previous === mode) {
      applyNavControls(mode);
      return;
    }
    navMode.value = mode;
    storeNavMode(mode, navStorage());
    // The house opens in the mode it was left in, on every device.
    save();
    // The way out of SIMULATION is the way IN (owner, 2026-10-19: *"entering
    // simulation and then exiting should restore the previous mode"*): the mode it
    // replaced is remembered here and spent by `exitNavMode`. Any other mode
    // clears it, so a switch that never entered a simulation cannot resurrect one.
    e.returnMode = mode === 'sim' ? previous : null;
    // A simulation owns the section plane while it runs, so ANY way out — the
    // bar's Exit, Esc or a preset that switches the mode — hands the operator's own
    // band back here, not only in `exitNavMode`.
    if (previous === 'sim' && mode !== 'sim') releaseSimCut();
    // A hop does not survive a mode switch (card t_565b83bf): the arc belongs to
    // the SIMULATION session that started it, and leaving it mid-air would hang the
    // body over a floor the mode no longer resolves.
    e.jump = null;
    // …and neither does the WHEEL's walk: it belongs to the mode that was walking,
    // so a notch still bleeding off when the mode changes moves nothing.
    e.wheelWalkForward = 0;
    // A walk LEAVES its place behind for the next one (owner, 2026-10-14): read before
    // the mode changes anything, so re-entering gravity resumes where it stopped.
    if (previous === 'gravity') rememberWalkerPose();
    // …and the TURNTABLE leaves its own behind on the way into ANY first-person mode,
    // which is what the exit shows again (the two places are separate, owner,
    // 2026-10-14) — including SIMULATION (owner, 2026-10-20: *"exiting the simulation
    // should take you to orbit mode last position before you entered the sim mode..
    // similar like we hawe in wlak mode"*). A sim entered from the WALK leaves this
    // bank alone: its own way out is the walk, which hands the turntable back itself.
    if (previous === 'orbit' && (mode === 'gravity' || mode === 'sim')) rememberOrbitPose();
    applyNavControls(mode);
    if (isFirstPersonMode(mode) && !wasFirstPerson) enterFirstPerson();
    else if (!isFirstPersonMode(mode) && wasFirstPerson) leaveFirstPerson();
    invalidate();
  }

  /**
   * The exit the bar's Exit button and Esc both use (owner, 2026-10-19): a
   * SIMULATION hands back the mode it was entered from — the WALK stays the walk,
   * on its own place — and every other first-person mode goes back to the
   * turntable, which is where it came from.
   */
  function exitNavMode() {
    const back = e.returnMode ?? 'orbit';
    e.returnMode = null;
    releaseSimCut();
    setNavMode(back);
  }

  /**
   * Hand the turntable back for a LOOK — the door a surface that only WATCHES the
   * house uses (the dashboard's model card).
   *
   * The hand-over is the walk's own (`leaveFirstPerson`: the camera returns to the
   * place the turntable left, which is where the owner expects a card to be), but
   * the HOUSE's mode is left as it is: the 3D page opens in the mode it was left
   * in, and a card that happened to look at a walking house must not turn that
   * walk off for the next visit (`storeNavMode`/`save` are skipped). Owner,
   * 2026-10-30: *"if we go to walk mode on page. on dash the view gets taken far
   * to default fit view .. it should take you to last orbit view"*.
   */
  function leaveFirstPersonForLook(): void {
    const previous = navMode.value;
    if (!isFirstPersonMode(previous)) return;
    e.returnMode = null;
    if (previous === 'sim') releaseSimCut();
    // The two session bits a mode switch always drops (a hop, and the wheel's walk).
    e.jump = null;
    e.wheelWalkForward = 0;
    // Where the walk stopped is kept for the next walk, exactly as `setNavMode` does.
    if (previous === 'gravity') rememberWalkerPose();
    navMode.value = 'orbit';
    applyNavControls('orbit');
    // The turntable comes back on the place it was left, not on the walker's eye.
    leaveFirstPerson();
    invalidate();
  }

  /** The layout's own "back to the start" door — the sim bar's button and the R key. */
  function setDrivenResetHandler(handler: (() => void) | null): void {
    e.onDrivenReset = handler;
  }

  /**
   * The driven body's eye height (owner, 2026-10-20) — the LAYOUT measures the
   * avatar's own model and hands the number over, and the POV camera stands there.
   */
  function setDrivenEyeHeight(height: number): void {
    if (!Number.isFinite(height) || height <= 0.1) return;
    e.drivenEyeHeight = height;
    invalidate();
  }

  /**
   * Enter GRAVITY standing at a named place — the room pin's long press (owner,
   * 2026-10-19: *"long press on room pin should enter gravity mode with the view
   * inside the room"*).
   *
   * The place becomes the walk's OWN pose BEFORE the mode changes, because
   * entering gravity always stands in `gravityEntryPose()` and never at whatever
   * the turntable happened to orbit to. The camera is put at the feet that pose
   * resolves to (`enterFirstPerson`'s own gravity rule), so a room entry is the
   * same walk a re-entry is — from orbit, from SIMULATION and while already
   * walking alike. Only the orbit hand-over seats the walk for us; the other two
   * are mode-only switches, so the seat is made directly.
   *
   * The walk keeps the APPROACH BEARING (owner, 2026-10-19: *"when entering room
   * ground mode we should keep the approach angle"*) — but LEVELED to the horizon
   * (owner the same day: *"when dropped into gravity we want to level the 'head'
   * to the horizon.. so we only take azimuth into account"*): unless the caller
   * names a look, the head takes only the camera's AZIMUTH, so an aerial orbit's
   * downward tilt is not carried into the room.
   *
   * Leaving hands back the view the room was entered from (owner, 2026-10-19:
   * *"also when leaving, leave as entered"*). Orbit is banked by `setNavMode` it-
   * self (`rememberOrbitPose`); SIMULATION has no turntable place, so its view is
   * read here — camera and bearing — before the walk takes the camera over.
   */
  function enterGravityAt(place: NavPoint, look?: NavLook): void {
    const s = state.value;
    if (!s) return;
    const dir = new THREE.Vector3();
    s.camera.getWorldDirection(dir);
    const approach = look ?? levelLook({ x: dir.x, y: dir.y, z: dir.z }, e.look.yaw);
    const previous = navMode.value;
    const returnPose = previous === 'sim' ? currentViewPose() : null;
    e.walkerPose = { x: place.x, y: place.y, z: place.z, yaw: approach.yaw, pitch: approach.pitch };
    // Only ORBIT is handed over by `setNavMode` (it calls `enterFirstPerson` when
    // the walk begins). Coming from another first-person mode — or already
    // walking — the switch is mode-only, so the seat is made here.
    const wasFirstPerson = isFirstPersonMode(previous);
    setNavMode('gravity');
    if (wasFirstPerson) enterFirstPerson();
    if (returnPose) e.orbitPose = returnPose;
    invalidate();
  }

  /**
   * Switch SIMULATION mode's camera view (card t_01ebef40) — the `V` key, the pill's
   * button and the rigs all land here.
   *
   * The view is stored per user like the mode is, so a choice survives a
   * reload; and because the camera is re-seated in the same call, the switch
   * reads as one camera moving between two places over the same body, not as a
   * change that waits for the next key press.
   */
  function setSimView(view: BimSimView) {
    if (e.simView.value === view) return;
    e.simView.value = view;
    storeSimView(view, navStorage());
    save();
    // The drone OPENS looking down at the body (its own default steepness) and
    // closes by handing the user's section plane back (owner, 2026-10-13).
    e.look = simViewOpeningLook(view, e.look);
    if (view === 'drone') e.droneOrbitOffset = 0;
    const s = state.value;
    if (s && isThirdPersonMode(navMode.value)) {
      const placed = placeCameraForView(view);
      if (placed) queueCameraSerialUpdate();
      updateCameraAzimuthRefs(s.camera);
    }
    invalidate();
  }

  function toggleSimView() {
    setSimView(oppositeSimView(e.simView.value));
  }

  /**
   * Open the ACTIVE game view the way it opens — its own look and the camera
   * seated for it (owner, 2026-10-13: *"we should preserver the game view when
   * switching to another character"*).
   *
   * Entering the game and handing it to another body both come through here, so
   * the view the player chose is the view they get: the drone starts steeply
   * above whoever they now drive, instead of inheriting the pitch of the orbit
   * camera they happened to arrive on (measured: *"when entering game mode it
   * goes into drone. but drone is low"*).
   */
  function openSimView(): void {
    if (!isThirdPersonMode(navMode.value)) return;
    e.look = simViewOpeningLook(e.simView.value, e.look);
    // The drone opens BEHIND whoever is being handed the game (owner,
    // 2026-10-14) — the manual orbit is this session's nudge, not the view's.
    if (e.simView.value === 'drone') e.droneOrbitOffset = 0;
    const s = state.value;
    if (s) {
      const placed = placeCameraForView(e.simView.value);
      if (placed) queueCameraSerialUpdate();
      updateCameraAzimuthRefs(s.camera);
    }
    invalidate();
  }

  /**
   * Seat the camera the way the ACTIVE VIEW seats it. One dispatcher, because
   * there are five places the camera is re-seated (entering the mode, the spawn
   * seat, the per-frame follow, the view switch and the pose restore) and a
   * third view must not be able to be handled by two of them.
   */
  function placeCameraForView(view: BimSimView): boolean {
    return isPovCamera(navMode.value, view) ? placePovCamera() : placeDroneCamera();
  }

  function navStorage(): Storage | null {
    try {
      return typeof localStorage === 'undefined' ? null : localStorage;
    } catch {
      return null;
    }
  }

  /** OrbitControls owns the pointer in orbit; the first-person modes own it here. */
  function applyNavControls(mode: BimNavMode) {
    const s = state.value;
    if (!s) return;
    const firstPerson = isFirstPersonMode(mode);
    s.controls.enabled = !firstPerson;
    if (firstPerson) {
      s.controls.enableDamping = false;
      releasePointerLock();
    } else {
      s.controls.rotateSpeed = DEFAULT_ROTATE_SPEED;
      s.controls.enableDamping = true;
      s.controls.dampingFactor = DEFAULT_DAMPING_FACTOR;
    }
  }

  /**
   * Where a walk STARTS when gravity has no remembered place: the model's own default
   * (`defaultWalkPose` — outside the façade, on the ground floor, facing the house).
   *
   * `null` when the model is not known yet, in which case the walk falls back to the
   * old "from wherever the camera is" rule: a mode switch before the geometry arrives
   * has nothing to stand a body on.
   */
  function defaultGravityPose(): (NavPoint & NavLook) | null {
    const s = state.value;
    if (!s || modelMeshes.value.length === 0) return null;
    const floorY = e.walkLevels.length > 0 ? Math.min(...e.walkLevels) : modelBoundsY.value.min;
    // The reach comes from the FAÇADE's own box — the envelope the walk may not cross — and
    // not from the model's extent. The extent is the whole site (32 × 62 m here), half its
    // diagonal reaches 35 m, and the ground does not go that far: the body landed on the
    // site's outer rim, 0.5–0.9 m under the yard with no way back up (owner, 2026-10-14:
    // *"gravity is still off model"*; measured as 182 refused steps in five seconds). The
    // walk starts outside the BUILDING, on the ground, looking at it.
    const facade = boxOfMeshes(e.wallMeshes.length > 0 ? e.wallMeshes : modelMeshes.value);
    if (!facade) return null;
    const centre = {
      x: (facade.min.x + facade.max.x) / 2,
      y: (facade.min.y + facade.max.y) / 2,
      z: (facade.min.z + facade.max.z) / 2,
    };
    const outward = outwardBearingFrom(centre, s.camera.position) ?? { x: 0, z: 1 };
    const pose = defaultWalkPose({
      center: centre,
      radiusXZ: boxReachAlong(facade, outward),
      floorY,
      from: s.camera.position,
    });
    // …and the ground has to be under it: a façade's own edge can hang over a void, so the
    // place walks in toward the building until the model has ground where it stands.
    return pullOntoGround(pose, centre);
  }

  /** The unit bearing from a point to another, on the ground plane — `null` when they coincide. */
  function outwardBearingFrom(
    from: { x: number; z: number },
    to: { x: number; z: number },
  ): { x: number; z: number } | null {
    const dx = to.x - from.x;
    const dz = to.z - from.z;
    const length = Math.hypot(dx, dz);
    return length > 0.01 ? { x: dx / length, z: dz / length } : null;
  }

  /**
   * How far a box reaches from its own centre along a ground bearing: the face the ray
   * leaves through, which is the box's own answer — the diagonal is the furthest it reaches,
   * and only in one direction.
   */
  function boxReachAlong(box: PoseBox, bearing: { x: number; z: number }): number {
    const halfX = (box.max.x - box.min.x) / 2;
    const halfZ = (box.max.z - box.min.z) / 2;
    const reachX = Math.abs(bearing.x) > 1e-6 ? halfX / Math.abs(bearing.x) : Number.POSITIVE_INFINITY;
    const reachZ = Math.abs(bearing.z) > 1e-6 ? halfZ / Math.abs(bearing.z) : Number.POSITIVE_INFINITY;
    return Math.min(reachX, reachZ);
  }

  /**
   * A place with ground under it: stepped in toward the building, half a metre at a time,
   * until the model's own surfaces are beneath it. The feet go on the surface the walk's own
   * probe names, so a spawn beside a terrace deck stands on the deck and not at storey level.
   */
  function pullOntoGround(place: NavPoint & NavLook, centre: { x: number; y: number; z: number }): NavPoint & NavLook {
    const fromY = modelBoundsY.value.max + 1;
    const dx = centre.x - place.x;
    const dz = centre.z - place.z;
    const length = Math.hypot(dx, dz);
    if (length < 0.01) return place;
    for (let travelled = 0; travelled <= length; travelled += 0.5) {
      const x = place.x + (dx / length) * travelled;
      const z = place.z + (dz / length) * travelled;
      const y = probeWalkSupport(x, z, fromY);
      if (y !== null) return { ...place, x, z, y };
    }
    return place;
  }

  /**
   * The world box of a mesh set, measured off the meshes themselves — `null` when there is
   * nothing on screen to measure. `modelBoxOf` is the whole model's; the walk's spawn asks
   * for the ENVELOPE's (`e.wallMeshes`), which is the building rather than the site.
   */
  function boxOfMeshes(meshes: readonly THREE.Mesh[]): PoseBox | null {
    if (meshes.length === 0) return null;
    const box = new THREE.Box3();
    meshes.forEach((mesh) => box.expandByObject(mesh));
    if (box.isEmpty()) return null;
    return {
      min: { x: box.min.x, y: box.min.y, z: box.min.z },
      max: { x: box.max.x, y: box.max.y, z: box.max.z },
    };
  }

  /**
   * The loaded model's own world box — the same measurement `poseFactsAt` judges a stored
   * pose by, so "on the model" means the same box everywhere. The live layers' meshes
   * (device models, shades, avatars) are not part of it: they are not the model the walk is
   * authored on.
   */
  function modelBoxOf(): PoseBox | null {
    return boxOfMeshes(modelMeshes.value);
  }

  /**
   * The place GRAVITY stands when it is entered — `null` when the model is not known yet
   * and the camera is to keep the pose it has.
   *
   * Owner, 2026-10-14: *"when enter gravity show last gravity position on the ground. if
   * not on the ground the pace it to nearest point"*. So the walk returns to its OWN place
   * (the model's own spawn when it has never walked), and that place is put ON THE GROUND
   * before it is stood in: the walk's own cast finds the nearest surface beneath it, and a
   * place with no surface under it lands on the nearest measured level — a body left in
   * mid-air comes down to the floor under it rather than hanging there. Only a place with no
   * ground at all beneath it (under a model that has since moved, or no levels measured) is
   * replaced by the model's own spawn.
   *
   * The place is NOT required to lie inside the model's box: a body walks where it walks,
   * and the GROUND under the place is the question that matters — the one the walking frame
   * itself asks (`probeWalkSupport` + `resolveWalkFeetY`).
   */
  function gravityEntryPose(): (NavPoint & NavLook) | null {
    const s = state.value;
    if (!s) return null;
    // Nothing loaded: there is no model to stand on, so the camera keeps the pose it has.
    if (!modelBoxOf()) return null;
    const place = e.walkerPose;
    if (place) {
      const grounded = groundedPose(place);
      if (grounded) return grounded;
    }
    const spawn = defaultGravityPose();
    return spawn ? (groundedPose(spawn) ?? spawn) : null;
  }

  /**
   * A place with its feet ON THE GROUND: the nearest walkable surface beneath it (the
   * walk's own cast, with the measured levels as the fallback, so a body left in mid-air
   * lands on the floor under it rather than hanging there). `null` when that point has no
   * ground at all — the caller then stands the body at the model's own place.
   */
  function groundedPose(place: NavPoint & NavLook): (NavPoint & NavLook) | null {
    const supportY = probeWalkSupportArea(place.x, place.z, place.y);
    const resolved = resolveWalkFeetY({ supportY, levels: e.walkLevels, feetY: place.y });
    return resolved.blocked ? null : { ...place, y: resolved.feetY };
  }

  /** Keep the place gravity is standing in, so the mode can be re-entered there. */
  function rememberWalkerPose(): void {
    const s = state.value;
    if (!s) return;
    e.walkerPose = {
      x: s.camera.position.x,
      // The FEET, not the eyes: `enterFirstPerson` puts the eyes back on top of these.
      y: e.feetY ?? s.camera.position.y - NAV_EYE_HEIGHT_M,
      z: s.camera.position.z,
      yaw: e.look.yaw,
      pitch: e.look.pitch,
    };
  }

  /**
   * Keep the place the TURNTABLE is at, so the walk can hand it back on the way out
   * (owner, 2026-10-14: *"orbit and gravity should be 2 different pressets.. when exit the
   * ground show the orbit postition"*). The camera AND its target: the turntable's own
   * view is the pair, and re-seating only one of them would swing the house round.
   */
  function rememberOrbitPose(): void {
    const s = state.value;
    if (!s) return;
    e.orbitPose = {
      x: s.camera.position.x,
      y: s.camera.position.y,
      z: s.camera.position.z,
      targetX: s.controls.target.x,
      targetY: s.controls.target.y,
      targetZ: s.controls.target.z,
    };
  }

  /**
   * The view the camera is showing right now, as an orbit place — where it stands
   * and a point along its own line of sight. Unlike `rememberOrbitPose` it does NOT
   * read `controls.target`, which in a first-person mode is whatever the turntable
   * left behind rather than what the camera is aimed at. The distance only spaces
   * the re-seated turntable's target, so the walk's own reach is the natural one.
   */
  function currentViewPose(): typeof e.orbitPose {
    const s = state.value;
    if (!s) return null;
    const dir = new THREE.Vector3();
    s.camera.getWorldDirection(dir);
    const distance = THREE.MathUtils.clamp(e.orbitDistance || 6, 3, 30);
    return {
      x: s.camera.position.x,
      y: s.camera.position.y,
      z: s.camera.position.z,
      targetX: s.camera.position.x + dir.x * distance,
      targetY: s.camera.position.y + dir.y * distance,
      targetZ: s.camera.position.z + dir.z * distance,
    };
  }

  /**
   * The camera as a VIEW another surface can open on (`utils/bimViewHandoff`).
   *
   * The Explore doors use it: a card hands over the pose the owner just orbited
   * to, and a room page hands over its WALK so the 3D page stands where the room
   * view stood. A walk names the place its FEET stand — the eye is
   * `NAV_EYE_HEIGHT_M` above them — and carries its head direction; every other
   * mode names the turntable's own pair, the camera and what it looks at.
   */
  function captureLiveView(): BimViewHandoff | null {
    const s = state.value;
    if (!s) return null;
    const position = { x: s.camera.position.x, y: s.camera.position.y, z: s.camera.position.z };
    const target = { x: s.controls.target.x, y: s.controls.target.y, z: s.controls.target.z };
    if (navMode.value !== 'gravity') return { position, target, navMode: 'orbit' };
    const dir = new THREE.Vector3();
    s.camera.getWorldDirection(dir);
    return {
      position: { ...position, y: position.y - NAV_EYE_HEIGHT_M },
      target,
      navMode: 'gravity',
      look: lookFromDirection({ x: dir.x, y: dir.y, z: dir.z }),
    };
  }

  /** Enter a first-person mode from wherever the camera is — no jump, no teleport. */
  function enterFirstPerson() {
    const s = state.value;
    if (!s) return;
    const dir = new THREE.Vector3();
    s.camera.getWorldDirection(dir);
    e.look = lookFromDirection({ x: dir.x, y: dir.y, z: dir.z });
    e.velocity = { x: 0, y: 0, z: 0 };
    e.feetY = isWalkerMode(navMode.value) ? s.camera.position.y - NAV_EYE_HEIGHT_M : null;
    e.supportY = null;
    e.supportProbe = null;
    e.walkBlocked = 0;
    e.orbitDistance = s.camera.position.distanceTo(s.controls.target);

    // GRAVITY has its OWN place, and stands in it (owner, 2026-10-14: *"when enter gravity
    // show last gravity position on the ground. if not on the ground the pace it to nearest
    // point"*) — the model's own spawn the first time, the place the walk last stopped after
    // that, put on the ground by `gravityEntryPose()`. The turntable's place is kept for the
    // way out, so the two modes read as two places rather than one camera dragged about.
    // SIMULATION keeps the from-the-camera rule — it already has an avatar's own spawn, and
    // its body is meant to appear where the head was.
    if (navMode.value === 'gravity') {
      const pose = gravityEntryPose();
      if (pose) {
        s.camera.position.set(pose.x, pose.y + NAV_EYE_HEIGHT_M, pose.z);
        e.look = { yaw: pose.yaw, pitch: pose.pitch };
        e.feetY = pose.y;
        // …and the turntable comes back to THIS place when the walk ends, so the two
        // modes hand over at the house rather than at whatever the camera orbited to.
        // (Only when orbit left no place of its own: `leaveFirstPerson` prefers that one.)
        e.orbitDistance = new THREE.Vector3(pose.x, pose.y, pose.z).distanceTo(e.modelCenter);
      }
    }
    if (isThirdPersonMode(navMode.value)) {
      // SIMULATION: the avatar takes over the walker's job from the pose the camera
      // was already standing on — the body appears where the head was, so the
      // mode switch is a step back, not a teleport.
      syncDrivenToCamera();
      // …and the chosen view opens the way it opens (the drone above the body it
      // just adopted), so entering the game never starts the drone from the
      // pitch the orbit camera happened to be looking down at the house with.
      openSimView();
    }
    applyLookRotation();
    // The walk's own bookkeeping (owner, 2026-10-18): the rescue starts from the
    // pose the body really enters at, and the cut is widened to include its feet
    // before the first frame is painted. `lastFree` is what a body stuck inside a
    // wall is put back to, so a fresh entry seeds it with the entry pose itself.
    const body = isThirdPersonMode(navMode.value) ? e.driven : s.camera.position;
    const feet = e.feetY ?? body.y - NAV_EYE_HEIGHT_M;
    e.lastFree = { x: body.x, z: body.z, feetY: feet };
    e.insideWall = false;
    e.rescues = 0;
    guardWalkCut(feet);
  }

  /**
   * Hand the camera back to the turntable — at the place orbit was LEFT at, which is what
   * the exit shows (owner, 2026-10-14: *"when exit the ground show the orbit postition"*).
   * The place is spent here: a session that never orbited (the app opened in the walk) has
   * none, and falls back to the older rule — centred a few metres ahead of the view the
   * walker left, so the house stays in frame instead of the camera spinning on a stale
   * target.
   */
  function leaveFirstPerson() {
    const s = state.value;
    if (!s) return;
    releasePointerLock();
    // The operator's own section band comes back with the turntable (2026-10-18):
    // the walk widened it to keep the ground under the body, and the view he
    // returns to is the one he had.
    releaseWalkCut();
    e.velocity = { x: 0, y: 0, z: 0 };
    e.feetY = null;
    e.supportProbe = null;
    e.insideWall = false;
    const orbit = e.orbitPose;
    e.orbitPose = null;
    if (orbit) {
      s.camera.position.set(orbit.x, orbit.y, orbit.z);
      s.controls.target.set(orbit.targetX, orbit.targetY, orbit.targetZ);
      s.controls.update();
      queueCameraSerialUpdate();
      updateCameraAzimuthRefs(s.camera);
      return;
    }
    const dir = new THREE.Vector3();
    s.camera.getWorldDirection(dir);
    const distance = THREE.MathUtils.clamp(e.orbitDistance || 6, 3, 30);
    s.controls.target.copy(s.camera.position).addScaledVector(dir, distance);
    s.controls.update();
    queueCameraSerialUpdate();
    updateCameraAzimuthRefs(s.camera);
  }

  /**
   * Point the camera along the first-person look. Three's YXZ order is what
   * makes yaw and pitch independent — with the default XYZ order a fresh pitch
   * would tilt the horizon as the walker turns.
   */
  function applyLookRotation() {
    const s = state.value;
    if (!s) return;
    s.camera.rotation.order = 'YXZ';
    s.camera.rotation.set(e.look.pitch, e.look.yaw, 0);
  }

  // ─── The pointer: drag to look, click to lock (card t_9cf7e787) ────────────

  /**
   * The pointer is NEVER captured (owner, 2026-10-13: *"in game mode remove mouse
   * lock, which locks cursor and does zoom mode and pan.. remove lock here for
   * each mode you must drag the view, zoom with scoll"*). The lock used to be
   * taken by a clean click, which is exactly what made the cursor disappear and
   * the wheel useless; every mode is drag-to-look now, and the wheel zooms.
   */
  function releasePointerLock() {
    try {
      if (document.pointerLockElement) document.exitPointerLock();
    } catch {
      /* nothing to release */
    }
  }

  /**
   * Pointer look + wheel zoom. A DRAG is the look, always: the pointer is never
   * locked (owner, 2026-10-13), and where the layout still needs the pointer for
   * itself — the dashboard editor, the element editor, the device placer and pick
   * mode — `setPointerLockAllowed` is what keeps the drag from looking.
   */
  function attachPointerLook(element: HTMLElement) {
    if (e.pointerHandlers) return;
    const drag = e.dragLook;

    const onPointerDown = (ev: PointerEvent) => {
      if (!isFirstPersonMode(navMode.value) || !e.pointerLockAllowed.value) return;
      if (ev.button !== 0) return;
      drag.active = true;
      drag.pointerId = ev.pointerId;
      drag.lastX = ev.clientX;
      drag.lastY = ev.clientY;
      drag.moved = 0;
      try {
        element.setPointerCapture(ev.pointerId);
      } catch {
        /* capture is a nicety, the drag works without it */
      }
    };

    const onPointerMove = (ev: PointerEvent) => {
      if (!isFirstPersonMode(navMode.value)) return;
      if (!drag.active) return;
      // …and the pointer is still OURS. Entering edit mode (or the device placer, or
      // pick mode) hands the pointer to another surface, and a drag that began
      // before that must not go on steering the camera — which is exactly how
      // *"drag to look gets messed up if toggling edit mode"* (owner, 2026-10-14)
      // showed itself: the hold was released elsewhere and the look kept turning.
      if (!e.pointerLockAllowed.value) {
        drag.active = false;
        return;
      }
      const dx = ev.clientX - drag.lastX;
      const dy = ev.clientY - drag.lastY;
      drag.lastX = ev.clientX;
      drag.lastY = ev.clientY;
      drag.moved += Math.abs(dx) + Math.abs(dy);
      if (dx === 0 && dy === 0) return;
      if (isDroneView(navMode.value, simView.value)) {
        // The DRONE's drag (owner, 2026-10-14): *"mouse drag can lift/lower the
        // angle (up/down) and turn the character (left/right)"*. ONE OWNER PER
        // AXIS: the HORIZONTAL axis turns the CHARACTER — the A/D letters are the
        // camera's orbit offset's only owner, so a drag can never fight them — and
        // the VERTICAL axis is the camera's own angle, the very number W/S write,
        // in the same band. The camera then swings along behind the turned body by
        // itself, which is the follow rule doing its job.
        //
        // …and this view's two deltas arrive MIRRORED (`droneDragDeltas`, owner,
        // 2026-10-15: *"in sim mode the mouse drags should be inverted in
        // direction.."*, *"sim mode. drone view!"*): drag right turns the character
        // right and drag up tilts the view up, where every other drag in the viewer
        // pulls the world instead.
        const hand = droneDragDeltas(dx, dy);
        const turned = turnYawDegByDrag(e.driven.yawDeg, hand.dx);
        const lifted = applyLookDelta(e.look, 0, hand.dy);
        e.look = { yaw: bodyHeadingYaw(turned), pitch: clampDronePitch(lifted.pitch) };
        e.driven.yawDeg = turned;
      } else {
        e.look = applyLookDelta(e.look, dx, dy);
      }
      // In SIMULATION's drone view the placement owns the camera's rotation (it aims at
      // the avatar's head): applying the look angles directly here would point
      // the view away from the body for the frame between this move and the next
      // placement, so the next frame does it. Everywhere the camera IS the
      // character (GRAVITY, SIMULATION's POV view) the look applies now.
      if (isPovCamera(navMode.value, simView.value)) applyLookRotation();
      markCameraInteraction();
      invalidate();
    };

    const onPointerUp = (ev: PointerEvent) => {
      if (!drag.active) return;
      drag.active = false;
      try {
        element.releasePointerCapture(ev.pointerId);
      } catch {
        /* nothing captured */
      }
    };

    /**
     * The wheel, in the first-person modes (owner, 2026-10-13: *"zoom with
     * scoll"*): the DRONE camera changes its DISTANCE — from the character, in the
     * drone's own band (owner, 2026-10-14: *"mouse wheel = camera distance from
     * character"*) — the POV its own lens. Orbit
     * keeps OrbitControls' wheel — it is the one mode that always had a working zoom,
     * and doubling it here would fight it.
     */
    const onWheel = (ev: WheelEvent) => {
      if (!isFirstPersonMode(navMode.value)) return;
      const s0 = state.value;
      if (!s0) return;
      ev.preventDefault();
      // GRAVITY is the WALK, so its wheel WALKS (owner, 2026-10-14: *"scroll to zoom
      // should be disbaled in walko mode.. scroll should move the camera forwaerd,
      // backward.. zoom should be minimal"*). The impulse feeds the same forward
      // axis W/S write, so the wheel is a second way to press W and the walk's own
      // speed, floor probe and blocked-step rule all still apply. Its lens is left
      // ALONE: zooming is not what a walker reaches for.
      if (navMode.value === 'gravity') {
        e.wheelWalkForward = wheelWalkImpulse(e.wheelWalkForward, ev.deltaY);
        markCameraInteraction();
        invalidate();
        return;
      }
      if (isPovCamera(navMode.value, e.simView.value)) {
        s0.camera.fov = fovByWheel(s0.camera.fov, ev.deltaY);
        s0.camera.updateProjectionMatrix();
      } else {
        e.droneDistance = zoomDroneDistanceByWheel(e.droneDistance, ev.deltaY);
      }
      markCameraInteraction();
      invalidate();
    };

    element.addEventListener('pointerdown', onPointerDown);
    element.addEventListener('pointermove', onPointerMove);
    element.addEventListener('pointerup', onPointerUp);
    element.addEventListener('pointercancel', onPointerUp);
    // `passive: false`, because the zoom must be able to eat the wheel: a page
    // scroll under a walking character is not a thing anyone asked for.
    element.addEventListener('wheel', onWheel, { passive: false });
    e.pointerHandlers = { element, onPointerDown, onPointerMove, onPointerUp, onWheel };
  }

  function detachPointerLook() {
    const handlers = e.pointerHandlers;
    if (!handlers) return;
    handlers.element.removeEventListener('pointerdown', handlers.onPointerDown);
    handlers.element.removeEventListener('pointermove', handlers.onPointerMove);
    handlers.element.removeEventListener('pointerup', handlers.onPointerUp);
    handlers.element.removeEventListener('pointercancel', handlers.onPointerUp);
    if (handlers.onWheel) handlers.element.removeEventListener('wheel', handlers.onWheel);
    e.pointerHandlers = null;
    e.dragLook.active = false;
    pointerLocked.value = false;
  }

  /**
   * The layout's own answer to "may a click take the pointer?": the dashboard
   * editor, the element editor, the device placer and pick mode all need the
   * pointer for themselves.
   */
  function setPointerLockAllowed(allowed: boolean) {
    e.pointerLockAllowed.value = allowed;
    if (!allowed) {
      // A drag in flight ENDS here (owner, 2026-10-14: *"drag to look gets messed up
      // if toggling edit mode"*): the pointer now belongs to the editor that took
      // it, so the walker must not keep looking on a button it no longer owns.
      e.dragLook.active = false;
      releasePointerLock();
    }
  }

  /**
   * The walking surface at (x, z): the first mesh a ray cast DOWN from one step
   * above the feet meets. Casting from the step ceiling is what makes stairs
   * work — the next tread is inside the window and is climbed — while anything
   * taller (a counter top, a wall's own top) is simply not a surface: v1 walks
   * through it, exactly as the card allows for walls. Meshes the operator hid
   * and anything the section cut removed are not surfaces either, because the
   * ray must only answer for what is on screen.
   *
   * Card t_e86ce88e: the walk-surface set is tried first (cheap), and when even
   * that misses, the WHOLE model is asked once — "the nearest surface beneath"
   * is a fact about the loaded geometry, not about which mesh names happen to be
   * in a list. The wide cast only runs where the walker has nothing underfoot at
   * all, so the walking-in-a-room frame budget is unchanged.
   */
  function probeWalkSupport(x: number, z: number, feetY: number): number | null {
    return probeWalkSupportInfo(x, z, feetY).y;
  }

  /**
   * One point's support, read from the model — no cache, no state. The ring below
   * samples through this so eight extra casts cannot rewrite the rigs' reading.
   */
  function sampleWalkSupport(
    x: number,
    z: number,
    feetY: number,
  ): { y: number | null; name: string | null; typeName: string | null; expressID: number | null; source: string } {
    const s = state.value;
    if (!s || modelMeshes.value.length === 0)
      return { y: null, name: null, typeName: null, expressID: null, source: 'none' };

    const cast = (targets: THREE.Object3D[]) => {
      s.raycaster.set(new THREE.Vector3(x, feetY + NAV_STEP_UP_MAX_M, z), new THREE.Vector3(0, -1, 0));
      const hits = s.raycaster.intersectObjects(targets, false);
      for (const hit of hits) {
        if (hit.object.visible === false) continue;
        // WATER IS NOT A FLOOR (owner, 2026-10-21: *"would be good if our walk and
        // simulation modes would allow to sink in"*). A pool sheet is a horizontal
        // plate, so it answers this cast exactly like a terrace deck would — the
        // body then walks ACROSS the water. Skipping the water meshes (matched once
        // per load, `e.waterMeshes`) sends the ray on down to the basin floor, and
        // `resolveWalkFeetY` follows a DROP without limit: that is the sink.
        if (e.waterMeshes.includes(hit.object as THREE.Mesh)) continue;
        // The cut is NOT consulted (owner, 2026-10-18: *"you can fall into a cut
        // wall … you fall into an cant get out?"*): skipping clipped hits took the
        // floor away whenever the band's floor rose above the walker's feet, and
        // with no storey fallback in a GLB there is nothing left to stand on.
        // Collision reads the authored building; the band is a view.
        return hit;
      }
      return null;
    };

    const narrowTargets = e.walkSurfaces.length > 0 ? e.walkSurfaces : modelMeshes.value;
    let hit = cast(narrowTargets);
    let source = e.walkSurfaces.length > 0 ? 'walk-set' : 'model';
    if (!hit && e.walkSurfaces.length > 0) {
      // Nothing on the walk set — ask the whole model, which is the honest
      // answer to "what is nearest beneath me" (a terrain block, a scan's own
      // floor mesh, a deck the type list never named).
      hit = cast(modelMeshes.value);
      source = 'model-wide';
    }

    const expressID = hit ? ((hit.object.userData.expressID as number | undefined) ?? null) : null;
    const element = expressID === null ? null : (ifcElements.value.find((el) => el.expressID === expressID) ?? null);
    return hit
      ? {
          y: hit.point.y,
          name: element?.name ?? null,
          typeName: element?.typeName ?? String(hit.object.userData.typeName ?? ''),
          expressID,
          source,
        }
      : { y: null, name: null, typeName: null, expressID: null, source };
  }

  /**
   * The same probe, with the cache and the rigs' own reading (`e.supportY` /
   * `e.supportHit`, which `navDebug` and the rigs print) written for ONE point.
   */
  function probeWalkSupportInfo(
    x: number,
    z: number,
    feetY: number,
  ): { y: number | null; name: string | null; typeName: string | null; expressID: number | null; source: string } {
    const s = state.value;
    if (!s || modelMeshes.value.length === 0)
      return { y: null, name: null, typeName: null, expressID: null, source: 'none' };
    const cached = e.supportProbe;
    if (cached && Math.abs(cached.x - x) < 0.05 && Math.abs(cached.z - z) < 0.05 && Math.abs(cached.y - feetY) < 0.05) {
      return {
        ...(e.supportHit ?? { name: null, typeName: null, expressID: null }),
        y: e.supportY,
        source: e.supportHit ? 'cache' : 'cache-miss',
      };
    }
    // …and the cast itself is bounded (measured; see NAV_PROBE_INTERVAL_MS): a
    // ray per frame against every mesh of a 20 MB model cost more than the frame
    // it ran in. Within the interval and the skip radius the last answer stands.
    if (
      cached &&
      Date.now() - cached.at < NAV_PROBE_INTERVAL_MS &&
      Math.abs(cached.x - x) < NAV_PROBE_SKIP_M &&
      Math.abs(cached.z - z) < NAV_PROBE_SKIP_M
    ) {
      return {
        ...(e.supportHit ?? { name: null, typeName: null, expressID: null }),
        y: e.supportY,
        source: e.supportHit ? 'cache' : 'cache-miss',
      };
    }

    const info = sampleWalkSupport(x, z, feetY);
    e.supportProbe = { x, z, y: feetY, at: Date.now() };
    e.supportY = info.y;
    e.supportHit =
      info.y === null ? null : { name: info.name, typeName: info.typeName, expressID: info.expressID };
    return info;
  }

  /**
   * The FOOTPRINT probe (owner, 2026-10-20: *"a small crevice in the model e.g. by
   * floor lifted by a millimetre can make avatar slip through.. cant we assign some
   * radius to the model so it can't fall through?"*).
   *
   * The centre is asked first and its answer stands — that is the floor it walks
   * on. Only when the centre finds NOTHING (a hairline crack between two plates, the
   * seam of a floor finish) a ring of points `NAV_SUPPORT_RADIUS_M` around it is
   * sampled, and the highest one that is no more than a step above the feet carries
   * the body: the rim of a crack is still floor, while a chair seat under the
   * footprint is an obstacle. Nothing in the ring → `null`, and it falls.
   */
  function probeWalkSupportArea(x: number, z: number, feetY: number): number | null {
    const centre = probeWalkSupportInfo(x, z, feetY);
    if (centre.y !== null) return centre.y;
    const samples = NAV_SUPPORT_RING.map((dir) =>
      sampleWalkSupport(x + dir.x * NAV_SUPPORT_RADIUS_M, z + dir.z * NAV_SUPPORT_RADIUS_M, feetY),
    );
    const best = pickFootprintSupport(
      null,
      samples.map((sample) => sample.y),
      feetY,
    );
    if (best === null) return null;
    const winner = samples.find((sample) => sample.y === best) ?? null;
    // The rigs read WHO carries the body: report the rim that caught it, not the
    // hole it did not.
    e.supportProbe = { x, z, y: feetY, at: Date.now() };
    e.supportY = best;
    e.supportHit = winner ? { name: winner.name, typeName: winner.typeName, expressID: winner.expressID } : null;
    return best;
  }

  /**
   * The wall the walk may not cross (card t_565b83bf): two horizontal rays from
   * the walker's own point — knee and waist height over its feet — along the
   * direction it is about to travel, which is the card's *"forward probe …
   * knee/waist height"*. The pair stands in for the body's column: a parapet
   * under a window stops it (the knee ray passes under the sill, the waist ray
   * meets the wall), while a doorway is a gap both rays run through.
   *
   * What it is cast AGAINST is the envelope alone (`e.wallMeshes`: the walls and
   * the glass in them) — 36 meshes, ~1.1 k triangles on this model — so the cast
   * is cheap enough for every walking frame, and it is the only reason a walker
   * cannot leave the site through a facade. A hidden mesh blocks nothing and a
   * clipped-away hit is not a wall, the same rule the floor probe and
   * `raycastFromPointer` follow (rule 22: a cull and the paint must agree).
   *
   * Returns the outward normal of the nearest hit, which is what the slide
   * needs; the caller decides "blocked" from the distances with
   * `wallBlocksStep` (the step plus the body's own radius).
   */
  function probeWallAhead(
    origin: NavPoint,
    feetY: number,
    dir: NavPoint,
    travel: number,
  ): { blocked: boolean; distance: number | null; normal: NavPoint; name: string | null } {
    const s = state.value;
    const clear = { blocked: false, distance: null as number | null, normal: { x: 0, y: 0, z: 0 }, name: null };
    if (!s || e.wallMeshes.length === 0) return clear;
    const flat = normalizeDirection({ x: dir.x, y: 0, z: dir.z });
    if (flat.x === 0 && flat.z === 0) return clear;

    const far = travel + NAV_WALK_RADIUS_M;
    const distances: number[] = [];
    let nearest: { distance: number; normal: NavPoint; name: string | null } | null = null;
    for (const height of [NAV_WALL_KNEE_M, NAV_WALL_WAIST_M]) {
      s.raycaster.set(new THREE.Vector3(origin.x, feetY + height, origin.z), new THREE.Vector3(flat.x, 0, flat.z));
      s.raycaster.far = far;
      const hits = s.raycaster.intersectObjects(e.wallMeshes, false);
      s.raycaster.far = Number.POSITIVE_INFINITY;
      // The band is not consulted (owner, 2026-10-18: the cut wall he walked into
      // was the one whose upper part the band had removed) — a wall blocks the
      // body wherever the paint happens to be cut.
      const hit = hits.find((candidate) => candidate.object.visible !== false);
      if (!hit) continue;
      distances.push(hit.distance);
      if (nearest && hit.distance >= nearest.distance) continue;
      // The face normal in world space, turned to face the walker: a ray that
      // met the wall's far side would otherwise slide the body INTO it.
      const face = hit.face ? hit.face.normal.clone().transformDirection(hit.object.matrixWorld) : new THREE.Vector3();
      const outward = horizontalNormal({ x: face.x, y: 0, z: face.z });
      const flipped = outward.x * flat.x + outward.z * flat.z > 0 ? { x: -outward.x, y: 0, z: -outward.z } : outward;
      nearest = {
        distance: hit.distance,
        normal: flipped,
        name: String(hit.object.userData.typeName ?? hit.object.name ?? '') || null,
      };
    }
    if (!nearest) return clear;
    e.wallHit = { name: nearest.name, distance: nearest.distance, at: Date.now() };
    return {
      blocked: wallBlocksStep(distances, travel),
      distance: nearest.distance,
      normal: nearest.normal,
      name: nearest.name,
    };
  }

  /**
   * The band follows the walker's feet (owner, 2026-10-18: *"you can fall into a
   * cut wall.. when the model is vertiallly cut.. you fall into an cant get out?"*).
   *
   * Two rules, and this is the second: the COLLISION stops consulting the band
   * (`probeWalkSupport` and `probeWallAhead` above — a cut wall still blocks, a cut
   * floor still carries), and the BAND is never allowed to take the ground out from
   * under the body. The rule itself is pure (`utils/bimNav.walkCutBand`): the band
   * is widened — never shrunk — until it contains the feet, and the walk records
   * that it did (`e.walkCut.lifted`) so the layout can hand the operator's own band
   * back on the way out.
   */
  function guardWalkCut(feetY: number): void {
    if (!clipEnabled.value) {
      e.walkCut.lifted = false;
      return;
    }
    if (!isWalkerMode(navMode.value)) return;
    // The DRONE view is the band's own author: its cut is the view (the body from
    // above, the roof taken off), and the camera is above the body there.
    if (isDroneView(navMode.value, simView.value)) return;
    // …and while he WALKS, the band must hold the whole column he collides with:
    // from just below his feet to just above his eyes, so the wall that stops him is
    // the wall he sees. (The walk itself turns the cut OFF entirely — owner,
    // 2026-10-19: *"in gravity mode we should not cut the model"* — so this only
    // matters if a simulation shares the band.)
    const band = walkCutBand(
      { bottom: clipBottomY.value, top: clipHeightY.value },
      feetY,
      NAV_EYE_HEIGHT_M + WALK_CUT_HEAD_MARGIN_M,
    );
    if (band.bottom === clipBottomY.value && band.top === clipHeightY.value) return;
    setClipRange(band.top, band.bottom);
    e.walkCut.lifted = true;
  }

  /**
   * The walk is over. The band itself is the LAYOUT's to restore — its sliders are
   * the operator's own intent, and it re-applies them when the mode leaves the walk
   * (`cutRangeAt(localClipY, localClipBottomY)`), so a band he moved mid-walk is
   * what comes back rather than the one the walk first borrowed.
   */
  function releaseWalkCut(): void {
    e.walkCut.lifted = false;
  }

  /**
   * Is the body INSIDE a wall solid? One horizontal ray through the wall set,
   * counting crossings (even–odd) — the wall boxes are closed solids, and a parity
   * test has no radius knob to get wrong. Read at waist height, where a body's own
   * column is. Cheap because it only runs on the rescue's own throttle (200 ms).
   */
  function walkerInsideWall(point: { x: number; y: number; z: number }, feetY: number): boolean {
    const s = state.value;
    if (!s || e.wallMeshes.length === 0) return false;
    s.raycaster.set(new THREE.Vector3(point.x, feetY + NAV_WALL_WAIST_M, point.z), new THREE.Vector3(1, 0, 0));
    s.raycaster.far = Number.POSITIVE_INFINITY;
    let crossings = 0;
    let last = Number.NaN;
    for (const hit of s.raycaster.intersectObjects(e.wallMeshes, false)) {
      if (hit.object.visible === false) continue;
      // One face, two triangles: a shared edge must count once.
      if (Math.abs(hit.distance - last) < 1e-4) continue;
      last = hit.distance;
      crossings += 1;
    }
    return crossings % 2 === 1;
  }

  /**
   * The walk's own way out (owner, 2026-10-18: *"… you fall into an cant get out?"*).
   * A body inside a wall has no walkable answer — every direction is refused from in
   * there — so it is put back at `e.lastFree`, the last pose a step was ACCEPTED at.
   * Never a teleport into somewhere new: the same place the body came from, with the
   * velocity dropped so the keyboard's push does not re-enter it.
   */
  function rescueWalker(): void {
    const s = state.value;
    const free = e.lastFree;
    if (!s || !free) return;
    e.feetY = free.feetY;
    e.velocity = { x: 0, y: 0, z: 0 };
    e.supportProbe = null;
    e.supportY = null;
    if (isThirdPersonMode(navMode.value)) {
      e.driven.x = free.x;
      e.driven.z = free.z;
      e.driven.y = free.feetY;
      e.driven.moving = false;
      e.driven.running = false;
    } else {
      s.camera.position.set(free.x, walkCameraY(free.feetY), free.z);
      applyLookRotation();
    }
    e.rescues += 1;
    e.insideWall = false;
    invalidate();
  }

  /**
   * The keyboard's own movement input, one -1/0/1 triple plus the boost. The
   * SCHEME lives in `utils/bimNav.ts` (cards t_e86ce88e + t_01ebef40 +
   * t_493c5f58) so it is unit-tested: WASD *and the arrow keys* move, and in
   * both walking modes the left/right pair STEERS (Up/Down walk, A/D turn — the
   * car's own mapping), Shift runs, Space/J (SIMULATION) hops.
   *
   * SIMULATION's DRONE view takes the four LETTERS for its own camera (owner,
   * 2026-10-13): the arrows are read here without them, so the drone flies around
   * a body that keeps walking under it.
   */
  function navInputFromMode(): NavMoveInput {
    const pressed = isDroneView(navMode.value, simView.value) ? withoutDroneLetters(e.pressedKeys) : e.pressedKeys;
    return navInputForMode(navMode.value, pressed);
  }

  /**
   * One frame of first-person navigation: movement damped toward the keyboard's
   * target velocity, and the horizontal move resolved against the model's own
   * walking surfaces. The LOOK is the pointer's alone (card t_e86ce88e: the keys
   * move the character, they never turn the head — except the STEERING keys of
   * card t_01ebef40, which turn the character's view itself on purpose: Up/Down
   * walk along the facing, Left/Right turn it).
   *
   * SIMULATION drives the DRIVEN avatar's own feet (`e.driven`), not the camera, and
   * its camera follows that body (card t_a41a75f6) — above it in the drone view,
   * or from inside its own eyes in the POV view (card t_01ebef40). Everything
   * else — the speed, the step ceiling, the blocked-step count — is gravity's
   * own. Since card t_493c5f58 the two modes read the SAME key scheme (the car
   * keys) and, in SIMULATION's POV, the drawn body's facing is the look itself.
   *
   * The DRONE view is the exception (owner, 2026-10-13: *"we cloud lik wasd keys
   * to camera control.. in drone mode"*): its four LETTERS steer the CAMERA — A/D
   * orbit the drone around the body, W/S raise and lower it on the same constant
   * distance — while the ARROWS keep the character, so the drone flies around a
   * body that walks under it, which is the view's whole purpose. One `C` away,
   * POV hands every key back to the character.
   */
  function updateFirstPersonNav(dt: number): boolean {
    const s = state.value;
    if (!s || !isFirstPersonMode(navMode.value)) return false;
    const mode = navMode.value;
    const droneView = isDroneView(mode, simView.value);
    const thirdPerson = isThirdPersonMode(mode);
    const eyes = isPovCamera(mode, simView.value);
    let changed = false;

    // ── The drone's own frame: its letters are the CAMERA's ──────────────────
    if (droneView) {
      const steer = droneSteerFromPressed(e.pressedKeys);
      if (steer.orbit !== 0 || steer.lift !== 0) {
        // W/S are the camera's own ANGLE (the look's pitch, the same number the
        // drag's vertical axis writes)…
        e.look = applyDroneLift(e.look, steer, dt);
        // …and A/D are the ORBIT OFFSET around the body, never the camera's yaw:
        // the yaw is the BODY's heading now (the follow rule below), so the
        // letters swing the drone aside and the body keeps its own facing.
        e.droneOrbitOffset = applyDroneOrbit(e.droneOrbitOffset, steer, dt);
        changed = true;
      }
      // …and its ZOOM KEYS are the camera's DISTANCE (owner, 2026-10-15:
      // *"simulation drone view +, -, shuld change the disctane of the camera
      // (zoom).."*): `+` closer, `-` farther, on the wheel's own number and band —
      // one more hand on the keys can fly the whole drone.
      const zoom = droneZoomFromPressed(e.pressedKeys);
      if (zoom !== 0) {
        e.droneDistance = zoomDroneDistanceByKeys(e.droneDistance, zoom, dt);
        changed = true;
      }
    }

    // The scheme is read FOR THE MODE (cards t_01ebef40 + t_493c5f58): both
    // walking modes turn the left/right keys into a steering axis — the car
    // scheme the owner asked for everywhere — and the only translation key is
    // forward/back, along the facing.
    const input = navInputFromMode();
    // The WHEEL's own walk (see `onWheel`): it adds to the SAME forward axis the
    // letters write — one way to move, two ways to ask for it — and bleeds off, so
    // the walking stops when the wheel does. Written before `moving` is read, or a
    // wheel notch would ask for a move the frame would not take.
    if (e.wheelWalkForward !== 0) {
      input.forward = Math.min(1, Math.max(-1, input.forward + e.wheelWalkForward));
      e.wheelWalkForward = approach(e.wheelWalkForward, 0, NAV_WHEEL_WALK_DECAY_PER_S, dt);
      changed = true;
    }
    const moving = input.forward !== 0 || input.strafe !== 0;

    // ── The FOLLOW rule (owner, 2026-10-14) ──────────────────────────────────
    // *"make drone view camera stay behind the person"*: while the character
    // WALKS the manual orbit eases back to directly behind them; standing still
    // it holds where the user put it (an idle camera that recentred itself would
    // undo the A/D keys the moment the hand left them).
    if (droneView) {
      const recentred = easeDroneOrbitOffset(e.droneOrbitOffset, moving, dt);
      if (recentred !== e.droneOrbitOffset) {
        e.droneOrbitOffset = recentred;
        changed = true;
      }
    }
    // The walker's own point: the camera in first person, the avatar in SIMULATION.
    if (thirdPerson && e.driven.x === 0 && e.driven.z === 0 && e.driven.y === 0) {
      syncDrivenToCamera();
    }
    const point = thirdPerson ? e.driven : null;

    // The STEERING first (card t_01ebef40), so everything below runs against the
    // new facing. Steering alone moves nothing: the movement branch is entered
    // by forward/back, and a turn with no walk key leaves the feet exactly
    // where they were — the owner's *"left right should not move object
    // sideways, but turn the ... character's view"*.
    //
    // In the DRONE view the arrows turn the CHARACTER instead (owner: *"[use] WASD
    // keys to camera control in drone mode"*): the camera's angle there is the
    // letters' and the drag's own, so a turn under the drone must not swing the
    // drone with it.
    if (input.turn !== 0) {
      if (droneView && point) {
        point.yawDeg = turnYawDeg(point.yawDeg, input.turn, dt);
      } else {
        e.look = applyTurn(e.look, input.turn, dt);
      }
      changed = true;
    }

    // In the drone view the look's own YAW IS the body's heading — the camera is
    // what hangs off it (offset by the manual orbit) — so the two are kept in step
    // here. That is what makes the follow rule harmless to everything else that
    // reads the look: the character's facing is the same number POV walks with,
    // a stored preset carries and the crumb shows.
    if (droneView && point) {
      const heading = bodyHeadingYaw(point.yawDeg);
      const drifted = Math.abs(Math.atan2(Math.sin(heading - e.look.yaw), Math.cos(heading - e.look.yaw)));
      if (drifted > 1e-6) {
        e.look = { yaw: heading, pitch: e.look.pitch };
        changed = true;
      }
    }

    // …and the drawn body carries that facing, at the avatar layer's own turn
    // rate (card t_493c5f58): in the car scheme the body's yaw IS the look, so
    // the steering keys and a walk turn the model with one rule. SIMULATION's POV only:
    // GRAVITY has no drawn body (its camera is the character), and in the drone
    // view the body's own facing is the arrows' business, not the camera's.
    if (point && !droneView) {
      const facingDeg = (e.look.yaw * 180) / Math.PI;
      const nextYaw = approachYawDeg(point.yawDeg, facingDeg, BIM_AVATAR_TURN_RATE_DEG * dt);
      if (nextYaw !== point.yawDeg) {
        point.yawDeg = nextYaw;
        changed = true;
      }
    }

    if (!moving && isStill(e.velocity)) {
      e.velocity = { x: 0, y: 0, z: 0 };
      if (thirdPerson) {
        e.driven.moving = false;
        e.driven.running = false;
      }
    } else {
      // The direction: along the look — the walker's own facing — or, under the
      // drone, along the BODY's own facing, because the look out there is the
      // camera's and the character's is the arrows'.
      const direction = normalizeDirection(
        droneView && point
          ? walkDirection({ yaw: (point.yawDeg * Math.PI) / 180, pitch: 0 }, input)
          : walkDirection(e.look, input),
      );
      const speed = navSpeed(input.boost);
      // The boost is also the CLIP's own switch (owner, 2026-10-13: *"we need
      // running for person"*): the layer plays the run clip off this flag, so the
      // body runs when the walker does rather than at its own idea of fast.
      if (thirdPerson) e.driven.running = input.boost;
      e.velocity = approachPoint(
        e.velocity,
        { x: direction.x * speed, y: direction.y * speed, z: direction.z * speed },
        NAV_VELOCITY_SMOOTHING,
        dt,
      );

      // Both first-person modes walk: the floor probe resolves the feet and no
      // mode flies any more (FLOAT's free-flight branch retired, card t_493c5f58).
      {
        const origin = point ?? s.camera.position;
        const feetY = e.feetY ?? origin.y - NAV_EYE_HEIGHT_M;
        const stepX = e.velocity.x * dt;
        const stepZ = e.velocity.z * dt;
        const travel = Math.hypot(stepX, stepZ);
        // ── The WALLS first (card t_565b83bf) ────────────────────────────────
        // The owner: *"make wall block the movement in game mode.. so you can't
        // fall out.. we would allow the walking throught doors.."*. The probe
        // asks the envelope about THIS frame's step; when it refuses, the step
        // slides along the surface instead (the card's "forward probe with a
        // slide") and only a step that cannot slide at all is given up. Doors
        // need no case of their own: they are gaps in the wall solids, so both
        // probes pass through them.
        let moveX = stepX;
        let moveZ = stepZ;
        let wallRefused = false;
        if (travel > 1e-4) {
          const heading = normalizeDirection({ x: stepX, y: 0, z: stepZ });
          const wall = probeWallAhead(origin, feetY, heading, travel);
          if (wall.blocked) {
            const slid = slideAlongWall(heading, wall.normal);
            if (slid.x === 0 && slid.z === 0) {
              wallRefused = true;
            } else {
              const slideWall = probeWallAhead(origin, feetY, slid, travel);
              if (slideWall.blocked) wallRefused = true;
              else {
                moveX = slid.x * travel;
                moveZ = slid.z * travel;
              }
            }
          }
        }
        if (wallRefused) {
          // A wall, not a step: the walker stays where it is and the keyboard's
          // push is dropped (the next frame starts from standstill, so a
          // diagonal along the wall still slides once the hand turns).
          e.wallsBlocked += 1;
          e.velocity.x = 0;
          e.velocity.z = 0;
        } else {
          const nextX = origin.x + moveX;
          const nextZ = origin.z + moveZ;
          // The FOOTPRINT, not a single ray (owner, 2026-10-20): a millimetre crack
          // between two floor plates must not swallow the body.
          const supportY = probeWalkSupportArea(nextX, nextZ, feetY);
          const resolved = resolveWalkFeetY({ supportY, levels: e.walkLevels, feetY });
          if (resolved.blocked) {
            // The surface ahead is more than one step up: the step is refused and
            // the walker stays put (its own floor, not a climb).
            e.walkBlocked += 1;
            e.velocity.x = 0;
            e.velocity.z = 0;
            // …unless there was NO surface at all (owner, 2026-10-18: *"you fall into
            // an cant get out?"*): a body standing over nothing can never walk out,
            // because every step it takes is refused the same way. That is the
            // rescue's own case, not a ledge.
            if (supportY === null) rescueWalker();
          } else {
            e.feetY = resolved.feetY;
            e.lastFree = { x: nextX, z: nextZ, feetY: resolved.feetY };
            if (point) {
              // The body travels; its FACING is the look's own (card
              // t_493c5f58): in the car scheme forward/back walk along the
              // facing the keys and the drag set, so nothing here has to turn
              // the model — and walking backwards must not spin it round.
              point.x = nextX;
              point.z = nextZ;
              point.moving = true;
            } else {
              s.camera.position.x = nextX;
              s.camera.position.z = nextZ;
            }
            changed = true;
          }
        }
      }
    }

    // The walker keeps chasing the floor it stands on — including after the keys
    // are released, so a landing or a step finishes smoothly instead of freezing
    // halfway up a riser. Both first-person modes walk (card t_493c5f58).
    {
      const feetY = e.feetY ?? (point ?? s.camera.position).y - NAV_EYE_HEIGHT_M;
      e.feetY = feetY;
      // The hop (card t_565b83bf): the FAB's own jump key, as an arc over the
      // floor the walker stands on. The KEYDOWN starts it (`e.jump`), this is
      // the integration, and the floor chase is what it lands on — the body's
      // own Y is set straight from the arc while it is in the air, and the
      // smoothing takes it back the moment the feet touch.
      if (e.jump) {
        const next = stepJump(e.jump, dt);
        e.jump = next.active ? next : null;
        if (next.active) changed = true;
      }
      const lift = e.jump ? e.jump.offset : 0;
      if (point) {
        // The avatar's own feet ARE the resolved floor — the camera follows the
        // body, never the other way round. (In the air, the floor plus the arc.)
        const targetY = feetY + lift;
        const nextY = e.jump ? targetY : approach(e.driven.y, targetY, NAV_EYE_Y_SMOOTHING, dt);
        if (Math.abs(nextY - e.driven.y) > 1e-4) changed = true;
        e.driven.y = nextY;
        // Where the camera sits for that body is the VIEW's decision (card
        // t_01ebef40): behind it, or at its own eyes.
        const placed = placeCameraForView(e.simView.value);
        if (placed) changed = true;
      } else {
        const targetY = walkCameraY(feetY);
        const nextY = approach(s.camera.position.y, targetY, NAV_EYE_Y_SMOOTHING, dt);
        if (Math.abs(nextY - s.camera.position.y) > 1e-4) {
          s.camera.position.y = nextY;
          changed = true;
        }
      }
    }

    // ── The rescue, and the band that follows the feet (owner, 2026-10-18) ─────
    {
      const body = point ?? s.camera.position;
      const feetY = e.feetY ?? body.y - NAV_EYE_HEIGHT_M;
      const now = Date.now();
      if (now - e.rescueCheckedAt >= 200) {
        e.rescueCheckedAt = now;
        e.insideWall = walkerInsideWall(body, feetY);
        // A body inside a wall cannot walk out of it: every direction is refused
        // from in there. Put it back where its last accepted step left it.
        if (e.insideWall && e.lastFree && Math.hypot(e.lastFree.x - body.x, e.lastFree.z - body.z) > 0.05) {
          rescueWalker();
          changed = true;
        }
      }
      guardWalkCut(e.feetY ?? feetY);
    }

    if (changed) {
      // The camera takes the look angles itself whenever it IS the character
      // (GRAVITY, and SIMULATION's POV view); the drone points it at the body instead,
      // in its own placement.
      if (eyes) applyLookRotation();
      queueCameraSerialUpdate();
      updateCameraAzimuthRefs(s.camera);
    }
    return changed;
  }

  /** Put the driven avatar where the camera stands, when SIMULATION mode starts. */
  function syncDrivenToCamera(): void {
    const s = state.value;
    if (!s) return;
    const feetY = e.feetY ?? s.camera.position.y - NAV_EYE_HEIGHT_M;
    e.driven = {
      x: s.camera.position.x,
      y: feetY,
      z: s.camera.position.z,
      // The body adopts the walker's own facing (the look is where the player
      // faces — the same rule POV walks with, written here so the DRONE opens
      // behind a body that faces the way the user was looking instead of
      // inheriting whatever bearing a previous session left on the avatar).
      yawDeg: yawDegFromLook(e.look.yaw),
      moving: false,
      running: false,
    };
    e.feetY = feetY;
  }

  /**
   * Seat the driven walker at a known pose (card t_a41a75f6): the person's own
   * spawn — where they asked to stand on this dashboard — or a pose a rig names.
   * The camera is NOT moved here: the next frame's view placement hangs it over
   * the body (the drone), or stands it at the eyes the spawn implies (POV). The look
   * angles take the pose's own facing, so the camera watches the way the avatar
   * is turned (and the keys then walk it forward from there).
   */
  function placeDrivenAt(pose: { x: number; y: number; z: number; yawDeg: number }): void {
    const s = state.value;
    if (!s) return;
    e.driven = { x: pose.x, y: pose.y, z: pose.z, yawDeg: pose.yawDeg, moving: false, running: false };
    e.feetY = pose.y;
    e.supportY = null;
    e.supportProbe = null;
    e.velocity = { x: 0, y: 0, z: 0 };
    e.look = { yaw: (pose.yawDeg * Math.PI) / 180, pitch: e.look.pitch };
    applyLookRotation();
    // The camera is seated the way the ACTIVE VIEW seats it (card t_01ebef40):
    // over the body, or at the eyes the spawn's facing implies.
    const pose0 = placeCameraForView(simView.value);
    if (pose0) queueCameraSerialUpdate();
    updateCameraAzimuthRefs(s.camera);
    invalidate();
  }

  /**
   * The spans the drone's ceiling cap reads. The level DOCUMENT wins when it has
   * any (it is the building's own answer); a model with no levels falls back to its
   * measured floor lines (`walkLevels`), grouped so a floor finish 2 cm over its
   * own slab is one storey and not two. The top line has no floor over it, and its
   * ceiling reads as `Infinity` — "nothing above" — which `storeyCeilingAt` turns
   * into "no cut".
   */
  function cutStoreySpans(): { floorY: number; ceilingY: number }[] {
    if (e.storeySpans.length > 0) return e.storeySpans;
    const floors: number[] = [];
    for (const level of [...e.walkLevels].sort((a, b) => a - b)) {
      if (!Number.isFinite(level)) continue;
      if (floors.length === 0 || level - floors[floors.length - 1] > 0.2) floors.push(level);
    }
    return floors.map((floorY, index) => ({ floorY, ceilingY: floors[index + 1] ?? Number.POSITIVE_INFINITY }));
  }

  /**
   * The ceiling of the storey a body standing at `feetY` is IN — the next floor
   * level over its head (owner, 2026-10-19: *"cap to the floor levels"*). A body
   * outside every storey (the garden, the roof, a model with no levels) has no
   * ceiling over it, and `null` says so.
   */
  function storeyCeilingAt(feetY: number): number | null {
    if (!Number.isFinite(feetY)) return null;
    let standing: { floorY: number; ceilingY: number } | null = null;
    for (const span of cutStoreySpans()) {
      if (!Number.isFinite(span.floorY) || !Number.isFinite(span.ceilingY)) continue;
      if (span.floorY > feetY + 1e-2) continue;
      if (!standing || span.floorY > standing.floorY) standing = span;
    }
    if (!standing || !Number.isFinite(standing.ceilingY)) return null;
    if (feetY >= standing.ceilingY - 1e-2) return null;
    return standing.ceilingY;
  }

  /**
   * Park the operator's own band for the length of a simulation (owner, 2026-10-20:
   * the MODE owns the plane while it runs). Called by whichever view is seated
   * first; the band is handed back untouched by `releaseSimCut`.
   */
  function borrowSimCut(): void {
    if (e.simCut.saved !== null) return;
    e.simCut.saved = { enabled: clipEnabled.value, height: clipHeightY.value, bottom: clipBottomY.value };
  }

  /**
   * The drone's own vertical rule (owner, 2026-10-13; the CEILING cap 2026-10-19:
   * *"we need to cap the ceiling … remove cut following the user head height.. cap
   * to the floor levels"*). The section cut this page already has is driven to the
   * ceiling of the storey the body stands IN and followed as they walk, so looking
   * down shows the room they are in — the storey above and the roof come off, and
   * the walls around them stay whole.
   *
   * Outside every storey there is no ceiling to take off, and then nothing is cut:
   * the house stays whole, and the operator's own band is still parked for the way
   * out (`releaseSimCut`). Returns the ceiling the cut sits at, `null` when there is
   * none — the camera's own clearance uses it.
   */
  function driveDroneCut(feet: { x: number; y: number; z: number }): number | null {
    // The drone borrows the plane itself, so the WALK's flag is dropped here
    // (2026-10-18): from now on this view is the band's author.
    releaseWalkCut();
    borrowSimCut();
    const ceiling = storeyCeilingAt(feet.y);
    if (ceiling === null) {
      e.simCut.at = Number.NaN;
      if (clipEnabled.value) disableClip();
      return null;
    }
    if (Math.abs(ceiling - e.simCut.at) < 0.005 && clipEnabled.value) return ceiling;
    e.simCut.at = ceiling;
    setClipHeight(ceiling);
    return ceiling;
  }

  /** Leaving the simulation hands the operator's own section band back. */
  function releaseSimCut(): void {
    const saved = e.simCut.saved;
    if (!saved) return;
    e.simCut.saved = null;
    e.simCut.at = Number.NaN;
    if (saved.enabled) setClipRange(saved.height, saved.bottom);
    else disableClip();
  }

  /**
   * Hang the camera behind and over the driven body — SIMULATION's DRONE view
   * (owner, 2026-10-13; the follow rule 2026-10-14): a constant distance from a
   * point 50 cm above the head, standing BEHIND the body's own heading (swung by
   * the manual orbit offset) and always looking down, so the body and the floor
   * around it are what the view is for. The wheel sets the distance, the drag
   * turns the character and lifts this camera's angle, and no wall probe runs: a
   * drone that pulled itself in for a wall would stop being at a constant
   * distance, which is the one thing this view promises.
   *
   * Returns true when the camera moved.
   */
  function placeDroneCamera(): boolean {
    const s = state.value;
    if (!s) return false;
    const feet = { x: e.driven.x, y: e.driven.y, z: e.driven.z };
    const cutY = driveDroneCut(feet);
    const look = droneCameraLook(e.driven.yawDeg, e.droneOrbitOffset, e.look.pitch);
    const pose = droneCameraPose(feet, look, e.droneDistance, cutY);
    const moved =
      Math.abs(s.camera.position.x - pose.position.x) > 1e-4 ||
      Math.abs(s.camera.position.y - pose.position.y) > 1e-4 ||
      Math.abs(s.camera.position.z - pose.position.z) > 1e-4;
    s.camera.position.set(pose.position.x, pose.position.y, pose.position.z);
    s.camera.lookAt(pose.target.x, pose.target.y, pose.target.z);
    return moved || e.driven.moving;
  }

  /**
   * Put the camera at the driven body's own eyes (card t_01ebef40 — SIMULATION's POV
   * view): the pose `povCameraPose` derives, plus the look rotation, which is the
   * same camera GRAVITY walks with. No wall pull-in runs here: there is nothing
   * between a head and its own eyes, and the first-person camera has never been
   * clipped against the model.
   *
   * Returns true when the camera moved.
   */
  function placePovCamera(): boolean {
    const s = state.value;
    if (!s) return false;
    // POV is the WALK's own view (owner, 2026-10-20: *"in simulate pov view there
    // should be no vertical cuts.. same as in walking"*): from the body's eyes the
    // house is whole — the drone may own the plane, but never through the head. The
    // operator's band stays parked (`borrowSimCut`) for the way out.
    borrowSimCut();
    if (clipEnabled.value) disableClip();
    const pose = povCameraPose({ x: e.driven.x, y: e.driven.y, z: e.driven.z }, e.look, e.drivenEyeHeight);
    const moved =
      Math.abs(s.camera.position.x - pose.position.x) > 1e-4 ||
      Math.abs(s.camera.position.y - pose.position.y) > 1e-4 ||
      Math.abs(s.camera.position.z - pose.position.z) > 1e-4;
    s.camera.position.set(pose.position.x, pose.position.y, pose.position.z);
    applyLookRotation();
    return moved || e.driven.moving;
  }

  function invalidate() {
    e.needsRender = true;
    // A scene suspended by an unmount is only resumed by `init()` — and a re-visit
    // can reach a frame BEFORE that runs, or miss it entirely when the container is
    // not built yet. Then every `invalidate()` was dropped on the floor: the scene
    // existed, the camera kept its pose, and the page drew nothing at all (owner,
    // 2026-10-24: *"i go some other page, then come back … nothing is working"*).
    // A canvas that is in the document is the page saying it is back.
    if (e.suspended && state.value?.renderer.domElement.isConnected) {
      resume();
      return;
    }
    if (!e.suspended && state.value) {
      startRenderLoop();
    }
  }

  function startRenderLoop() {
    if (e.suspended || !state.value || animationId.value) return;
    e.lastFrameTime = performance.now();
    animationId.value = requestAnimationFrame(renderFrame);
  }

  function renderFrame(now: number) {
    const s = state.value;
    if (!s || e.suspended) {
      animationId.value = 0;
      return;
    }

    const dt = Math.min((now - e.lastFrameTime) / 1000, 0.1);
    e.lastFrameTime = now;
    if (e.traceOn) {
      const round = (n: number) => Math.round(n * 100) / 100;
      e.trace.push({
        t: Math.round(now),
        cam: [round(s.camera.position.x), round(s.camera.position.y), round(s.camera.position.z)],
        target: s.controls ? [round(s.controls.target.x), round(s.controls.target.y), round(s.controls.target.z)] : [],
        anim: e.flyToAnimationId.value !== 0,
        mode: String(e.navMode?.value ?? ''),
      });
      if (e.trace.length > 400) e.trace.splice(0, e.trace.length - 400);
    }
    // The frame time the LIGHTING budget reads (`adaptiveLightBudget`): an EMA of
    // the frames that actually rendered, not of the idle ticks of an on-demand
    // loop (which are cheap and would flatter the number).
    if (e.needsRender) {
      // Clamped: the first frame after the loop re-arms can carry a rAF timestamp
      // EARLIER than the `performance.now()` that scheduled it, i.e. a negative
      // delta (measured: a frame average of −136 ms). A negative sample is not a
      // fast frame, it is no measurement at all.
      const frameMs = Math.max(0, dt * 1000);
      // Plain field on the entry, read through a closure: writing a NUMBER into the
      // reactive state on every rendered frame is what made orbiting stutter (it
      // wakes every watcher of the scene state, every frame).
      //
      // A STALL does not vote (owner, 2026-10-23: *"devices are loading and the
      // lights flicker and the model gets darkened"*): the model build, a shader
      // compile and a GC pause each land here as ONE multi-second "frame", and
      // feeding those to the EMA is what collapsed the fixture-light budget to
      // eight lamps while the page loaded — then restored it when the page
      // settled, i.e. the lights went off and came back.
      if (!isRenderStall(frameMs)) {
        e.frameMsAvg = e.frameMsAvg ? e.frameMsAvg * 0.9 + frameMs * 0.1 : frameMs;
      }
    }

    // ORBIT and the first-person modes are two regimes, not two settings: the
    // turntable updates and reads the keyboard the way it always has, while a
    // first-person mode drives the camera itself (and must NOT let
    // OrbitControls update — its own `lookAt` would square the head back up).
    const orbiting = navMode.value === 'orbit';
    const keyboardChanged = orbiting ? updateKeyboardNav(dt) : false;
    const navChanged = updateFirstPersonNav(dt);
    // OrbitControls damps per CALL, not per second: its tail is ~30 frames whatever the frame
    // rate, so at 20 fps the house kept gliding for 1.5 s and the widget layer stayed hidden —
    // *"why is the orbit move taking so long to settle down"* (owner, 2026-10-23). Re-derive the
    // factor from the frame time so the glide lasts the same ~0.5 s at any frame rate.
    if (orbiting) {
      s.controls.dampingFactor = 1 - Math.pow(1 - DEFAULT_DAMPING_FACTOR, Math.min(dt, 0.1) * 60);
    }
    const controlsChanged = orbiting ? s.controls.update() : false;
    // An orbit whose polar angle clamps at π puts the camera straight UNDER the
    // target and the operator is looking at the grass from below (owner,
    // 2026-10-24: *"it starts spinning like crazy … now i am seeing the grass from
    // below"*). A camera below the ground is never a view of this house, so it is
    // lifted to the floor with the orbit target untouched.
    keepCameraAboveGround();
    keepOrbitRadius();

    // …and what the CAMERA itself did (card t_72106dc1): read after the updates
    // above, so the frame a walk or an orbit moves is the frame the widget
    // overlay layer goes away, and the settle timer is fed by the damping tail.
    detectCameraMotion();

    const shouldRender = e.needsRender || keyboardChanged || controlsChanged || navChanged;

    if (shouldRender) {
      e.needsRender = false;
      renderScene();
    }

    const shouldContinue =
      !e.suspended &&
      (e.needsRender ||
        keyboardChanged ||
        controlsChanged ||
        navChanged ||
        e.pressedKeys.size > 0 ||
        e.flyToAnimationId.value !== 0 ||
        !isStill(e.velocity));

    if (shouldContinue) {
      animationId.value = requestAnimationFrame(renderFrame);
      return;
    }

    animationId.value = 0;
  }

  function attachKeyboardListeners() {
    if (!e.keydownHandler) {
      e.keydownHandler = (ev: KeyboardEvent) => {
        const tag = (ev.target as HTMLElement | null)?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
        const k = ev.key.toLowerCase();
        // Esc leaves a first-person mode (the pill's Exit). While the pointer is
        // locked the browser eats the first Esc to release the lock, so the
        // ordering is: Esc releases the pointer, Esc again goes back to orbit.
        if (k === 'escape' && isFirstPersonMode(navMode.value) && document.pointerLockElement === null) {
          exitNavMode();
          return;
        }
        // C toggles SIMULATION's two camera views (card t_01ebef40; owner 2026-10-13:
        // *"make key "C" togle the two.."*): drone ⇄ pov. One press, one switch —
        // `ev.repeat` is what keeps a held key from flipping it back and forth.
        if (k === NAV_VIEW_TOGGLE_KEY && !ev.repeat && isThirdPersonMode(navMode.value)) {
          ev.preventDefault();
          toggleSimView();
          return;
        }
        // R puts the driven body back on its own start (owner, 2026-10-20: *"we
        // need a button in simulate nav bar to reset the avatar position to
        // original position.. also assign key "R" to this"*). The spawn belongs to
        // the layout, so the key only asks for it. SIMULATION mode only — and a
        // held key does not repeat the reset.
        if (k === 'r' && !ev.repeat && isThirdPersonMode(navMode.value)) {
          ev.preventDefault();
          e.onDrivenReset?.();
          return;
        }
        // The FAB's own jump key (card t_565b83bf): Space — the FAB's keymap's
        // last row — with J as the twin the card names. SIMULATION mode only: the hop
        // is the driven body's, and a held key does not restart it (`ev.repeat`
        // plus "already in the air" both refuse).
        if (NAV_JUMP_KEYS.includes(k) && isThirdPersonMode(navMode.value)) {
          ev.preventDefault();
          if (!ev.repeat) {
            if (!e.jump || !e.jump.active) {
              e.jump = startJump();
              invalidate();
            }
          }
          return;
        }
        if (ev.shiftKey) e.pressedKeys.add('shift');
        else e.pressedKeys.delete('shift');
        // Ctrl is the FAB's second spelling of "run" (card t_565b83bf), so it is
        // tracked exactly as Shift is — one held modifier, two keycaps.
        if (ev.ctrlKey) e.pressedKeys.add('control');
        else e.pressedKeys.delete('control');
        if (NAV_KEYS.has(k)) {
          e.pressedKeys.add(k);
          ev.preventDefault();
          invalidate();
          return;
        }
        // Space is the jump's key and nothing else now that FLOAT is gone (card
        // t_493c5f58): SIMULATION spends it in its own branch above, and any other
        // first-person mode simply keeps the page from scrolling under the
        // viewer. In ORBIT the browser keeps whatever a space bar does.
        if (k === ' ' && isFirstPersonMode(navMode.value)) ev.preventDefault();
      };
    }

    if (!e.keyupHandler) {
      e.keyupHandler = (ev: KeyboardEvent) => {
        if (!ev.shiftKey) e.pressedKeys.delete('shift');
        if (!ev.ctrlKey) e.pressedKeys.delete('control');
        const k = ev.key.toLowerCase();
        e.pressedKeys.delete(k);
        // `+` and `=` are ONE keycap in two spellings: which of them `ev.key`
        // reports depends on Shift's state at that moment, so a keyup can name the
        // other one than the keydown did — press `=`, add Shift, release, and `=`
        // would stay held forever. The zoom keys are read from the held set every
        // frame (the drone's `+`/`-`), so a key that cannot be released is a camera
        // that cannot be stopped: clearing both spellings is what lets go.
        if (k === '+' || k === '=') {
          e.pressedKeys.delete('+');
          e.pressedKeys.delete('=');
        }
      };
    }

    if (e.keyboardListenersAttached) return;
    document.addEventListener('keydown', e.keydownHandler);
    document.addEventListener('keyup', e.keyupHandler);
    e.keyboardListenersAttached = true;
  }

  function detachKeyboardListeners() {
    if (!e.keyboardListenersAttached) return;
    if (e.keydownHandler) document.removeEventListener('keydown', e.keydownHandler);
    if (e.keyupHandler) document.removeEventListener('keyup', e.keyupHandler);
    e.keyboardListenersAttached = false;
    e.pressedKeys.clear();
  }

  /**
   * Move / rotate the ORBIT camera from the currently pressed keys — the
   * turntable's own keyboard, byte-for-byte the behaviour it has always had.
   * The first-person modes have their own reader (`updateFirstPersonNav`).
   */
  function updateKeyboardNav(dt: number): boolean {
    const s = state.value;
    if (!s || e.pressedKeys.size === 0) return false;

    const { camera, controls } = s;
    let changed = false;

    const shiftMult = e.pressedKeys.has('shift') ? 1.6 : 1.0;
    const ORBIT_SPEED = 2.0 * shiftMult; // radians per second for WASD orbit
    const PAN_SPEED_FACTOR = 2.2 * shiftMult;

    // ── W/S and A/D: the turntable's own tilt and turn ───────────────────
    // W/S lift the camera's own angle; A/D swing the turntable around the model,
    // A counter-clockwise and D clockwise (owner, 2026-10-15: *"Orbit mode: WASD:
    // AD should trun the view leff (counter) and right (clock)."*). The letters
    // were moved onto the azimuth the day after they had been given the arrows'
    // sideways pan (owner, 2026-10-14: *"Arrows: up, down ok, left and right
    // should move sideways"*) — the arrows keep that pan, the letters do not.
    const hasW = e.pressedKeys.has('w');
    const hasS = e.pressedKeys.has('s');
    const hasA = e.pressedKeys.has('a');
    const hasD = e.pressedKeys.has('d');

    if (hasW || hasS || hasA || hasD) {
      // Orbit: move camera position in spherical coordinates around target
      const offset = camera.position.clone().sub(controls.target);
      const spherical = new THREE.Spherical().setFromVector3(offset);

      const dTheta = ((hasA ? 1 : 0) - (hasD ? 1 : 0)) * ORBIT_SPEED * dt;
      const dPhi = ((hasS ? 1 : 0) - (hasW ? 1 : 0)) * ORBIT_SPEED * dt;

      spherical.theta += dTheta;
      spherical.phi = THREE.MathUtils.clamp(spherical.phi + dPhi, 0.04, Math.PI - 0.04);
      offset.setFromSpherical(spherical);
      camera.position.copy(controls.target).add(offset);
      controls.update();
      changed = true;
    }

    // ── Up/Down and Left/Right arrows (and Q/E): pan (translate camera + target)
    const hasUp = e.pressedKeys.has('arrowup');
    const hasDown = e.pressedKeys.has('arrowdown');
    const panLeft = e.pressedKeys.has('arrowleft');
    const panRight = e.pressedKeys.has('arrowright');
    const hasE = e.pressedKeys.has('e');
    const hasQ = e.pressedKeys.has('q');

    if (hasUp || hasDown || panLeft || panRight || hasE || hasQ) {
      const dist = camera.position.distanceTo(controls.target);
      const speed = Math.max(dist * PAN_SPEED_FACTOR, 0.5) * dt;

      const forward = new THREE.Vector3();
      camera.getWorldDirection(forward);
      forward.y = 0;
      if (forward.lengthSq() > 0.0001) forward.normalize();
      const right = new THREE.Vector3().crossVectors(forward, new THREE.Vector3(0, 1, 0)).normalize();

      const move = new THREE.Vector3();
      if (hasUp) move.add(forward);
      if (hasDown) move.sub(forward);
      if (panRight) move.add(right);
      if (panLeft) move.sub(right);
      if (hasE) move.y += 1;
      if (hasQ) move.y -= 1;

      if (move.lengthSq() > 0) {
        move.normalize().multiplyScalar(speed);
        camera.position.add(move);
        controls.target.add(move);
        controls.update();
        changed = true;
      }
    }

    // ── R/F/+/- : zoom (move camera along line to target) ────────────────
    const hasR = e.pressedKeys.has('r');
    const hasF = e.pressedKeys.has('f');
    const hasPlus = e.pressedKeys.has('+') || e.pressedKeys.has('=');
    const hasMinus = e.pressedKeys.has('-');
    const zoomIn = hasR || hasPlus;
    const zoomOut = hasF || hasMinus;

    if (zoomIn || zoomOut) {
      const ZOOM_SPEED = 2.5 * shiftMult;
      const dist = camera.position.distanceTo(controls.target);
      const step = Math.max(dist * ZOOM_SPEED * dt, 0.05);
      const dir = camera.position.clone().sub(controls.target).normalize();
      if (zoomIn) camera.position.addScaledVector(dir, -step);
      if (zoomOut) camera.position.addScaledVector(dir, step);
      controls.update();
      changed = true;
    }

    return changed;
  }

  function suspend() {
    e.suspended = true;
    // A drag cut short by leaving the page must not be replayed on the way back.
    clearControlsMomentum();
    e.needsRender = true;
    stopRenderLoop();
    stopCameraSerialFrame();
    // The sun clock goes with the canvas: a suspended page is not showing a
    // scene, and a background tab should not be repainting one every minute.
    stopSunClock();
    // …and so do the falling drops, for the same reason: nothing is on screen.
    stopPrecipitationLoop();

    if (e.flyToAnimationId.value) {
      cancelAnimationFrame(e.flyToAnimationId.value);
      e.flyToAnimationId.value = 0;
    }

    clearCameraInteractionTimeout();
    cameraInteracting.value = false;
    // The motion gate goes with the canvas (card t_72106dc1): a suspended page
    // is not moving, and a resumed one measures from where it lands.
    clearCameraSettleTimer();
    cameraMoving.value = false;
    detachKeyboardListeners();
    // The canvas is about to leave the DOM: drop the pointer with it.
    releasePointerLock();
    e.dragLook.active = false;
    e.velocity = { x: 0, y: 0, z: 0 };

    const s = state.value;
    if (!s) return;
    s.renderer.domElement.remove();
    s.labelRenderer.domElement.remove();
  }

  function resume() {
    if (!state.value) return;
    e.suspended = false;
    clearControlsMomentum();
    resetCameraSettlePose();
    attachKeyboardListeners();
    // A sky left raining is raining again the moment the canvas is back.
    startPrecipitationLoop();
    invalidate();
  }

  function setLocation(lat: number) {
    e.latitude.value = lat;
    if (sunEnabled.value) applySunTime(e.timeOfDay.value);
    save();
  }

  function setDate(dateStr: string) {
    e.solarDate.value = dateStr;
    if (sunEnabled.value) applySunTime(e.timeOfDay.value);
    save();
  }

  function setNorthAngle(deg: number) {
    const normalized = normalizeDegrees(deg);
    e.northAngle.value = normalized;
    // Re-apply sun with new orientation
    if (sunEnabled.value) applySunTime(e.timeOfDay.value);
    save();
  }

  /**
   * The ray: a plain line from the sun's position INTO the model's centre.
   *
   * It says where the light comes from and which way it is travelling, which is
   * what makes the shadows readable without hunting for the sun — but it is a
   * GUIDE, so it is drawn as a bare line. It was an `ArrowHelper` with a cone
   * head until the owner asked for the head to go: *"the sun arrow is huge..
   * remove the arrow.. just leave the line"*. The head had grown in the frame as
   * the sun disc pushed the helper out into the open sky, where nothing else was
   * competing with it.
   */
  function updateSunRay() {
    if (!e.sunRay) return;
    const center = e.modelCenter;
    const sunPos = state.value?.sun.position ?? new THREE.Vector3();
    const positions = e.sunRay.geometry.getAttribute('position') as THREE.BufferAttribute;
    positions.setXYZ(0, sunPos.x, sunPos.y, sunPos.z);
    positions.setXYZ(1, center.x, center.y, center.z);
    positions.needsUpdate = true;
    e.sunRay.visible = e.showSunRay.value && sunEnabled.value;
  }

  function toggleSunRay() {
    e.showSunRay.value = !e.showSunRay.value;
    updateSunRay();
    invalidate();
    save();
  }

  // ─── The sky's own bodies (the sun and the moon) ───────────────────────────

  /**
   * Where a sky body is drawn: a sprite out along its direction from the model.
   *
   * Far enough that the model's own camera moves (a few metres, tens when it
   * flies) barely change its apparent size, and well inside the camera's far
   * plane (5000).
   */
  const SKY_DISC_DISTANCE = 1400;

  /** The sprite textures' own resolution — plenty for a disc and its halo. */
  const SKY_DISC_TEXTURE_PX = 256;

  /**
   * A picture of one sky body, as the material of its sprite.
   *
   * `SunRadius`-style fractions are of the VIEWPORT's width, as they were when
   * this was painted onto the backdrop, so the discs keep the size they had.
   */
  function makeSkyDiscTexture(draw: (ctx: CanvasRenderingContext2D, size: number) => void): THREE.CanvasTexture {
    const canvas = document.createElement('canvas');
    canvas.width = SKY_DISC_TEXTURE_PX;
    canvas.height = SKY_DISC_TEXTURE_PX;
    draw(canvas.getContext('2d')!, SKY_DISC_TEXTURE_PX);
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    return texture;
  }

  function makeSkyDisc(draw: (ctx: CanvasRenderingContext2D, size: number) => void): THREE.Sprite {
    const sprite = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: makeSkyDiscTexture(draw),
        transparent: true,
        // A sky body is light, not a surface: it must not write depth (the model
        // in front of it still hides it — that is the depth TEST) and the scene's
        // exposure must not dim it into the clouds.
        depthWrite: false,
        toneMapped: false,
      }),
    );
    // Behind everything the model draws, which is what a sky is.
    sprite.renderOrder = -1;
    sprite.visible = false;
    return sprite;
  }

  /** The world size that makes a sprite subtend `fraction` of the viewport's width. */
  function skyDiscScale(fraction: number): number {
    const s = state.value;
    const el = container();
    if (!s || !el || el.clientHeight === 0) return fraction * SKY_DISC_DISTANCE;
    // The visible width at the sprite's distance, so the disc keeps the fraction
    // of the viewport it had when it was painted into the backdrop.
    const visibleWidth = 2 * SKY_DISC_DISTANCE * Math.tan((s.camera.fov * Math.PI) / 360) * s.camera.aspect;
    return fraction * visibleWidth;
  }

  /**
   * Put the sun and the moon where they belong, and show only what is up.
   *
   * The direction is the SUN'S OWN — the vector from the model's centre to
   * `sun.position`, which is also what the sun arrow points along. That is what
   * makes the drawn sun agree with the light and the arrow, and what makes it
   * move with the view: it is an object in the scene, not a mark on the backdrop.
   *
   * The moon rides the anti-solar point (the hub's moon position is only true for
   * "now", and a phase-correct moon in a plausible place reads better than a
   * blank night).
   */
  function updateSkyDiscs(): void {
    const s = state.value;
    if (!s || !e.sunDisc || !e.moonDisc) return;
    const centre = e.modelCenter;
    const sunPos = s.sun.position;
    const dir = sunPos.clone().sub(centre);
    if (dir.lengthSq() === 0) dir.set(0, 1, 0);
    dir.normalize();

    // The discs belong to the live sky: with that switched off the backdrop is
    // the plain gradient, and a sun painted into it would be back to being right
    // for one camera pose only.
    const discs = weatherOn.value ? e.weatherSkyDiscs : [];
    const sun = discs.find((disc) => disc.kind === 'sun');
    const moon = discs.find((disc) => disc.kind === 'moon');

    const sunMaterial = e.sunDisc.material as THREE.SpriteMaterial;
    sunMaterial.opacity = sun?.opacity ?? 0;
    e.sunDisc.visible = sunEnabled.value && (sun?.opacity ?? 0) > 0.01;
    e.sunDisc.position.copy(centre).addScaledVector(dir, SKY_DISC_DISTANCE);
    const sunScale = skyDiscScale(SUN_DISC_EXTENT);
    e.sunDisc.scale.set(sunScale, sunScale, 1);

    if (moon) {
      // One texture per phase: the moon's picture is its shape, so a changed
      // phase is a new texture. Once an hour at most, and never per frame.
      if (e.moonDiscPhase !== moon.phase) {
        e.moonDiscPhase = moon.phase;
        const material = e.moonDisc.material as THREE.SpriteMaterial;
        material.map?.dispose();
        material.map = makeSkyDiscTexture((ctx, size) => paintMoon(ctx, size, moon.phase ?? 0, 1));
        material.needsUpdate = true;
      }
      const moonMaterial = e.moonDisc.material as THREE.SpriteMaterial;
      moonMaterial.opacity = moon.opacity;
      e.moonDisc.visible = true;
      // Anti-solar: the moon is up when the sun is down. The tilt keeps it off
      // the exact opposite pole, where it would sit under the terrain.
      const moonDir = new THREE.Vector3(-dir.x, Math.max(0.08, -dir.y * 0.62), -dir.z).normalize();
      e.moonDisc.position.copy(centre).addScaledVector(moonDir, SKY_DISC_DISTANCE);
      const moonScale = skyDiscScale(MOON_DISC_EXTENT);
      e.moonDisc.scale.set(moonScale, moonScale, 1);
    } else {
      e.moonDisc.visible = false;
      e.moonDiscPhase = null;
    }
    // The discs are the backdrop's only 3D part, so moving them is the one reason a
    // backdrop change needs the scene drawn at all (the canvas itself is DOM now).
    invalidate();
  }

  function setExposure(val: number) {
    exposure.value = val;
    const s = state.value;
    if (s) s.renderer.toneMappingExposure = val;
    invalidate();
    save();
  }

  function setAmbientBoost(val: number) {
    ambientBoost.value = val;
    const s = state.value;
    if (!s) return;
    // Scale the environment fill with the "Interior fill" slider so the user
    // actually controls the env contribution (previously the slider only
    // touched the ambient light and could not darken the bright env map).
    s.scene.environmentIntensity = BIM_ENV_BASE * val;
    // Re-apply current lighting to pick up the new boost
    if (sunEnabled.value) {
      applySunTime(e.timeOfDay.value);
    } else {
      s.ambient.intensity = 0.6 * val;
      invalidate();
    }
    save();
  }

  // ─── Gradient background helpers ───────────────────────────────────────────

  /** Repaint the 1×512 gradient canvas from current skyColor → bottomColor */
  function paintGradient() {
    // The whole canvas, at its ONE fixed size — never a resize here: the canvas is
    // the source of a live texture, and resizing it breaks the texture upload (see
    // the canvas's creation). A vertical gradient stretched over a 1024-wide canvas
    // is the same picture it was at 1 px wide.
    const ctx = e.gradCtx;
    const width = e.gradCanvas.width;
    const height = e.gradCanvas.height;
    const top = e.skyColor;
    const bot = e.bottomColor;
    const hor = e.horizonColor;
    const grad = ctx.createLinearGradient(0, 0, 0, height);
    grad.addColorStop(0, `rgb(${Math.round(top.r * 255)},${Math.round(top.g * 255)},${Math.round(top.b * 255)})`);
    grad.addColorStop(0.55, `rgb(${Math.round(hor.r * 255)},${Math.round(hor.g * 255)},${Math.round(hor.b * 255)})`);
    grad.addColorStop(1, `rgb(${Math.round(bot.r * 255)},${Math.round(bot.g * 255)},${Math.round(bot.b * 255)})`);
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, width, height);
    // Which backdrop the operator is looking at is a question the log has to be
    // able to answer: the two painters write the SAME canvas, so the last one wins.
    console.debug(`[BimSky] gradient painted — canvas=${e.gradCanvas.width}x${e.gradCanvas.height}`);
  }

  /**
   * The sun's altitude for an hour, which is all the backdrop itself needs.
   *
   * Where the sun and moon are DRAWN no longer passes through here: they are
   * sprites out along the sun's own direction (`updateSkyDiscs`), the same vector
   * the light and the sun arrow use. That retires the whole question of which
   * azimuth a flat panel should be told — the model's `northAngle` included,
   * which is a scene value and now reaches the discs by being the scene's.
   */
  function sunAltitudeAt(hour: number): number {
    const day = getDayOfYear(e.solarDate.value);
    return computeSolarPosition(hour, e.latitude.value, day).elevDeg;
  }

  /**
   * Draw the live weather into the backdrop — the same sky the weather panels
   * draw, behind the model (utils/bimSky.ts decides it, this only sizes the
   * canvas and paints).
   *
   * The canvas takes the VIEWPORT's own aspect: three stretches a background
   * texture across the renderer, so a square canvas over a 16:9 window would
   * stretch the sun's disc into an ellipse. Re-painted on every lighting change,
   * on a weather change and on a resize — a few milliseconds of canvas work.
   *
   * The plan is painted in TWO halves so that rain can fall without the whole
   * picture being re-rasterised thirty times a second: everything that holds
   * still — the gradient, the clouds, the stars, the mist — goes into a cached
   * canvas of its own, and the drops are the only thing the animation loop draws.
   */
  function paintWeatherSky(): void {
    const el = container();
    if (!el) return;
    // The canvas's OWN size, never the viewport's: three stretches a background
    // texture across the renderer anyway, and a canvas that resizes under a live
    // texture is exactly the upload failure this backdrop was losing its sky to.
    const width = e.gradCanvas.width;
    const height = e.gradCanvas.height;
    const facts = sunAltitudeAt(e.timeOfDay.value);
    const plan = planBimSky({
      altitudeDeg: facts,
      condition: e.weatherSky.condition,
      moonPhase: e.weatherSky.moonPhase,
      bottomHex: `#${e.bottomColor.getHexString()}`,
    });
    e.weatherSkyDiscs = plan.discs;
    e.weatherSkyPrecipitation = plan.precipitation;
    e.weatherSkyDim = plan.dim;

    const still = (e.skyStillCanvas ??= document.createElement('canvas'));
    if (still.width !== width || still.height !== height) {
      still.width = width;
      still.height = height;
    }
    // The STILL sky: the plan without its drops and without its film, both of
    // which the frame painter puts back so they sit in the plan's own order.
    paintBimSky(still.getContext('2d')!, { ...plan, precipitation: null, dim: 0 }, width, height);

    paintPrecipitationFrame();
    console.debug(
      `[BimSky] sky painted — canvas=${e.gradCanvas.width}x${e.gradCanvas.height} condition=${e.weatherSky.condition ?? 'none'}`,
    );
    updateSkyDiscs();

    if (precipitationAnimating()) startPrecipitationLoop();
    else stopPrecipitationLoop();
  }

  /** The cached still sky, with its drops drawn over it at the current time. */
  function paintPrecipitationFrame(): void {
    const still = e.skyStillCanvas;
    if (!still) return;
    e.gradCtx.drawImage(still, 0, 0);
    if (e.weatherSkyPrecipitation) {
      paintBimPrecipitation(e.gradCtx, e.weatherSkyPrecipitation, still.width, still.height, e.precipitationSeconds);
    }
    paintBimDim(e.gradCtx, e.weatherSkyDim, still.width, still.height);
  }

  /**
   * `prefers-reduced-motion` stops the fall: the motion here is decoration, and
   * the rest of the app honours the same switch. The drops stay where the plan
   * put them, which is exactly the still sky this backdrop had before.
   */
  /**
   * Watch the motion preference itself. `precipitationAnimating()` reads it every
   * frame, but the loop STOPS when it turns true — and nothing restarted it when
   * it turned false again (measured: after clearing the emulation the page stayed
   * at 55 fps with the rain frozen). A live listener closes that.
   */
  function watchReducedMotion(): void {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (e.reducedMotionQuery === query) return;
    e.reducedMotionQuery?.removeEventListener?.('change', onReducedMotionChange);
    e.reducedMotionQuery = query;
    query.addEventListener?.('change', onReducedMotionChange);
  }

  function onReducedMotionChange(): void {
    if (prefersReducedMotion()) {
      stopPrecipitationLoop();
      return;
    }
    if (e.weatherSkyPrecipitation !== null) startPrecipitationLoop();
  }

  function prefersReducedMotion(): boolean {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    // Asked once per entry: the list is live, so its answer follows the setting.
    e.reducedMotionQuery ??= window.matchMedia('(prefers-reduced-motion: reduce)');
    return e.reducedMotionQuery.matches;
  }

  /** Whether the sky has something falling that is allowed to move right now. */
  function precipitationAnimating(): boolean {
    return (
      !e.suspended &&
      state.value !== null &&
      weatherOn.value &&
      e.weatherSkyPrecipitation !== null &&
      !prefersReducedMotion()
    );
  }

  /** Stop the falling drops. */
  function stopPrecipitationLoop(): void {
    if (!e.precipitationFrameId.value) return;
    cancelAnimationFrame(e.precipitationFrameId.value);
    e.precipitationFrameId.value = 0;
  }

  /**
   * Start the fall, if the sky has something falling and this page may move it.
   * Idempotent: every repaint of the sky calls it, and only one loop ever runs.
   */
  function startPrecipitationLoop(): void {
    if (e.precipitationFrameId.value || !precipitationAnimating()) return;
    e.precipitationLastFrameAt = performance.now();
    e.precipitationFrameId.value = requestAnimationFrame(stepPrecipitation);
  }

  /**
   * One animation frame of falling rain.
   *
   * The backdrop changes on its own here, so it is this loop — not a camera or
   * lighting change — that has to upload the texture and ask the scene for the
   * frame. Nothing else about the scene moves.
   */
  function stepPrecipitation(now: number): void {
    e.precipitationFrameId.value = 0;
    if (!precipitationAnimating()) return;
    if (now - e.precipitationLastFrameAt < PRECIPITATION_FRAME_MS) {
      e.precipitationFrameId.value = requestAnimationFrame(stepPrecipitation);
      return;
    }
    // Clamped like the scene's own frame delta: a page that was hidden must not
    // teleport the drops when it comes back.
    const dt = Math.min((now - e.precipitationLastFrameAt) / 1000, 0.1);
    e.precipitationLastFrameAt = now;
    e.precipitationSeconds += dt;
    // 2D paint only: no texture upload and no `invalidate()` — that pair was a
    // full house render 30 times a second (measured: 40,893 draw calls/s, 7.7 fps).
    paintPrecipitationFrame();
    e.precipitationFrameId.value = requestAnimationFrame(stepPrecipitation);
  }

  /**
   * The backdrop's ONE painter: the live sky when it is on, the plain gradient
   * otherwise. Every caller that used to repaint the gradient calls this, so the
   * two looks can never both be half-applied.
   */
  function paintBackground(): void {
    // The live sky is painted only once the WEATHER IS KNOWN. Before that the
    // gradient stands in — cheap, and at night it is the same picture anyway.
    // Painting a "clear sky" first and the real one a second later is what the
    // owner saw as the model dimming and the clouds arriving after the house was
    // already on screen (2026-10-23): *"the weather background changed.. the
    // clouds were drawn.. and the model got dimmed.."*.
    if (weatherOn.value && e.weatherAnswered) {
      paintWeatherSky();
    } else {
      stopPrecipitationLoop();
      paintGradient();
    }
    // NO `invalidate()`: the backdrop is DOM, so painting it costs the 3D scene
    // nothing. This line was the reason a rain frame re-drew the whole house.
  }

  /** Switch the backdrop between the live sky and the plain gradient. */
  function setWeatherSkyEnabled(enabled: boolean): void {
    e.weatherSkyEnabled.value = enabled;
    // Neither look resizes the canvas any more: both paint into the one fixed
    // shape, so the texture's size never changes under the renderer.
    paintBackground();
    updateSkyDiscs();
    save();
  }

  /**
   * The live weather the backdrop draws from — pushed by the layout, which owns
   * the one weather read (`useWeather`). `null` clears it back to a clear sky,
   * which is what a hub with no weather should look like.
   */
  function setWeatherSky(condition: string | null, moonPhase: number | null): void {
    const first = !e.weatherAnswered;
    e.weatherAnswered = true;
    const changed = e.weatherSky.condition !== condition || e.weatherSky.moonPhase !== moonPhase;
    e.weatherSky = { condition, moonPhase };
    // The FIRST answer replaces the gradient's stand-in even when it says "no
    // weather" — that is a clear sky, and it is painted once.
    if (weatherOn.value && (changed || first)) {
      paintBackground();
      // The one line that says which backdrop a morning log is looking at, and
      // whether it came from a real answer or from "the hub reported nothing".
      console.info(
        `[BimSky] backdrop painted — condition=${condition ?? 'none (clear sky)'} moon=${moonPhase ?? '-'}${
          first ? ' (the first answer; the gradient stood in until now)' : ''
        }`,
      );
    }
  }
  /** Read the themed bottom color from CSS variables (--gray-950 / --gray-100) */
  function refreshBottomColor() {
    const el = document.documentElement;
    const style = getComputedStyle(el);
    const isDark = el.classList.contains('dark');
    const grayVar = isDark ? '--gray-950' : '--gray-100';
    const rgb = style.getPropertyValue(grayVar).trim();
    if (!rgb) return;
    const [r, g, b] = rgb.split(/\s+/).map(Number);
    e.bottomColor.setRGB(r / 255, g / 255, b / 255);
    // The themed ground colour is one of the sky's own stops, so the whole
    // backdrop is repainted, not just the gradient — the live sky's last stop
    // is this colour.
    paintBackground();
  }

  // ─── Grid toggle ───────────────────────────────────────────────────────────

  function toggleGrid() {
    const s = state.value;
    if (!s) return;
    gridVisible.value = !gridVisible.value;
    s.grid.visible = gridVisible.value;
    invalidate();
  }

  // ─── Init / re-attach ──────────────────────────────────────────────────────

  /**
   * The backdrop canvas goes in FIRST, behind the renderer's own canvas. Both are
   * absolutely positioned by `.bim-sky-canvas` / `.bim-gl-canvas`; the renderer is
   * transparent (`setClearColor(0x000000, 0)`), so this element is what the
   * operator sees behind the house.
   */
  function attachBackdrop(el: HTMLElement): void {
    const sky = e.gradCanvas;
    sky.classList.add('bim-sky-canvas');
    // INLINE styles, not the layout's stylesheet. `bimLayoutStyles.css` is SCOPED,
    // so its selectors only match elements carrying the component's scope
    // attribute — and a canvas created at runtime has none. The rule silently did
    // nothing: the backdrop stayed in normal flow at its intrinsic size (1400×861),
    // pushed the renderer's canvas BELOW it, and the house was drawn off the bottom
    // of the page (owner, 2026-10-24: *"model is off screen.. background not
    // fullscreen.. nothing is working"*).
    Object.assign(sky.style, {
      position: 'absolute',
      left: '0',
      top: '0',
      width: '100%',
      height: '100%',
      zIndex: '0',
      pointerEvents: 'none',
      display: 'block',
    });
    if (el.firstChild !== sky) el.insertBefore(sky, el.firstChild);
  }

  function init() {
    const el = container();
    if (!el) {
      // The template's ref may not be bound on the tick a RE-VISIT mounts (the
      // cached scene is restored before the layout has built its container).
      // Bailing here left the scene alive with NO canvas in the document: the page
      // came back blank while the old geometry, camera and labels were all still
      // there to be dragged around (owner, 2026-10-24: *"i go some other page, then
      // come back and the whole model is repositioned like it being thrown around
      // violently"*). Retry on the next frames, with a cap so a page that never
      // builds a container cannot spin.
      if (e.attachRetries < 30) {
        e.attachRetries += 1;
        requestAnimationFrame(() => init());
      }
      return;
    }
    e.attachRetries = 0;

    console.info(
      `[BimScene] init — ${state.value ? 're-attach' : 'first'} container=${el.isConnected ? 'connected' : 'detached'} ` +
        `canvases=${el.querySelectorAll('canvas').length} cacheSize=${sceneCache.size}`,
    );
    if (state.value) {
      // Scene already exists — just re-attach the canvas elements and resize
      attachBackdrop(el);
      el.appendChild(state.value.renderer.domElement);
      el.appendChild(state.value.labelRenderer.domElement);
      watchReducedMotion();
      refreshBottomColor();
      resize();
      resume();
      if (e.sunClockSynced.value) startSunClock();
      // A re-visit still reconciles with the house: another device may have moved
      // the sun while this page was elsewhere.
      void adoptHubViewSettings();
      return;
    }

    // Scene
    const scene = new THREE.Scene();
    // The backdrop is a DOM canvas BEHIND this renderer, not `scene.background`:
    // a background texture can only change by re-rendering the scene, so every
    // rain frame re-drew the whole house — measured on the operator's page at
    // 5,311 draw calls/frame and 7.7 fps with rain, against 250 and 55 fps with
    // it paused. As a DOM element the weather animates in 2D and the house is
    // drawn only when something in the house changes.
    attachBackdrop(el);
    watchReducedMotion();
    paintBackground();

    // Camera
    const camera = new THREE.PerspectiveCamera(60, el.clientWidth / el.clientHeight, 0.1, 5000);
    camera.position.set(15, 15, 15);
    camera.lookAt(0, 0, 0);

    // WebGL Renderer
    //
    // A phone is the constrained device (owner's report: the 3D page reloads the
    // iOS app in a loop — iOS kills the WebView, Capacitor relaunches into the
    // same route). Its framebuffer and shadow map are the cheapest wins: a
    // devicePixelRatio of 3 means 9x the pixels of CSS size, and shadows render
    // the whole model a second time.
    const touchDevice = typeof navigator !== 'undefined' && (navigator.maxTouchPoints ?? 0) > 0;
    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: !touchDevice, alpha: true });
    } catch (error) {
      // A context that could not be created used to throw inside onMounted and paint
      // NOTHING — no model, no message (owner, 2026-11-02: *"it doesn't load"* after a
      // hub switch). Say so instead, so the surface shows its error state.
      console.error('[BimScene] WebGL context could not be created', error);
      modelError.value =
        'The 3D view could not start on this device — the WebGL context could not be created. Reopen the page, or close another 3D view first.';
      return;
    }
    // Transparent clear: the backdrop canvas behind this one IS the sky. The
    // scene never draws a background of its own (`scene.background` stays null).
    renderer.setClearColor(0x000000, 0);
    renderer.domElement.classList.add('bim-gl-canvas');
    // Same reason as the backdrop's: inline beats a scoped stylesheet this element
    // has no scope attribute for. Absolute so it can never push the backdrop around.
    Object.assign(renderer.domElement.style, {
      position: 'absolute',
      left: '0',
      top: '0',
      zIndex: '1',
    });
    renderer.setPixelRatio(touchDevice ? Math.min(window.devicePixelRatio || 1, 1.5) : window.devicePixelRatio);
    renderer.setSize(el.clientWidth, el.clientHeight);
    // ── Shader-error checking: OFF unless asked for ────────────────────────────
    // three's check (its `onFirstUse`) runs a SYNCHRONOUS driver round trip for
    // EVERY new program — `getProgramParameter(LINK_STATUS)`,
    // `getProgramParameter(VALIDATE_STATUS)` and both shader info logs. On this
    // house (685 meshes, many materials) through ANGLE/D3D11 that was the whole
    // opening: a CPU profile of a 55 s window put 94 % of its self time in that
    // one function, and the page ran at ~1 fps until every program had been
    // validated. It is a DIAGNOSTIC, not a feature: a release build does not pay
    // for it, and the door below puts it back when a blank scene needs reading
    // (`__bimShaderCheck(true)` before the next load, or `?bimShaderCheck=1`).
    renderer.debug.checkShaderErrors = readShaderCheckPreference();
    /**
     * The LIGHTING's own read (owner, 2026-10-23: *"the model shows up bright at
     * first.. then the lights flicker and the model gets darkened (because of
     * weather? sun?)"*). One row per sample, so a rig can line the change up
     * against the load instead of guessing which of sun, exposure, weather dim or
     * the fixture budget moved it. Read-only; nothing in the app calls it.
     */
    /**
     * The camera's trajectory, for a rig: `__bimTrace(true)` records one row per
     * rendered frame, `__bimTrace(false)` stops and drains. "Spins like crazy and
     * ends under the grass" is a shape in this list, not an opinion.
     */
    (window as unknown as Record<string, unknown>).__bimTrace = (on = true, rows = 400) => {
      if (on) {
        e.trace.length = 0;
        e.traceOn = true;
        return { recording: true };
      }
      e.traceOn = false;
      const out = e.trace.slice(-rows);
      e.trace.length = 0;
      return out;
    };
    /**
     * Trap every write to the camera's position, with the stack that wrote it
     * (`__bimTrap(true)` … `__bimTrap(false)` drains). A camera that moves with no
     * flight and one instance is written by SOMETHING — this names it.
     */
    (window as unknown as Record<string, unknown>).__bimFramed = () => e.framedOnce;
    (window as unknown as Record<string, unknown>).__bimTrap = (on = true) => {
      const s = state.value;
      if (!s) return 'no scene';
      const pos = s.camera.position;
      if (on) {
        if (!e.trapStacks) e.trapStacks = [];
        e.trapStacks.length = 0;
        const record = (name: string) => {
          const stack = (new Error().stack || '').split('\n').slice(2, 8).map((l) => l.trim());
          e.trapStacks!.push({ name, at: Math.round(performance.now()), stack });
          if (e.trapStacks!.length > 400) e.trapStacks!.splice(0, 100);
        };
        const trap = pos as unknown as {
          set: (x: number, y: number, z: number) => void;
          copy: (v: THREE.Vector3) => void;
        };
        const originalSet = pos.set.bind(pos);
        const originalCopy = pos.copy.bind(pos);
        trap.set = (x: number, y: number, z: number) => {
          record('set');
          originalSet(x, y, z);
        };
        trap.copy = (v: THREE.Vector3) => {
          record('copy');
          originalCopy(v);
        };
        return 'trap on';
      }
      const out = e.trapStacks ?? [];
      e.trapStacks = [];
      return out.slice(-120);
    };
    (window as unknown as Record<string, unknown>).__bimLighting = () => {
      const s = state.value;
      if (!s) return null;
      return {
        at: Math.round(performance.now()),
        hour: Math.round(e.timeOfDay.value * 100) / 100,
        sunEnabled: sunEnabled.value,
        autoLighting: e.autoLighting.value,
        exposure: Math.round(e.exposure.value * 100) / 100,
        ambientBoost: Math.round(e.ambientBoost.value * 100) / 100,
        rendererExposure: Math.round(s.renderer.toneMappingExposure * 100) / 100,
        envIntensity: Math.round((s.scene.environmentIntensity ?? 0) * 1000) / 1000,
        sunIntensity: Math.round(s.sun.intensity * 1000) / 1000,
        sunColor: `#${s.sun.color.getHexString()}`,
        hemiIntensity: Math.round(s.hemi.intensity * 1000) / 1000,
        ambientIntensity: Math.round(s.ambient.intensity * 1000) / 1000,
        weatherSkyEnabled: weatherOn.value,
        weatherCondition: e.weatherSky.condition,
        skyDim: Math.round((e.weatherSkyDim ?? 0) * 1000) / 1000,
        /**
         * The sky's two bodies: present in the scene at all, and drawn — a surface
         * that reports `sunEnabled` with a hidden disc is a surface whose backdrop
         * never got its plan (the card and the hero read the same numbers as the
         * page, so this is what tells "night" from "nothing drawn").
         */
        sunDisc: e.sunDisc ? e.sunDisc.visible : null,
        moonDisc: e.moonDisc ? e.moonDisc.visible : null,
        discsPlanned: e.weatherSkyDiscs.length,
        /**
         * The frame-time EMA the fixture-light budget reads, and whether it is
         * currently a sample or a stall — the two numbers that explain "the lights
         * flicker" without a guess (see `isRenderStall`).
         */
        frameMsAvg: Math.round(e.frameMsAvg * 1000) / 1000,
        frameSampleIsStall: isRenderStall(e.frameMsAvg),
        /**
         * The scene's own geometry, read on demand: how many model groups are in
         * it, how many canvases the container holds, and where the camera, its
         * orbit target and the model group actually are. A re-visit that leaves two
         * groups, two canvases or a target at the origin is visible here and
         * nowhere else (owner, 2026-10-24: *"the whole model is repositioned like it
         * being thrown around violently"*).
         */
        geometry: (() => {
          const el = container();
          const groups = s.scene.children.filter((child) => child.userData.__bimGroup);
          const group = groups[0] ?? null;
          const toFixed = (n: number) => Math.round(n * 100) / 100;
          return {
            groups: groups.length,
            canvases: el ? el.querySelectorAll('canvas').length : 0,
            camera: [toFixed(s.camera.position.x), toFixed(s.camera.position.y), toFixed(s.camera.position.z)],
            target: s.controls ? [toFixed(s.controls.target.x), toFixed(s.controls.target.y), toFixed(s.controls.target.z)] : null,
            groupPosition: group
              ? [toFixed(group.position.x), toFixed(group.position.y), toFixed(group.position.z)]
              : null,
            groupRotationY: group ? toFixed(group.rotation.y) : null,
            groupScale: group ? toFixed(group.scale.x) : null,
            modelMeshes: modelMeshes.value.length,
            /**
             * Flights are the one thing that writes the camera without the operator:
             * how many views are live, whether one is in the air, and where the last
             * one was aimed. Two live views each flying to their own preset is what an
             * alternating azimuth looks like from outside.
             */
            instances: liveBimViews,
            flying: e.flyToAnimationId.value !== 0,
            lastFlight: e.lastFlight,
          };
        })(),
        /**
         * The BACKDROP's own pixels: what the painter actually put on the canvas
         * three uploads as the scene's background. A "painted" line in the log and
         * a black screen are the same story without these numbers.
         */
        sky: (() => {
          const canvas = e.gradCanvas;
          const sample = (x: number, y: number) => {
            try {
              const d = e.gradCtx.getImageData(x, y, 1, 1).data;
              return [d[0], d[1], d[2], d[3]];
            } catch {
              return null;
            }
          };
          return {
            canvas: `${canvas.width}x${canvas.height}`,
            still: e.skyStillCanvas ? `${e.skyStillCanvas.width}x${e.skyStillCanvas.height}` : null,
            inDom: Boolean(canvas.parentElement),
            behindRenderer: canvas.parentElement
              ? canvas.parentElement.firstElementChild === canvas
              : false,
            sceneBackground: s.scene.background === null ? 'none' : 'set',
            top: sample(0, 2),
            middle: sample(0, Math.max(0, Math.floor(canvas.height / 2))),
            bottom: sample(0, Math.max(0, canvas.height - 2)),
          };
        })(),
      };
    };
    renderer.shadowMap.enabled = e.shadowsEnabled.value && !touchDevice;
    renderer.shadowMap.type = THREE.PCFShadowMap;
    // Linear tone mapping, not NoToneMapping: it is the exposure with nothing
    // else — `saturate(toneMappingExposure * colour)` — so the IFC colours are
    // still rendered exactly as authored (ACES filmic mapping, the previous
    // default, desaturates and washes out the pastel/white architectural
    // palette). NoToneMapping was the wrong tool for that intent: three omits
    // the whole `tonemapping_pars_fragment` chunk — `toneMappingExposure`
    // included — when tone mapping is off, so the Exposure slider did nothing at
    // all. At exposure 1 the two are identical (the framebuffer clamps to 1
    // either way); the difference is that this one responds.
    renderer.toneMapping = THREE.LinearToneMapping;
    renderer.toneMappingExposure = e.exposure.value;
    el.appendChild(renderer.domElement);
    // The DEBUG door for the check above: flips the LIVE renderer and remembers
    // the answer, so a rig can read a failing program without a reload.
    (window as unknown as Record<string, unknown>).__bimShaderCheck = (on = true) => {
      shaderCheckOverride = on;
      renderer.debug.checkShaderErrors = on;
      if (state.value) state.value.renderer.debug.checkShaderErrors = on;
      try {
        window.localStorage.setItem(SHADER_CHECK_STORAGE_KEY, on ? '1' : '0');
      } catch {
        // A refused write leaves the choice for this session only.
      }
      return on;
    };

    // Soft environment fill for MeshStandardMaterial. Without this, interior
    // surfaces receive only flat ambient/hemisphere light and render as uniform
    // low-contrast gray — the "foggy/pale" look. A baked RoomEnvironment gives
    // every surface gentle omnidirectional shading so rooms read crisply.
    // Intensity is kept LOW (and tied to the "Interior fill" slider) so the sun
    // stays visible and nothing blows out toward white.
    const pmrem = new THREE.PMREMGenerator(renderer);
    const envTexture = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    pmrem.dispose();
    e.envTexture = envTexture;

    // The gizmos' scene: drawn separately (see `renderOverlayScene`) so the
    // section cut can never slice a handle.
    const overlayScene = new THREE.Scene();

    // The sky's bodies' own scene: drawn separately, BEFORE the model and with
    // the cut lifted (see `renderSkyBodies`), for the same reason.
    const skyScene = new THREE.Scene();

    // CSS2D Renderer (for HTML widget overlays)
    const labelRenderer = new CSS2DRenderer();
    labelRenderer.setSize(el.clientWidth, el.clientHeight);
    labelRenderer.domElement.style.position = 'absolute';
    // Above `.bim-gl-canvas` (z-index 1): the label layer is DOM over a DOM sky over
    // a transparent renderer, and a z-index on the renderer would otherwise bury it.
    labelRenderer.domElement.style.zIndex = '2';
    labelRenderer.domElement.style.top = '0';
    labelRenderer.domElement.style.pointerEvents = 'none';
    el.appendChild(labelRenderer.domElement);

    // Orbit Controls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = DEFAULT_DAMPING_FACTOR;
    controls.screenSpacePanning = true;
    controls.minDistance = 0.5;
    controls.maxDistance = 2000;
    // ── The turntable needs a horizon AND a defined axis ─────────────────────
    // Both ends of the polar range matter, and both were open:
    //  · straight DOWN (phi ≈ 0) makes a horizontal drag spin the house around the
    //    VIEW axis instead of orbiting it — a top-down preset put the camera exactly
    //    above its target, azimuth undefined (owner, 2026-10-24: *"it starts spinning
    //    like crazy"*);
    //  · below the horizon (phi → π) swings the camera under the terrain and the
    //    operator looks at the grass from below (*"now I am seeing the grass from
    //    below"*), then every drag only moves the azimuth — the spin again.
    // A few degrees of tilt at the top and a stop at the horizon make every drag an
    // orbit.
    controls.minPolarAngle = ORBIT_MIN_POLAR_ANGLE;
    controls.maxPolarAngle = ORBIT_MAX_POLAR_ANGLE;
    controls.addEventListener('change', () => {
      queueCameraSerialUpdate();
      updateCameraAzimuthRefs(camera);
      markCameraInteraction();
      invalidate();
    });

    // At max zoom the wheel moves the camera FORWARD along the ground instead of
    // zooming any further (owner, 2026-10-14: *"When max zoom reached, then
    // scroller moves the cmaera fwd/back"*). It stays in ORBIT: the same message
    // used to drop the operator into GRAVITY, which turned a scroll into a mode
    // change nobody asked for (*"Cancel this!!!"*).
    //
    // Scrolling OUT is the way home, though (owner, 2026-10-20: *"if you zoom to
    // much it starts walking … as soon as you scroll back it should go back into
    // orbit, now its stuck int this orbital walk"*). The walk keeps camera and
    // target together, so the distance never leaves `minDistance` and a backward
    // pan could never restore the zoom — the wheel stayed hijacked forever. The
    // wheel-out is therefore left to OrbitControls, which pulls the camera off the
    // target and hands the turntable back.
    // Registered at capture phase so it fires before OrbitControls' own wheel
    // handler; stopImmediatePropagation prevents zoom.
    const _onWheelAtMaxZoom = (ev: WheelEvent) => {
      // The wheel belongs to the turntable: with the pointer in a first-person
      // mode, scrolling must not pan the walker around.
      if (navMode.value !== 'orbit') return;
      if (camera.position.distanceTo(controls.target) > controls.minDistance + 0.05) return;
      // Zooming out is the zoom: let OrbitControls take it and leave the walk.
      if (ev.deltaY > 0) return;
      ev.preventDefault();
      ev.stopImmediatePropagation();

      // Project camera look direction onto the horizontal XZ plane
      const forward = new THREE.Vector3();
      camera.getWorldDirection(forward);
      forward.y = 0;
      if (forward.lengthSq() < 0.0001) return; // camera pointing straight up/down
      forward.normalize();

      // Pan speed proportional to wheel delta
      const panAmount = -(ev.deltaY / 100) * 0.3;
      const move = forward.multiplyScalar(panAmount);
      camera.position.add(move);
      controls.target.add(move);

      controls.update();
    };
    renderer.domElement.addEventListener('wheel', _onWheelAtMaxZoom, { passive: false, capture: true });
    e.wheelListener = _onWheelAtMaxZoom;

    // Lighting
    // With the RoomEnvironment providing soft omnidirectional fill, keep the
    // explicit lights lower so interiors get gentle directional variation
    // instead of flat uniform gray. The sun still drives highlights/shadows.
    scene.environment = envTexture;
    scene.environmentIntensity = BIM_ENV_BASE * e.ambientBoost.value;
    const ambient = new THREE.AmbientLight(0xffffff, 0.18);
    scene.add(ambient);
    const sun = new THREE.DirectionalLight(0xfff5e0, 1.4);
    sun.position.set(150, 250, 100);
    sun.castShadow = true;
    sun.shadow.mapSize.set(2048, 2048);
    sun.shadow.camera.near = 1;
    sun.shadow.camera.far = 600;
    sun.shadow.camera.left = -50;
    sun.shadow.camera.right = 50;
    sun.shadow.camera.top = 50;
    sun.shadow.camera.bottom = -50;
    scene.add(sun);
    scene.add(sun.target);
    const hemi = new THREE.HemisphereLight(0x87ceeb, 0x7cba7c, 0.22);
    scene.add(hemi);

    // Grid helper — created but hidden by default
    const grid = new THREE.GridHelper(100, 100, 0x333355, 0x222244);
    grid.visible = false;
    scene.add(grid);

    const raycaster = new THREE.Raycaster();

    state.value = {
      scene,
      overlayScene,
      skyScene,
      camera,
      renderer,
      labelRenderer,
      controls,
      raycaster,
      grid,
      sun,
      hemi,
      ambient,
      // Filled by the render loop; the fixture-light budget reads it to decide how
      // many lamps may light the room (`adaptiveLightBudget`).
      frameMs: () => e.frameMsAvg,
      lightIntensityFactor: LIGHT_PRESET_FACTOR[lightPresetOf(lightPreset.value)],
      glowScale: clampGlowScale(glowScale.value),
    };
    // The overlay motion gate's zero point (card t_72106dc1): a move is measured
    // from the pose the view settles on, and the load-fit's own placement is
    // expected to count as one.
    resetCameraSettlePose();
    applyNavControls(navMode.value);
    // The pointer look is attached to the canvas itself (drag = look, a clean
    // click = pointer lock, Esc releases).
    attachPointerLook(renderer.domElement);
    // A mode the user left the app in is honoured on the next load too — the
    // camera then settles onto the model's own floor (gravity) as soon as the
    // model is there.
    if (isFirstPersonMode(navMode.value)) enterFirstPerson();

    // ─── The sun's ray ───────────────────────────────────────────────────────
    {
      const geometry = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(), new THREE.Vector3(0, 1, 0)]);
      const material = new THREE.LineBasicMaterial({ color: 0xfacc15, transparent: true, opacity: 0.75 });
      const sunRay = new THREE.Line(geometry, material);
      // Two points 300 units apart, re-aimed on every sun move: culling them by a
      // bounding sphere that never moves with them would hide the line the moment
      // it left the origin.
      sunRay.frustumCulled = false;
      sunRay.visible = e.showSunRay.value && sunOn.value;
      scene.add(sunRay);
      e.sunRay = sunRay;
    }

    // ─── The sky's own bodies ────────────────────────────────────────────────
    // Real objects out at the sun's (and the anti-sun's) direction, so they agree
    // with the light and with the ray, and move with the view. Their own scene,
    // so the section cut cannot slice them (see `renderSkyBodies`).
    e.sunDisc = makeSkyDisc((ctx, size) => paintSunDisc(ctx, size, 1));
    e.moonDisc = makeSkyDisc((ctx, size) => paintMoon(ctx, size, 0.5, 1));
    skyScene.add(e.sunDisc);
    skyScene.add(e.moonDisc);

    // Set initial gradient bottom color from current theme
    refreshBottomColor();

    // The sun follows the clock from the first frame when it is synced — a
    // view opened at 21:40 shows the 21:40 sun, not the stored one.
    if (e.sunClockSynced.value) syncSunClock();
    else applySunTime(e.timeOfDay.value);
    if (e.sunClockSynced.value) startSunClock();

    resume();
    // The cache painted this first frame, the section band included: put it on the
    // planes before the first render. Guarded as an ADOPT (`applying`), because a
    // render-cache read is not a change — nothing is written back.
    e.hubSync.applying = true;
    try {
      if (clipEnabled.value) setClipRange(clipHeightY.value, clipBottomY.value);
    } finally {
      e.hubSync.applying = false;
    }
    // The hub's copy — the house's own sun — takes over as soon as it answers
    // (see `adoptHubViewSettings`).
    void adoptHubViewSettings();
  }

  function resize() {
    const el = container();
    const s = state.value;
    if (!el || !s) return;
    const w = el.clientWidth;
    const h = el.clientHeight;
    s.camera.aspect = w / h;
    // The projection is what carries the aspect into the frustum. Setting
    // `aspect` alone leaves the old frustum in place while the viewport takes
    // the new size, and the model paints STRETCHED. `filmOffset` is zeroed in
    // the same breath: no drawer owns a lens shift any more, and a cached scene
    // built by a build that still had one must not keep it.
    s.camera.filmOffset = 0;
    s.camera.updateProjectionMatrix();
    s.renderer.setSize(w, h);
    s.labelRenderer.setSize(w, h);
    // The backdrop is a DOM canvas the size of the container (capped: it is a soft
    // sky, and a 4K bitmap is 4x the fill for no visible gain). Resizing a plain
    // canvas is free — it is no longer the source of a GL texture, which is what
    // made a resize break the upload (*"only blue, no matter the time of day"*).
    const backdropWidth = Math.min(1400, Math.max(320, Math.round(w || 320)));
    const backdropHeight = Math.max(200, Math.round((h / Math.max(1, w)) * backdropWidth));
    if (e.gradCanvas.width !== backdropWidth || e.gradCanvas.height !== backdropHeight) {
      e.gradCanvas.width = backdropWidth;
      e.gradCanvas.height = backdropHeight;
    }
    paintBackground();
    updateSkyDiscs();
    invalidate();
  }

  /**
   * Load a model from a URL and put it on screen — an IFC through web-ifc, or a
   * GLB/glTF through three's own GLTFLoader (card t_14696d19).
   *
   * Which of the two it is comes from the BYTES, not from the URL: the hub
   * serves every model through one extension-less route
   * (`/api/dashboards/:id/bim-models/:modelId/file`), so the sniffed format is
   * the only reliable signal (`utils/bimModelFormat.ts`). An unidentified
   * payload takes the IFC path — which is what this loader did before the
   * split, web-ifc's own parse error included.
   *
   * Falls back to a placeholder geometry if the library or model fails, so the
   * viewer always remains functional.
   *
   * `options.preserveView` turns the load into a LIVE SWAP (the Home 3D
   * auto-refresh, card t_4e78c7af):
   *
   *   · the model already on screen keeps rendering while the new file
   *     downloads and parses — the two models exchange places in ONE tick, so
   *     the canvas never goes blank and there is no white flash; only then are
   *     the replaced group's geometries and materials released;
   *   · the camera pose and the orbit target are left exactly as they are
   *     (the fit below is what a load does — a swap must not move the view);
   *   · a failed fetch leaves the previous model untouched and reports false —
   *     the live view must never become the error placeholder, and the next
   *     poll retries.
   *
   * Returns whether a model is on screen when it completes.
   */
  async function loadModelFromUrl(url: string, options: BimLoadModelOptions = {}): Promise<boolean> {
    const s = state.value;
    if (!s) return false;
    const preserveView = options.preserveView === true;
    // A load frames the camera by default. `framing: 'none'` is for a caller that
    // owns the opening pose itself (the dashboard's first preset): the install
    // still sets near/far from the model's size, but it does not touch the pose,
    // so the page never shows the loader's aerial frame one flight before the
    // view it was asked to open on.
    const framing = options.framing !== 'none';
    // The meshes/groups this load replaces — kept in place until the new model
    // is fully built (a live swap may render for the whole parse), removed and
    // disposed at the swap point below.
    const previousMeshes = modelMeshes.value;

    modelLoading.value = true;
    modelError.value = null;

    try {
      // ── Fetch the bytes FIRST — they are what picks the loader ─────────────
      // WHO the model belongs to decides HOW it is fetched.
      //
      //   · `hub` (the default, every surface until Torik Plan): the model is the
      //     hub's, so the request goes out with the hub's local token and through
      //     `routedFetch` — which carries it over the WebRTC data channel in cloud
      //     mode and would 404 at the cloud origin if it went out as plain HTTP.
      //   · `cloud`: the model belongs to a CLOUD project (Torik Plan), so it is
      //     NOT a hub path and must not travel the channel; it is fetched directly
      //     with the cloud access token. A presigned storage URL needs no token at
      //     all, which is what `bearer: 'none'` is for.
      //
      // Adding the hub token to a cloud request would be worse than useless: the
      // cloud would verify it against the wrong issuer.
      const authStore = useAuthStore();
      const bearer = options.bearer ?? 'hub';
      const headers: Record<string, string> = {};
      if (bearer === 'hub' && authStore.token) {
        headers.Authorization = `Bearer ${authStore.token}`;
      }

      const fetchModel = async (): Promise<Response> => {
        if (bearer === 'hub') return routedFetch(url, { headers });
        if (bearer === 'cloud') {
          await authStore.ensureCloudToken();
          if (authStore.cloudAccessToken) {
            headers.Authorization = `Bearer ${authStore.cloudAccessToken}`;
          }
        }
        // A direct fetch, deliberately: this URL is not a hub path, and sending it
        // through the channel is what the router exists to prevent.
        const response = await fetch(url, { headers });
        // One refresh + retry on a 401, the same policy `cloudFetch` applies: a
        // long-lived planning page must survive an expired token, and a model that
        // fails to load over an expiry reads to the operator as a missing model.
        if (bearer === 'cloud' && response.status === 401) {
          const refreshed = await authStore.refreshCloudAccessToken();
          if (refreshed) {
            if (authStore.cloudAccessToken) {
              headers.Authorization = `Bearer ${authStore.cloudAccessToken}`;
            }
            return fetch(url, { headers });
          }
        }
        return response;
      };

      const response = await fetchModel();
      if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      const arrayBuffer = await response.arrayBuffer();
      const bytes = new Uint8Array(arrayBuffer);

      // ── The format split (card t_14696d19; USDZ by t_92f7411f) ────────────
      // Content first, the URL's own name second (an upload's filename):
      // `resolveBimModelFormat` documents the two pieces of evidence. A glTF or
      // a USDZ goes to three's own loader and this function is done with it; an
      // IFC (and anything unidentified) continues into the web-ifc pipeline
      // below, unchanged.
      const format = resolveBimModelFormat({ name: url, bytes }) ?? 'ifc';
      console.info(`[useBimScene] loading model — format=${format} bytes=${bytes.byteLength}`);
      if (isGltfFormat(format)) {
        const gltf = await buildGltfModel(bytes, format, url, applyMeshVisibility);
        return installModelGroup(gltf, { url, preserveView, framing, previousMeshes });
      }
      if (isUsdzFormat(format)) {
        const usd = await buildUsdModel(bytes, applyMeshVisibility);
        return installModelGroup(usd, { url, preserveView, framing, previousMeshes });
      }

      // Dynamic import — WASM is only loaded when the viewer is actually used
      const { IfcAPI } = await import('web-ifc');

      // Build a type-number → short name map from web-ifc exports once
      const webifc = await import('web-ifc');
      const typeNameMap = new Map<number, string>(
        Object.entries(webifc)
          .filter(([k, v]) => typeof v === 'number' && k.startsWith('IFC'))
          .map(([k, v]) => [v as number, k.slice(3)]), // strip leading 'IFC' for display
      );

      const ifcAPI = new IfcAPI();
      // web-ifc.wasm is copied from public/ into the build root. Use an
      // origin-absolute path in http(s) mode. In Electron the renderer is a
      // file:// page where window.location.origin is the string "file://"
      // (not "null"). Derive the app directory from the document href, and
      // ALWAYS strip the hash fragment — the app uses hash-based routing
      // (#/...), so href ends in index.html#/ and lastIndexOf('/') would
      // otherwise pick up the hash segment and resolve the wasm to index.html.
      const origin = window.location.origin;
      const isFileScheme =
        origin === 'null' || origin === '' || origin === 'file://' || window.location.protocol === 'file:';
      const wasmBase = isFileScheme
        ? (() => {
            const h = window.location.href.split('#')[0];
            return h.substring(0, h.lastIndexOf('/') + 1);
          })()
        : `${origin}/`;
      ifcAPI.SetWasmPath(wasmBase, true);
      await ifcAPI.Init();

      const modelID = ifcAPI.OpenModel(bytes, { COORDINATE_TO_ORIGIN: true });

      // ── Build storey map ──────────────────────────────────────────────────
      // Parse IfcBuildingStorey elements → {name, elevation}
      const storeyInfoMap = new Map<number, { name: string; elevation: number }>();
      try {
        const storeyIDs = ifcAPI.GetLineIDsWithType(modelID, webifc.IFCBUILDINGSTOREY);
        for (let si = 0; si < storeyIDs.size(); si++) {
          const sid = storeyIDs.get(si) as number;
          try {
            const line = ifcAPI.GetLine(modelID, sid, false);
            const rawName = line?.Name?.value ?? line?.LongName?.value;
            const name = rawName ? String(rawName) : `Floor ${si + 1}`;
            const elevation = typeof line?.Elevation?.value === 'number' ? line.Elevation.value : si * 3;
            storeyInfoMap.set(sid, { name, elevation });
          } catch {
            storeyInfoMap.set(sid, { name: `Floor ${si + 1}`, elevation: si * 3 });
          }
        }
      } catch {
        /* IFCBUILDINGSTOREY not found in this model */
      }

      // Parse IfcRelContainedInSpatialStructure → element ID → storey ID
      const elementStoreyID = new Map<number, number>();
      try {
        const containRelIDs = ifcAPI.GetLineIDsWithType(modelID, webifc.IFCRELCONTAINEDINSPATIALSTRUCTURE);
        for (let ci = 0; ci < containRelIDs.size(); ci++) {
          const relID = containRelIDs.get(ci) as number;
          try {
            const rel = ifcAPI.GetLine(modelID, relID, false);
            const structureRef = rel?.RelatingStructure?.value as number | undefined;
            if (structureRef === undefined || !storeyInfoMap.has(structureRef)) continue;
            const relatedElements: unknown[] = rel?.RelatedElements ?? [];
            for (const elemRef of relatedElements) {
              const eid = expressIDOf(elemRef);
              if (eid !== undefined) elementStoreyID.set(eid, structureRef);
            }
          } catch {
            /* skip bad rel */
          }
        }
      } catch {
        /* IFCRELCONTAINEDINSPATIALSTRUCTURE not found */
      }
      // ─────────────────────────────────────────────────────────────────────

      const newMeshes: THREE.Mesh[] = [];
      const newElementMeshes = new Map<number, THREE.Mesh[]>();
      const newElements: IfcElementInfo[] = [];
      const seenExpressIDs = new Set<number>();

      // Group every placed geometry by (element, material) so we can merge it
      // into ONE mesh per unique material per element. This cuts draw calls by
      // roughly 6× on typical models (3993 → ~630 for the Rakitna house) while
      // keeping per-element picking and visibility toggles intact, because each
      // merged mesh still belongs to a single expressID.
      interface MergeBucket {
        eid: number;
        typeName: string;
        color: { x: number; y: number; z: number; w: number };
        geoms: THREE.BufferGeometry[];
      }
      const mergeBuckets = new Map<string, MergeBucket>();

      ifcAPI.StreamAllMeshes(modelID, (ifcMesh) => {
        const typeNum = ifcAPI.GetLineType(modelID, ifcMesh.expressID);
        const typeName = typeNameMap.get(typeNum) ?? `Type_${typeNum}`;
        const eid = ifcMesh.expressID;

        // Collect element info once per expressID
        if (!seenExpressIDs.has(eid)) {
          seenExpressIDs.add(eid);
          let elementName = `${typeName} #${eid}`;
          let elementDescription = '';
          try {
            const line = ifcAPI.GetLine(modelID, eid, false);
            if (line?.Name?.value) elementName = String(line.Name.value);
            // `Description` is a string attribute on most entities; anything
            // else (absent, or not a value-bearing handle) reads as none.
            const rawDescription = line?.Description?.value;
            if (typeof rawDescription === 'string') elementDescription = rawDescription;
          } catch {
            /* use default name */
          }
          const sid = elementStoreyID.get(eid);
          const storeyInfo = sid !== undefined ? storeyInfoMap.get(sid) : undefined;
          newElements.push({
            expressID: eid,
            typeName,
            name: elementName,
            description: elementDescription,
            storeyName: storeyInfo?.name ?? 'Unassigned',
            storeyElevation: storeyInfo?.elevation ?? -Infinity,
          });
        }

        const geometries = ifcMesh.geometries;
        for (let i = 0; i < geometries.size(); i++) {
          const placed = geometries.get(i);
          const c = placed.color;

          // Skip fully transparent "air space" volumes — they are invisible and
          // only add transparent-pass sorting noise (and depth artifacts).
          if (c.w <= 0.01) continue;

          const geomData = ifcAPI.GetGeometry(modelID, placed.geometryExpressID);

          const rawVerts = ifcAPI.GetVertexArray(geomData.GetVertexData(), geomData.GetVertexDataSize());
          const rawIdx = ifcAPI.GetIndexArray(geomData.GetIndexData(), geomData.GetIndexDataSize());

          // Each vertex is 6 floats: x,y,z, nx,ny,nz
          const posArr = new Float32Array(rawVerts.length / 2);
          const normArr = new Float32Array(rawVerts.length / 2);
          for (let j = 0; j < rawVerts.length; j += 6) {
            const p = (j / 6) * 3;
            posArr[p] = rawVerts[j];
            posArr[p + 1] = rawVerts[j + 1];
            posArr[p + 2] = rawVerts[j + 2];
            normArr[p] = rawVerts[j + 3];
            normArr[p + 1] = rawVerts[j + 4];
            normArr[p + 2] = rawVerts[j + 5];
          }

          const bufferGeom = new THREE.BufferGeometry();
          bufferGeom.setAttribute('position', new THREE.BufferAttribute(posArr, 3));
          bufferGeom.setAttribute('normal', new THREE.BufferAttribute(normArr, 3));
          bufferGeom.setIndex(new THREE.BufferAttribute(rawIdx, 1));

          // Bake the placement matrix into the vertices so all geometries of an
          // element end up in the same coordinate space and can be merged.
          const matrix = new THREE.Matrix4().fromArray(Array.from(placed.flatTransformation));
          bufferGeom.applyMatrix4(matrix);

          const colorKey = `${c.x.toFixed(4)},${c.y.toFixed(4)},${c.z.toFixed(4)},${c.w.toFixed(4)}`;
          const key = `${eid}::${colorKey}`;
          let bucket = mergeBuckets.get(key);
          if (!bucket) {
            bucket = { eid, typeName, color: { x: c.x, y: c.y, z: c.z, w: c.w }, geoms: [] };
            mergeBuckets.set(key, bucket);
          }
          bucket.geoms.push(bufferGeom);

          geomData.delete();
        }
      });

      // web-ifc's coordination matrix: the translation `COORDINATE_TO_ORIGIN`
      // applied to this model's geometry. It is only populated once geometry has
      // been processed (identity before the stream above), and it is a pure
      // translation — measured constant across every element of the demo house.
      // `useBimSceneFrameContract.test.ts` pins the frame rule this feeds.
      let coordinationMatrix: number[] | null = null;
      try {
        coordinationMatrix = Array.from(ifcAPI.GetCoordinationMatrix(modelID));
      } catch (__caught: unknown) {
        console.warn('[useBimScene] IFC coordination matrix unavailable:', __caught);
      }

      // Build one mesh per (element, material) bucket
      for (const bucket of mergeBuckets.values()) {
        const { eid, typeName, color } = bucket;
        const merged = bucket.geoms.length === 1 ? bucket.geoms[0] : mergeGeometries(bucket.geoms, false);
        if (!merged) continue;
        merged.computeBoundingSphere();

        const isTransparent = color.w < 0.999;
        const mat = new THREE.MeshStandardMaterial({
          color: new THREE.Color(color.x, color.y, color.z),
          roughness: 0.85,
          metalness: 0,
          transparent: isTransparent,
          opacity: Math.min(Math.max(color.w, 0.05), 1),
          side: THREE.DoubleSide,
          // Critical for interiors: transparent glass must not write depth,
          // otherwise every pane of glass "mists up" the whole view.
          depthWrite: !isTransparent,
        });

        const mesh = new THREE.Mesh(merged, mat);
        mesh.castShadow = !isTransparent;
        mesh.receiveShadow = true;
        mesh.userData.expressID = eid;
        mesh.userData.typeName = typeName;
        // Restore visibility from EVERY hidden bucket (the live device layer's,
        // the roof cutaway's and the device-model layer's) — one rule,
        // `meshShouldBeVisible`, so a fourth bucket cannot be forgotten at one of
        // the sites that re-evaluate a mesh.
        applyMeshVisibility(mesh);
        newMeshes.push(mesh);

        if (!newElementMeshes.has(eid)) newElementMeshes.set(eid, []);
        newElementMeshes.get(eid)!.push(mesh);
      }

      ifcAPI.CloseModel(modelID);

      if (newMeshes.length === 0) throw new Error('IFC file contained no geometry');

      const group = new THREE.Group();
      newMeshes.forEach((m) => group.add(m));

      return installModelGroup(
        {
          format: 'ifc',
          group,
          meshes: newMeshes,
          elementMeshes: newElementMeshes,
          elements: newElements,
          // An IFC's objects ARE its elements; the GLB object layer stays empty
          // (card t_cb0aa19b — the two identities never mix).
          objects: [],
          objectMeshes: new Map<string, THREE.Mesh[]>(),
          coordinationMatrix,
        },
        { url, preserveView, framing, previousMeshes },
      );
    } catch (__caught: unknown) {
      const err = __caught as Error;
      console.error('[useBimScene] model load error:', err);
      modelError.value = err?.message ?? 'Failed to load the model';
      // A failed LIVE SWAP keeps the model on screen and reports false — the
      // next poll retries. A failed load with nothing worth keeping falls back
      // to the placeholder so the viewer stays functional.
      if (preserveView && previousMeshes.length > 0) return false;
      previousMeshes.forEach((mesh) => {
        if (mesh.parent) mesh.parent.remove(mesh);
      });
      modelMeshes.value = [];
      // The placeholder has no elements and no layers: the per-model maps
      // describe the model that just left, so they leave with it.
      elementMeshes.value = new Map();
      ifcElements.value = [];
      resetObjectLayer();
      dropModelGroups();
      _addPlaceholder(s.scene);
      return true; // the placeholder is what is on screen
    } finally {
      modelLoading.value = false;
    }
  }

  // ─── The GLB object layer (card t_cb0aa19b) ─────────────────────────────────
  //
  // A glTF has no semantic tree: its objects ARE its nodes. The model's own
  // names — plus the IFC id an exporter may have left in `extras` — are the only
  // identity a GLB gives, so this section walks the loaded group ONCE and
  // records, for every node:
  //
  //   · a row for the object list (`utils/bimObjects.ts` owns the key rule and
  //     the merge: several buckets of one element are one object, two nodes with
  //     one name are one object);
  //   · on every mesh, `userData.__bimObjectKey` — the object a click selects —
  //     and `userData.__bimObjectPath` — every object the mesh belongs to, from
  //     itself up, which is what `meshShouldBeVisible` reads.
  //
  // One walk, one set of facts; everything else (the toggle, the highlight, the
  // preset field, a widget's anchor) reads what it wrote.

  /**
   * Put `url` on screen if it is not already the revision on screen.
   *
   * The ONE rule every surface asks (the 3D page, an embed, the live swap): the
   * model is the hub's, so a shared scene loads once, and a different revision
   * replaces it without moving the camera (`preserveView` when something was
   * already on screen). Returns whether a model is on screen when it completes.
   */
  async function ensureModel(
    url: string,
    options: BimLoadModelOptions = {},
  ): Promise<boolean> {
    // A swap is only a swap when it is the SAME source: a hub model and a cloud
    // model are different documents even if their URLs coincided, so the bearer
    // is part of the identity of what is on screen.
    if (modelLoaded.value && loadedModelUrl.value === url) return true;
    return loadModelFromUrl(url, {
      preserveView: options.preserveView ?? modelLoaded.value,
      framing: options.framing,
      ...(options.bearer ? { bearer: options.bearer } : {}),
    });
  }

  /**
   * Put a built model on screen — the half every format shares (card
   * t_14696d19: this is the IFC tail, moved out unchanged and fed by whichever
   * builder read the bytes).
   *
   * Everything here runs in ONE synchronous span — from the swap point to
   * `modelLoaded.value = true` — so no frame is ever rendered without a model:
   * the replaced meshes/groups leave the scene in the same tick the new group
   * enters it, and their GPU memory is released only then.
   *
   * The frame: for an IFC, web-ifc moved every vertex by its coordination
   * translation so the float32 geometry stays small, and the group undoes that
   * (`utils/bimFrame.ts`) — the scene's world coordinates ARE the IFC
   * coordinates every stored placement speaks. A glTF has no coordination
   * matrix (`coordinationMatrix` null ⇒ no offset): the file's own frame is
   * displayed as authored, and its widgets (if any) are placed by hand in that
   * same frame.
   *
   * `options.preserveView` is the live swap (card t_4e78c7af): the camera pose
   * and the section-cut height are left exactly as the user set them.
   *
   * `options.framing` is the first load's own question: a caller that will place
   * the opening pose itself (the dashboard's first view preset) passes `false`,
   * so the install sets near/far from the model's size and leaves the POSE alone
   * — one framing, not the loader's aerial frame followed by a flight away from
   * it.
   */
  function installModelGroup(
    built: BuiltBimModel,
    options: { url: string; preserveView: boolean; framing?: boolean; previousMeshes: THREE.Mesh[] },
  ): boolean {
    const s = state.value;
    if (!s) return false;
    const { url, preserveView, previousMeshes } = options;
    const framing = options.framing !== false;
    const group = built.group;
    const newMeshes = built.meshes;
    const newElements = built.elements;
    const coordinationMatrix = built.coordinationMatrix;
    group.userData.__bimGroup = true;

    // ── Swap point — synchronous from here to `modelLoaded.value = true` ────
    // The replaced meshes/groups leave the scene in the SAME tick the new
    // group enters it (no frame is ever rendered without a model), and their
    // GPU memory is released now that nothing references them.
    for (const mesh of previousMeshes) {
      if (mesh.parent && mesh.parent !== group) mesh.parent.remove(mesh);
    }
    for (const child of [...s.scene.children]) {
      if (child.userData.__bimGroup && child !== group) {
        s.scene.remove(child);
        disposeModelGroup(child);
      }
    }
    s.scene.add(group);
    e.modelGroup.value = group;
    modelMeshes.value = newMeshes;
    // A live swap installs new materials: the see-through mode must survive it
    // exactly as the section cut's height does (`preserveView` above).
    if (transparencyEnabled.value) applyModelTransparency();
    elementMeshes.value = built.elementMeshes;
    ifcElements.value = newElements;
    // The object layer travels with the model the same way (card t_cb0aa19b):
    // a device anchor names one of these keys.
    e.bimObjects.value = built.objects;
    e.objectMeshes.value = built.objectMeshes;
    e.loadedModelUrl.value = url;
    e.modelFormat.value = built.format;


    // ── ONE frame: the IFC frame (utils/bimFrame.ts) ────────────────────────
    // web-ifc moved every vertex by its coordination translation so the float32
    // geometry stays small. Undo that on the GROUP, so the scene's world
    // coordinates are the IFC coordinates every stored placement speaks —
    // device-icon widgets (x3d/y3d/z3d) and the saved viewpoints. A stored
    // widget point is then a world point: no conversion
    // anywhere in the app. Must happen BEFORE anything reads the model's
    // bounds, so the camera fit and the clip/shadow frames are IFC-frame too.
    // A glTF carries no matrix, so this step passes it by (offset null).
    const ifcFrameOffset = ifcFrameGroupOffset(coordinationMatrix);
    if (ifcFrameOffset) {
      group.position.set(ifcFrameOffset.x, ifcFrameOffset.y, ifcFrameOffset.z);
      group.updateMatrixWorld(true);
      console.info(
        '[useBimScene] model placed in the IFC frame (group offset ' +
          `${ifcFrameOffset.x.toFixed(3)}, ${ifcFrameOffset.y.toFixed(3)}, ${ifcFrameOffset.z.toFixed(3)} m)`,
      );
    }

    // Fit camera to model — a LOAD does this; a live swap keeps the pose the
    // user is looking from and leaves near/far alone (same model, same scale).
    const box = new THREE.Box3().setFromObject(group);
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z) || 20;
    e.modelCenter.copy(center);
    e.modelCenterInfo.value = { x: center.x, y: center.y, z: center.z };
    e.modelSizeInfo.value = { x: size.x, y: size.y, z: size.z };
    if (!preserveView) {
      // near/far follow the model's own size whether or not this load frames the
      // camera: depth precision is not a framing decision.
      s.camera.near = maxDim * 0.001;
      s.camera.far = maxDim * 100;
      s.camera.updateProjectionMatrix();
      if (framing) {
        s.controls.target.copy(center);
        s.camera.position.copy(center).add(new THREE.Vector3(maxDim, maxDim * 0.8, maxDim));
        s.controls.update();
      }
    }

    modelBoundsY.value = { min: box.min.y, max: box.max.y };
    // Default clip height to the top of the model (disabled by default). A
    // live swap keeps the height the user set for the section cut — and so does
    // a LOAD that arrives while the cut is already ON: the height is then the
    // viewer's own setting (a recalled preset can have set it before the model
    // finished loading, card t_ace34f0b) and the loader must not take it away.
    // The ref and the plane are written together, so the state the app reads is
    // the state the renderer clips with (the plane is inert while the cut is off
    // — the renderer holds no plane then).
    if (!preserveView && !clipEnabled.value) {
      clipHeightY.value = box.max.y;
      clipPlane.constant = box.max.y;
      clipBottomY.value = box.min.y;
      clipPlaneBottom.constant = -box.min.y;
    }

    // ── The walkable floor model (card t_9cf7e787) ─────────────────────────
    // GRAVITY's camera is clamped to the model's OWN surfaces. Two sources,
    // both read off what was just loaded: the room floor finishes' top faces
    // (an IfcCovering — this house has no slab floors; its three IfcSlabs are
    // the roof and terrace structures) and the storeys' own elevations. They
    // only ever answer for the fallback list: the live surface underfoot is
    // measured by `probeWalkSupport`. No height is hardcoded. A glTF has
    // neither storeys nor IFC types: both lists stay empty and the support
    // probe falls back to every mesh, which is what a scan needs anyway (its
    // floor is a mesh named `Floor0`, not an IfcCovering).
    const surfaceTops: number[] = [];
    for (const mesh of newMeshes) {
      if (!isFloorFinishType(String(mesh.userData.typeName ?? ''))) continue;
      const meshBox = new THREE.Box3().setFromObject(mesh);
      if (!meshBox.isEmpty()) surfaceTops.push(meshBox.max.y);
    }
    // The meshes the support probe may land on (card t_9cf7e787, widened by
    // t_e86ce88e): the floor finishes and the stair structure, plus every
    // PLATE-LIKE surface the model carries. The stair matches by NAME as well as
    // type — this house's flight is a run of `TREAD-…` proxies, not an IfcStair —
    // and the plate rule is what puts the SITE under the walker's feet: the demo
    // house's terrain plates are plain `IfcBuildingElementProxy` blocks named
    // `TR - 015/016`, which no type or name token would ever catch, so a walker
    // that stepped off the terrace found nothing underfoot and froze at a storey
    // level in mid-air (the owner's "gravity mode does not pin you down to the
    // nearest surface"). The rule is geometric and model-agnostic: a box that is
    // thin next to its own footprint, and wide enough to stand on.
    const elementByExpressId = new Map(newElements.map((el) => [el.expressID, el]));
    // A mesh's own semantics, however its format spells them: the IFC path puts
    // the class and the element name on the element row, a GLB carries them in
    // the node's name alone (`utils/bimNav.elementTypeNameFromObjectName`) — and
    // this house's stair is a run of `TREAD-…` proxies, which only that name
    // states (2026-10-18: the walker stayed on the floor finish beneath the
    // flight because no tread ever entered this list).
    const meshTypeName = (mesh: THREE.Mesh) => String(mesh.userData.typeName ?? '');
    const meshObjectName = (mesh: THREE.Mesh) => String(mesh.userData.name ?? mesh.name ?? '');
    // ── the water surfaces (owner, 2026-10-21: *"allow to sink in"*) ──────────
    // Found ONCE per load and skipped everywhere the walk asks "what is underfoot".
    // Hints, in the order the formats state them: the glTF `extras.water` flag, the
    // node/element name ("Pool Water"), and the material name ("Voda (pool water)").
    const meshIsWater = (mesh: THREE.Mesh) => {
      const material = (mesh as THREE.Mesh).material as THREE.Material | THREE.Material[] | undefined;
      const materialName = Array.isArray(material)
        ? material.map((m) => m.name).join(' ')
        : material?.name ?? '';
      return isWaterSurface({
        flag: mesh.userData.water === true,
        name: `${meshObjectName(mesh)} ${meshTypeName(mesh)}`,
        materialName,
      });
    };
    e.waterMeshes = newMeshes.filter(meshIsWater);
    const waterSet = new Set<THREE.Object3D>(e.waterMeshes);
    const waterTops = e.waterMeshes
      .map((mesh) => new THREE.Box3().setFromObject(mesh))
      .filter((box) => !box.isEmpty())
      .map((box) => box.max.y);
    e.waterLevelY = waterTops.length > 0 ? Math.max(...waterTops) : null;
    e.walkSurfaceTyped = newMeshes.filter((mesh) => {
      if (waterSet.has(mesh)) return false;
      if (isWalkSurfaceType(meshTypeName(mesh))) return true;
      const element = elementByExpressId.get(Number(mesh.userData.expressID));
      return isWalkSurfaceName(String(element?.name ?? meshObjectName(mesh)));
    });
    const typed = new Set(e.walkSurfaceTyped);
    e.walkSurfacePlates = newMeshes.filter((mesh) => {
      if (typed.has(mesh) || waterSet.has(mesh)) return false;
      const meshBox = new THREE.Box3().setFromObject(mesh);
      return isPlateLikeSurfaceBox(meshBox);
    });
    e.walkSurfaces = [...e.walkSurfaceTyped, ...e.walkSurfacePlates];
    // The BLOCKING set (card t_565b83bf): the envelope the walk may not cross —
    // the walls, and nothing else. Read off the loaded model's own element
    // types, never a guess: a glTF/scan that carries no class in its names
    // leaves the list empty and the walk is exactly what it was before, and the
    // GLB path recovers the class from the node name (`buildGltfModel`).
    //
    // WINDOWS are deliberately NOT here (owner, 2026-10-18: *"in simulate mode we
    // should be blocked by walls. can only go through doors and windows.."*):
    // doors were always gaps in the wall solids, and the glass is an opening the
    // walk may take too.
    e.wallMeshes = newMeshes.filter((mesh) => isWallMeshType(meshTypeName(mesh)));
    e.walkLevels = floorLevelsFromSurfaces({
      storeyElevations: newElements.map((el) => el.storeyElevation),
      floorTops: surfaceTops,
      // The model's own vertical extent is the referee for a storey elevation
      // stated in millimetres (this house's export does exactly that).
      boundsY: { min: box.min.y, max: box.max.y },
    });
    // The support cache belongs to the meshes that just left; the walker's
    // own position is NOT touched — a live swap must not teleport it.
    e.supportProbe = null;
    e.supportY = null;
    console.info(
      '[useBimScene] walkable levels (m): ' +
        (e.walkLevels.length > 0 ? e.walkLevels.map((level) => level.toFixed(2)).join(', ') : '(none)') +
        ` | floor finishes: ${surfaceTops.length}` +
        ` | walk surfaces: ${e.walkSurfaces.length}` +
        ` (typed ${e.walkSurfaceTyped.length}, plate ${e.walkSurfacePlates.length})` +
        ` | walls that block: ${e.wallMeshes.length}` +
        ` | model Y: ${box.min.y.toFixed(2)} … ${box.max.y.toFixed(2)}`,
    );

    // Fit shadow frustum to the loaded model
    updateShadowFrustum();

    modelLoaded.value = true;
    modelRevision.value += 1;
    // A LOAD frames the model (the fit above), and that is a pose a walk cannot be left
    // standing in: the app opens in the mode it was left in (`storeNavMode`), so a reload
    // put the body at the aerial fit pose — off the model, with nothing underfoot, which
    // is the owner's *"the gravity mode is still off screen"* (2026-10-14). Re-entering
    // the walk HERE, with the walk model just measured, checks the place and stands the
    // body on the model — in the same tick as the fit, so no frame is ever painted
    // between them and no zoom into the mode is seen at all.
    //
    // A live SWAP keeps its own rule and is not settled: the model is the one already on
    // screen (edits to it included), so the walker's position is deliberate and stays.
    if (!preserveView && navMode.value === 'gravity') enterFirstPerson();
    invalidate();
    return true;
  }

  /**
   * Remove every model group from the scene and release its GPU memory.
   * The element editor's ghosted duplicates share their source's geometry and
   * one material between them, so a shared resource is disposed more than once
   * here — three treats that as a no-op, and a leak is the alternative.
   */
  function disposeModelGroup(group: THREE.Object3D): void {
    group.traverse((child) => {
      const mesh = child as THREE.Mesh;
      if ((mesh as { isMesh?: boolean }).isMesh !== true) return;
      mesh.geometry?.dispose();
      const material = mesh.material as THREE.Material | THREE.Material[] | undefined;
      if (Array.isArray(material)) material.forEach((one) => one.dispose());
      else material?.dispose();
    });
  }

  function dropModelGroups(): void {
    const s = state.value;
    if (!s) return;
    const groups = s.scene.children.filter((c) => c.userData.__bimGroup);
    groups.forEach((group) => s.scene.remove(group));
    groups.forEach((group) => disposeModelGroup(group));
    e.modelGroup.value = null;
    e.loadedModelUrl.value = null;
  }

  /** Resize the directional-light shadow frustum to enclose the loaded model. */
  function updateShadowFrustum() {
    const s = state.value;
    if (!s || modelMeshes.value.length === 0) return;
    const box = new THREE.Box3();
    modelMeshes.value.forEach((m) => box.expandByObject(m));
    if (box.isEmpty()) return;
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    e.modelCenter.copy(center);
    e.modelCenterInfo.value = { x: center.x, y: center.y, z: center.z };
    e.modelSizeInfo.value = { x: size.x, y: size.y, z: size.z };
    const halfDim = Math.max(size.x, size.y, size.z) * 0.85;
    s.sun.target.position.copy(center);
    s.sun.target.updateMatrixWorld();
    s.sun.shadow.camera.left = -halfDim;
    s.sun.shadow.camera.right = halfDim;
    s.sun.shadow.camera.top = halfDim;
    s.sun.shadow.camera.bottom = -halfDim;
    s.sun.shadow.camera.far = s.sun.position.length() + halfDim * 2;
    s.sun.shadow.camera.near = 1;
    s.sun.shadow.camera.updateProjectionMatrix();
  }

  /**
   * Exposure and interior fill for the hour, when auto lighting is on.
   *
   * The scene's `exposure`/`ambientBoost` refs ARE the auto values while it is
   * on — the panel's sliders read them, so what the panel shows is what the
   * scene is doing. Turning auto off leaves the last auto values in place, which
   * is exactly the handover the operator asked for: the sliders pick up from
   * where the sun left them.
   */
  function applyAutoLighting(elevDeg: number): void {
    if (!e.autoLighting.value) return;
    const s = state.value;
    if (!s) return;
    const { exposure: autoExposure, ambientBoost: autoBoost } = autoSceneLighting(elevDeg);
    e.exposure.value = autoExposure;
    e.ambientBoost.value = autoBoost;
    s.renderer.toneMappingExposure = autoExposure;
    s.scene.environmentIntensity = BIM_ENV_BASE * autoBoost;
  }

  /** Turn the time-of-day-driven exposure/fill on or off. */
  function setAutoLighting(enabled: boolean): void {
    e.autoLighting.value = enabled;
    if (enabled) {
      // Adopt the hour's own values at once, rather than waiting for the next
      // tick or the next slider move.
      applySunTime(e.timeOfDay.value);
    }
    save();
  }

  /**
   * Move the sun to where the clock says it is — the server's clock, through
   * `services/clockSync`, which measures its offset from the hub over Socket.IO
   * and falls back to this device's own clock when that has never run.
   *
   * The date travels with the hour: a scene left open across midnight (or opened
   * on a stored date) would otherwise put today's time on last week's sun.
   */
  function syncSunClock(): void {
    const now = serverNow();
    const date = localCivilDate(now);
    if (e.solarDate.value !== date) e.solarDate.value = date;
    e.timeOfDay.value = localSolarHour(now);
    applySunTime(e.timeOfDay.value);
  }

  function startSunClock(): void {
    if (e.sunClockTimer) return;
    e.sunClockTimer = setInterval(syncSunClock, SUN_CLOCK_TICK_MS);
  }

  function stopSunClock(): void {
    if (!e.sunClockTimer) return;
    clearInterval(e.sunClockTimer);
    e.sunClockTimer = null;
  }

  /**
   * Whether the sun follows the clock. Turning it on syncs at once and starts
   * the tick; turning it off leaves the sun exactly where it stands.
   */
  function setSunClockSynced(synced: boolean): void {
    e.sunClockSynced.value = synced;
    if (synced) {
      syncSunClock();
      startSunClock();
    } else {
      stopSunClock();
    }
    save();
  }

  /**
   * Set the sun position and scene lighting to simulate a given hour (0–24).
   * Smoothly transitions colours for dawn/dusk/night.
   */
  /**
   * Put an HOUR on the sun — the lights, the sky, the shadows and the backdrop.
   *
   * `persist: false` is the borrow's path: a visiting surface (a room view) may
   * re-apply the hour to restore lighting it took away, and that must not write the
   * house's settings.
   */
  function applySunTime(hour: number, options: { persist?: boolean } = {}) {
    const persist = options.persist !== false;
    const s = state.value;
    if (!s) return;
    e.timeOfDay.value = hour;
    // The backdrop follows the hour whatever the sun is doing — it is a sky, and
    // a sky that froze whenever the sun was switched off would read as a bug. The
    // gradient re-paints the same colours it already had when the sun is off
    // (nothing below has touched them), so this changes nothing for it.
    if (!sunOn.value) {
      paintBackground();
      if (persist) save();
      return;
    }

    // ── True solar position ────────────────────────────────────────────────
    const day = getDayOfYear(e.solarDate.value);
    const { elevDeg, elevRad, azimFromNorthRad } = computeSolarPosition(hour, e.latitude.value, day);

    // ── Auto exposure / interior fill ──────────────────────────────────────
    // Before the lights are set below, because those lines read
    // `e.ambientBoost.value`: the hour's own fill has to be in place for the
    // ambient light and the environment to be scaled by it.
    applyAutoLighting(elevDeg);

    // Place sun in 3D: azimuth measured from north (CW), then rotated by northAngle.
    // Mirror X so sunrise/sunset swap east-west sides while keeping noon on south.
    // The same direction the sky's own sun disc and the sun arrow use, from the
    // one pure rule (`utils/bimSky.sunDirectionAt`).
    const dist = 300;
    const totalAzim = azimFromNorthRad + e.northAngle.value * (Math.PI / 180);
    const center = e.modelCenter;
    const sunDir = sunDirectionAt(elevDeg, totalAzim * (180 / Math.PI));
    s.sun.position.set(center.x + dist * sunDir.x, center.y + dist * sunDir.y, center.z + dist * sunDir.z);
    updateSunRay();

    // ── Lighting colour / intensity based on elevation ─────────────────────
    // Night: elev < -12°  |  pre-dawn/dusk: -12° … 0°  |  twilight: 0° … 15°  |  day: > 15°
    const isNight = elevDeg < -12;
    const isPreDawn = !isNight && elevDeg < 0;
    const isTwilight = elevDeg >= 0 && elevDeg < 15;
    const isMorning = hour <= 12;

    if (isNight) {
      s.sun.color.set(0x1a2a44);
      s.sun.intensity = 0.05;
      s.hemi.color.set(0x0a0e1a);
      s.hemi.groundColor.set(0x060810);
      s.ambient.intensity = 0.12 * e.ambientBoost.value;
      e.skyColor.set(0x07091a);
      e.horizonColor.set(0x0d1020);
    } else if (isPreDawn) {
      // -12° → 0°: night sky gradually brightens, very subtle warm tint near horizon
      const t = (elevDeg + 12) / 12; // 0 at -12°, 1 at 0°
      s.sun.color.set(0x1a2a44);
      s.sun.intensity = 0.05 + t * 0.07;
      s.hemi.color.copy(new THREE.Color(0x0a0e1a).lerp(new THREE.Color(0x1a1f30), t));
      s.hemi.groundColor.set(0x080a14);
      s.ambient.intensity = (0.12 + t * 0.07) * e.ambientBoost.value;
      e.skyColor.copy(new THREE.Color(0x07091a).lerp(new THREE.Color(0x131828), t));
      e.horizonColor.copy(new THREE.Color(0x0d1020).lerp(new THREE.Color(isMorning ? 0x2a1e30 : 0x251a28), t));
    } else if (isTwilight) {
      // 0° → 15°: warm peach/amber tones, transitioning to blue sky
      const t = elevDeg / 15; // 0 at horizon, 1 at 15°
      const warmSky = isMorning ? new THREE.Color(0x3a2840) : new THREE.Color(0x342030);
      const warmHor = isMorning ? new THREE.Color(0xc07040) : new THREE.Color(0xb06030);
      const daySky = new THREE.Color(0x4a7aaa);
      const dayHor = new THREE.Color(0x7aaad0);
      s.sun.color.copy(new THREE.Color(0xffb570).lerp(new THREE.Color(0xfff5e0), t));
      s.sun.intensity = 0.12 + t * 0.55;
      s.hemi.color.copy(new THREE.Color(0x3a3050).lerp(new THREE.Color(0x87ceeb), t));
      s.hemi.groundColor.copy(new THREE.Color(0x4a3a28).lerp(new THREE.Color(0x7cba7c), t));
      s.ambient.intensity = (0.18 + t * 0.1) * e.ambientBoost.value;
      e.skyColor.copy(warmSky.lerp(daySky, t));
      e.horizonColor.copy(warmHor.lerp(dayHor, t));
    } else {
      // Full day: scale intensity with sin(elev). The RoomEnvironment supplies
      // the base fill, so the sun mainly adds directional highlights/shadows.
      const elevFactor = Math.max(0, Math.sin(elevRad));
      s.sun.color.set(0xfff5e0);
      s.sun.intensity = 0.55 + elevFactor * 0.7;
      s.hemi.color.set(0x87ceeb);
      s.hemi.groundColor.set(0x7cba7c);
      s.ambient.intensity = 0.18 * e.ambientBoost.value;
      e.skyColor.set(0x4a7aaa);
      e.horizonColor.set(0x7aaad0);
    }

    // The backdrop follows the same hour the lights just took: the gradient, or
    // the live sky when that is switched on.
    paintBackground();
    updateSkyDiscs();
    updateShadowFrustum();
    updateSunRay();
    invalidate();
    if (persist) save();
  }

  function setSunEnabled(enabled: boolean) {
    const s = state.value;
    if (!s) return;
    e.sunEnabled.value = Boolean(enabled);
    applySunEnabled();
    save();
  }

  /**
   * Put the sun's daylight (or its absence) on the scene from the EFFECTIVE value —
   * the page's own, or the one a room view borrowed.
   *
   * Off is flat neutral lighting: no directional sun, no sky fill, and just enough
   * ambient to read the room by its own lamps.
   */
  function applySunEnabled(): void {
    const s = state.value;
    if (!s) return;
    if (sunOn.value) {
      // Re-apply the current time to restore sun/hemi/ambient to their proper values.
      applySunTime(e.timeOfDay.value, { persist: false });
    } else {
      s.sun.intensity = 0;
      s.hemi.intensity = 0;
      s.ambient.intensity = 0.6 * e.ambientBoost.value;
      updateSunRay();
      invalidate();
    }
  }

  /**
   * ONE rule for what is drawn: the live device layer's standing-in set, the
   * roof cutaway's lid (card t_a41a75f6) and the device-model layer's twins.
   *
   * Every setter below re-evaluates through this function instead of repeating
   * the conjunction at each call site.
   */
  function meshShouldBeVisible(mesh: THREE.Mesh): boolean {
    const eid = mesh.userData.expressID as number | undefined;
    if (eid === undefined) return true;
    if (e.liveHiddenElements.value.has(eid)) return false;
    if (e.cutHiddenElements.value.has(eid)) return false;
    if (e.modelHiddenElements.value.has(eid)) return false;
    return true;
  }

  function applyMeshVisibility(mesh: THREE.Mesh): void {
    mesh.visible = meshShouldBeVisible(mesh);
  }

  /** Re-evaluate every mesh of these elements (the buckets changed). */
  function refreshElementVisibility(expressIDs: Iterable<number>): void {
    for (const expressID of expressIDs) {
      for (const mesh of elementMeshes.value.get(expressID) ?? []) applyMeshVisibility(mesh);
    }
  }

  /**
   * The live device layer's standing-in set (card t_f81aa9a1): the baked twins it
   * renders itself. Applied exactly like a visibility toggle — every affected
   * mesh is re-evaluated against ALL hidden sets, so a layer toggle or an
   * element toggle can never un-hide a twin that has a live group on it.
   * Idempotent: an unchanged set is a no-op (the layer reconciles often).
   */
  function setLiveHiddenElements(ids: Iterable<number>) {
    const next = new Set(ids);
    const previous = e.liveHiddenElements.value;
    if (next.size === previous.size && [...next].every((id) => previous.has(id))) return;
    e.liveHiddenElements.value = next;
    refreshElementVisibility(new Set([...previous, ...next]));
    cameraSerial.value++;
    invalidate();
  }

  /**
   * The roof cutaway's lid (card t_a41a75f6): the elements the avatar layer is
   * standing in for while the driven walker is under a roof. Its own bucket on
   * purpose — it is neither the operator's hiding nor the live device layer's
   * standing-in set, it must survive a layer toggle and a re-serve without
   * popping back, and it must be restored the moment the walker steps outside.
   * Idempotent, like the two above (the layer reconciles on every move).
   */
  function setCutHiddenElements(ids: Iterable<number>) {
    const next = new Set(ids);
    const previous = e.cutHiddenElements.value;
    if (next.size === previous.size && [...next].every((id) => previous.has(id))) return;
    e.cutHiddenElements.value = next;
    refreshElementVisibility(new Set([...previous, ...next]));
    cameraSerial.value++;
    invalidate();
  }

  /**
   * The device-model layer's standing-in set (card t_07645b98): the baked twins
   * the app draws its own device model for. Applied exactly like the two sets
   * above — every affected mesh is re-evaluated against ALL hidden buckets, so a
   * layer toggle or an element toggle can never un-hide a twin that has a live
   * model on it. Idempotent: an unchanged set is a no-op (the layer reconciles
   * often).
   */
  function setModelHiddenElements(ids: Iterable<number>) {
    const next = new Set(ids);
    const previous = e.modelHiddenElements.value;
    if (next.size === previous.size && [...next].every((id) => previous.has(id))) return;
    e.modelHiddenElements.value = next;
    refreshElementVisibility(new Set([...previous, ...next]));
    cameraSerial.value++;
    invalidate();
  }

  // ─── The GLB object layer's operations (card t_cb0aa19b) ─────────────────────
  //
  // Everything the layout drives over a GLB: the lookups a device anchor and a
  // device model need, and the key a picked mesh carries.
  // read by the ONE visibility rule above.

  /** The object a picked mesh belongs to (null for a mesh the file left unnamed). */
  function objectKeyAt(object: THREE.Object3D | null | undefined): string | null {
    let node: THREE.Object3D | null = object ?? null;
    while (node) {
      const key = node.userData?.__bimObjectKey;
      if (typeof key === 'string' && key.length > 0) return key;
      node = node.parent;
    }
    return null;
  }

  /** The meshes of one object (empty for a key this revision does not carry). */
  function objectMeshesOf(key: string): THREE.Mesh[] {
    return e.objectMeshes.value.get(key) ?? [];
  }

  /** World-space bounds of one object's meshes (an empty box when it has none). */
  function objectBounds(key: string): THREE.Box3 {
    const box = new THREE.Box3();
    for (const mesh of objectMeshesOf(key)) box.expandByObject(mesh);
    return box;
  }

  /** The object layer leaves with the model it described (an error, a clear). */
  function resetObjectLayer(): void {
    e.bimObjects.value = [];
    e.objectMeshes.value = new Map();
  }

  /**
   * The cut is a RANGE now (owner, 2026-10-18: *"we would need to implement bottom
   * cut as well"*): two clipping planes the renderer applies together — the top
   * one removes everything over `topY`, the bottom one everything under
   * `bottomY`. Both values are written to their refs in the same call, so the
   * state the app reads is the state the renderer clips with.
   */
  function setClipRange(topY: number, bottomY: number) {
    const s = state.value;
    if (!s) return;
    const top = Number.isFinite(topY) ? topY : clipHeightY.value;
    const bottom = Number.isFinite(bottomY) ? bottomY : clipBottomY.value;
    clipPlane.constant = top;
    clipPlaneBottom.constant = -bottom;
    clipHeightY.value = top;
    clipBottomY.value = bottom;
    if (!clipEnabled.value) {
      clipEnabled.value = true;
      s.renderer.localClippingEnabled = true;
    }
    s.renderer.clippingPlanes = [clipPlane, clipPlaneBottom];
    // The band is the HOUSE's, the see-through's sibling: it is remembered on the
    // hub (paced, like every other view setting), so a reload — or the dashboard
    // card, or another device — opens cut the way it was left. A WALK does not own
    // the band: its own writes (`guardWalkCut`) are not recorded, and the
    // operator's band comes back with the turntable.
    if (navMode.value === 'orbit') save();
    invalidate();
  }

  /** The cut's ceiling alone — the operator's slider, and the drone's own drive. */
  function setClipHeight(y: number) {
    setClipRange(y, clipBottomY.value);
  }

  /** The cut's floor — a storey band, or the operator's lower slider. */
  function setClipBottom(y: number) {
    setClipRange(clipHeightY.value, y);
  }

  /**
   * (The walk's floor and wall casts used to ask a `clipRemovesPoint` helper here.
   * They no longer consult the cut at all — the band is a VIEW and the body stands
   * on the authored building, see `probeWalkSupport` / `probeWallAhead` and the
   * owner's 2026-10-18 report. A cut wall still blocks; a cut floor still carries.)
   */

  function disableClip() {
    const s = state.value;
    if (!s) return;
    clipEnabled.value = false;
    s.renderer.localClippingEnabled = false;
    s.renderer.clippingPlanes = [];
    if (navMode.value === 'orbit') save();
    invalidate();
  }

  /**
   * The building's storeys, handed over by the layout's level document (owner,
   * 2026-10-19: *"cap to the floor levels"*). The drone's cut is the ceiling of the
   * storey the driven body stands in, so the scene reads the spans rather than the
   * floor lines it walks on. An empty list (a model with no levels) cuts nothing.
   */
  function setStoreySpans(spans: readonly { floorY: number; ceilingY: number }[]): void {
    e.storeySpans = spans
      .filter((span) => Number.isFinite(span?.floorY) && Number.isFinite(span?.ceilingY))
      .map((span) => ({ floorY: span.floorY, ceilingY: span.ceilingY }));
  }

  // ─── Model transparency (owner, 2026-10-17) ───────────────────────────────
  //
  // The cut hides a band of the model; transparency lets the eye through all of
  // it, so the inside is readable without moving the camera. It is a MATERIAL
  // state, not a second copy of the model: every model mesh's material is put
  // into transparent blend mode with a scaled opacity, and its authored look is
  // kept in `materialGhostLook`, so switching the mode off restores the glass,
  // the colours and the depth writes byte-for-byte.
  //
  // The state lives in the scene (not only in the layout) because a live model
  // swap installs NEW materials: `installModelGroup` re-applies the current
  // setting, exactly as the loader keeps the section cut's height (card
  // t_ace34f0b).

  /** The material or materials of one model mesh. */
  function meshMaterials(mesh: THREE.Mesh): THREE.Material[] {
    return Array.isArray(mesh.material) ? mesh.material : [mesh.material];
  }

  /** The slider's own range: a round percent 10–90, the default on nonsense. */
  function clampTransparencyPercent(percent: number): number {
    if (!Number.isFinite(percent)) return BIM_TRANSPARENCY_DEFAULT_PERCENT;
    return Math.min(
      BIM_TRANSPARENCY_MAX_PERCENT,
      Math.max(BIM_TRANSPARENCY_MIN_PERCENT, Math.round(percent)),
    );
  }

  /** Put the current setting on every model material (or take it back off). */
  function applyModelTransparency(): void {
    const enabled = transparencyEnabled.value;
    const percent = clampTransparencyPercent(transparencyPercent.value);
    const ghostOpacity = enabled ? 1 - percent / 100 : 1;
    for (const mesh of modelMeshes.value) {
      for (const material of meshMaterials(mesh)) {
        let authored = materialGhostLook.get(material);
        if (!authored) {
          authored = {
            opacity: material.opacity,
            transparent: material.transparent,
            depthWrite: material.depthWrite,
          };
          materialGhostLook.set(material, authored);
        }
        const nextTransparent = enabled || authored.transparent;
        const transparentChanged = material.transparent !== nextTransparent;
        material.opacity = authored.opacity * ghostOpacity;
        material.transparent = nextTransparent;
        // A transparent shell must not write depth: a nearer wall would otherwise
        // erase everything behind it and the "see-through" model would still be
        // a solid one at every silhouette.
        material.depthWrite = enabled ? false : authored.depthWrite;
        if (transparentChanged) material.needsUpdate = true;
      }
    }
    invalidate();
  }

  /** Turn the see-through mode on at a percent (the slider's write path). */
  function setModelTransparency(percent: number): void {
    transparencyPercent.value = clampTransparencyPercent(percent);
    transparencyEnabled.value = true;
    applyModelTransparency();
    save();
  }

  /** Turn it off: every material is put back on its authored look. */
  function disableModelTransparency(): void {
    if (!transparencyEnabled.value) return;
    transparencyEnabled.value = false;
    applyModelTransparency();
    save();
  }

  function _addPlaceholder(scene: THREE.Scene) {
    const geo = new THREE.BoxGeometry(10, 3, 6);
    const mat = new THREE.MeshLambertMaterial({ color: 0x4488aa, wireframe: false });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.y = 1.5;
    scene.add(mesh);
    modelMeshes.value = [mesh];
    if (transparencyEnabled.value) applyModelTransparency();
    // The placeholder is not a model: it has no objects to list either.
    resetObjectLayer();
    e.modelCenter.set(0, 1.5, 0);
    e.modelCenterInfo.value = { x: 0, y: 1.5, z: 0 };
    e.modelSizeInfo.value = { x: 10, y: 3, z: 6 };

    // Fit camera
    if (state.value) {
      state.value.controls.target.set(0, 1.5, 0);
      state.value.camera.position.set(18, 12, 18);
      state.value.controls.update();
    }

    invalidate();
  }

  /**
   * Raycast from a pointer event into the IFC model meshes.
   * Returns the first hit the RENDERER still shows, or null.
   *
   * `THREE.Raycaster` ignores `renderer.clippingPlanes`, so with the section cut
   * on the removed roof/facade still produces the nearest hit: a click on a lamp
   * visible in the cut-open room selected the phantom slab in front of it (card
   * t_247ec0f9). The cut is therefore applied to the hit list exactly as the
   * renderer applies it to fragments (`filterUnclippedHits`) — read from the
   * scene's own `clipEnabled`/`clipPlane` pair, which are the same planes the
   * renderer was handed. With the cut off the filter is a pass-through, so
   * cut-off picks are byte-identical to before (the P15 rule's control case).
   */
  function raycastFromPointer(event: MouseEvent): THREE.Intersection | null {
    const el = container();
    const s = state.value;
    if (!el || !s || modelMeshes.value.length === 0) return null;

    const rect = el.getBoundingClientRect();
    const ndc = new THREE.Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1,
    );
    s.raycaster.setFromCamera(ndc, s.camera);
    const hits = s.raycaster.intersectObjects(modelMeshes.value, false);
    // Two mirrors, one rule: the section cut removes fragments (a plane the
    // raycaster knows nothing about) and the hidden buckets remove meshes —
    // including the roof cutaway's lid. `THREE.Raycaster` does NOT skip
    // invisible objects (three r184: `intersect()` only tests layers), so a pick
    // must drop them itself or a click inside a cut-away house lands on a roof
    // that is not on screen (card t_a41a75f6; the rule of card t_9cf7e787).
    const drawn = hits.filter((hit) => hit.object.visible !== false);
    const visible = filterUnclippedHits(drawn, clipEnabled.value ? [clipPlane, clipPlaneBottom] : null);
    return visible.length > 0 ? visible[0] : null;
  }

  /**
   * Create a CSS2DObject (HTML label) at a world-space position.
   * The element is owned by the caller — attach any DOM content to `el`.
   */
  function createLabel(el: HTMLElement, position: THREE.Vector3Like): CSS2DObject {
    const s = state.value;
    const label = new CSS2DObject(el);
    label.position.set(position.x, position.y, position.z);
    s?.scene.add(label);
    invalidate();
    return label;
  }

  function removeLabel(label: CSS2DObject) {
    state.value?.scene.remove(label);
    invalidate();
  }

  /**
   * Animate the camera smoothly to a target position/look-at.
   *
   * `onArrive` runs once the animation has finished — a viewpoint that carries
   * a first-person mode must enter it on the pose it ARRIVED at, not on the pose
   * it started from (the walker's head and feet are derived from the camera).
   */
  /**
   * The nearest pose the orbit can actually hold: the same radius, with the polar
   * angle pulled inside `[ORBIT_MIN_POLAR_ANGLE, ORBIT_MAX_POLAR_ANGLE]`.
   *
   * Clamping a pose up front is what keeps `controls.update()` from fighting an
   * animation. `flyTo` writes `camera.position` from its interpolation and then
   * updates the controls; the controls clamp the polar angle, i.e. they MOVE the
   * camera back — so the flight lerps it out and the clamp drags it back, every
   * frame. Measured on the operator's page: a camera pinned at `target.y + 0.08`
   * (exactly 89°) oscillating across the target 5 m at a time, which is what he
   * called *"it spinned"* (owner, 2026-10-24).
   */
  function poseInsideOrbitRange(position: THREE.Vector3Like, target: THREE.Vector3Like): THREE.Vector3 {
    const out = new THREE.Vector3(position.x, position.y, position.z);
    const offset = out.clone().sub(new THREE.Vector3(target.x, target.y, target.z));
    const radius = offset.length();
    if (radius < 1e-4) return out;
    const spherical = new THREE.Spherical().setFromVector3(offset);
    const phi = Math.min(Math.max(spherical.phi, ORBIT_MIN_POLAR_ANGLE), ORBIT_MAX_POLAR_ANGLE);
    if (Math.abs(phi - spherical.phi) < 1e-4) return out;
    spherical.phi = phi;
    return out.copy(new THREE.Vector3().setFromSpherical(spherical).add(new THREE.Vector3(target.x, target.y, target.z)));
  }

  function flyTo(position: THREE.Vector3Like, target: THREE.Vector3Like, durationMs = 800, onArrive?: () => void) {
    const s = state.value;
    if (!s) return;

    if (e.flyToAnimationId.value) {
      cancelAnimationFrame(e.flyToAnimationId.value);
      e.flyToAnimationId.value = 0;
    }

    const startPos = s.camera.position.clone();
    const startTarget = s.controls.target.clone();
    // The END pose is brought inside the orbit's own limits once, here — not by the
    // controls on every frame of the flight (see `poseInsideOrbitRange`).
    const endPos = poseInsideOrbitRange(position, target);
    e.lastFlight = {
      at: Math.round(performance.now()),
      pos: [Math.round(endPos.x * 100) / 100, Math.round(endPos.y * 100) / 100, Math.round(endPos.z * 100) / 100],
      target: [Math.round(target.x * 100) / 100, Math.round(target.y * 100) / 100, Math.round(target.z * 100) / 100],
      durationMs,
    };
    const endTarget = new THREE.Vector3(target.x, target.y, target.z);
    const start = performance.now();
    const flightTarget = new THREE.Vector3();

    function step(now: number) {
      const t = Math.min((now - start) / durationMs, 1);
      const ease = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t; // ease in-out quad
      s!.camera.position.lerpVectors(startPos, endPos, ease);
      // The target is kept in step and the camera looks at it directly: calling
      // `controls.update()` here let the controls clamp the polar angle mid-flight,
      // which is the camera fighting itself (measured: an oscillation of ~5 m per
      // frame at the horizon, *"it spinned"*).
      flightTarget.lerpVectors(startTarget, endTarget, ease);
      s!.controls.target.copy(flightTarget);
      s!.camera.lookAt(flightTarget);
      invalidate();
      if (t < 1) {
        e.flyToAnimationId.value = requestAnimationFrame(step);
        return;
      }

      e.flyToAnimationId.value = 0;
      s!.controls.target.copy(endTarget);
      s!.controls.update();
      onArrive?.();
    }
    e.flyToAnimationId.value = requestAnimationFrame(step);
  }

  /**
   * Put the camera on a pose NOW, with no flight, and with the overlay's motion
   * gate left closed.
   *
   * The opening view is not a move the operator made — it is where the page
   * STARTS — so flying there (an 800 ms animation whose every frame hides the
   * pin layers, card t_72106dc1) is what the owner saw as a page that paints its
   * devices, loses them, frames the house, brings them back and then flies
   * somewhere else. A load is framed by placing the pose, not by flying to it,
   * and `resetCameraSettlePose()` is what keeps the gate from reading the
   * placement as a move on the very next frame.
   */
  /**
   * The lowest y the camera may sit at: the model's own ground, plus a little air.
   * Cached per model revision — this runs on the frames of an orbit.
   */
  function groundFloorY(): number {
    const revision = e.modelRevision.value;
    if (e.groundFloorRevision !== revision) {
      e.groundFloorRevision = revision;
      const box = modelBoxOf();
      e.groundFloorY = box ? box.min.y + 0.35 : 0.35;
    }
    return e.groundFloorY;
  }

  /**
   * The orbit radius may never reach zero.
   *
   * At radius 0 the camera IS its target: the azimuth is `atan2(0, 0)`, i.e.
   * undefined, and every later `controls.update()` throws the camera to a random
   * azimuth at the polar limit — measured on the operator's page as a camera pinned
   * at `target.y + 0.28` with its azimuth jumping 20 m per frame, which is what he
   * called *"it still spinns !!!"* (owner, 2026-10-24). A collapsed radius is not a
   * view: it is pushed back out along the last direction the orbit actually had.
   */
  function keepOrbitRadius(): void {
    const s = state.value;
    if (!s) return;
    const offset = s.camera.position.clone().sub(s.controls.target);
    const radius = offset.length();
    if (radius >= MIN_ORBIT_RADIUS) {
      e.lastOrbitDir = offset.normalize();
      return;
    }
    const dir = e.lastOrbitDir
      ? e.lastOrbitDir.clone()
      : new THREE.Vector3(0.6, 0.5, 0.6).normalize();
    if (radius > 1e-4) dir.copy(offset).normalize();
    s.camera.position.copy(s.controls.target).addScaledVector(dir, MIN_ORBIT_RADIUS * 2);
    s.controls.update();
    invalidate();
  }

  /**
   * Drop any momentum the controls are holding (`sphericalDelta`, `panOffset`,
   * `scale`). A drag that was cut short by leaving the page otherwise survives in
   * the cached scene and is replayed on the way back.
   */
  function clearControlsMomentum(): void {
    const c = state.value?.controls as unknown as Record<string, unknown> | undefined;
    if (!c) return;
    for (const key of ['sphericalDelta', '_sphericalDelta', 'panOffset', '_panOffset']) {
      const v = c[key] as { set?: (x: number, y: number, z: number) => void; theta?: number; phi?: number; radius?: number } | undefined;
      if (!v) continue;
      v.set?.(0, 0, 0);
      if (typeof v.theta === 'number') v.theta = 0;
      if (typeof v.phi === 'number') v.phi = 0;
      if (typeof v.radius === 'number') v.radius = 0;
    }
    if (typeof c['scale'] === 'number') c['scale'] = 1;
  }

  /** See the call in `renderFrame`: an orbit may not end under the terrain. */
  function keepCameraAboveGround(): void {
    const s = state.value;
    // Never during a flight: the flight owns the camera, and a `controls.update()`
    // mid-animation is the fight this guard exists to avoid.
    if (!s || e.flyToAnimationId.value !== 0) return;
    const floor = groundFloorY();
    if (s.camera.position.y >= floor) return;
    s.camera.position.y = floor;
    s.controls.update();
    invalidate();
  }

  function placeCamera(position: THREE.Vector3Like, target: THREE.Vector3Like) {
    const s = state.value;
    if (!s) return;
    if (e.flyToAnimationId.value) {
      cancelAnimationFrame(e.flyToAnimationId.value);
      e.flyToAnimationId.value = 0;
    }
    const placed = poseInsideOrbitRange(
      { x: position.x, y: Math.max(position.y, groundFloorY()), z: position.z },
      target,
    );
    s.camera.position.set(placed.x, placed.y, placed.z);
    s.controls.target.set(target.x, target.y, target.z);
    s.controls.update();
    // The pose the NEXT move is measured against is this one: the gate compares
    // against `cameraSettled*`, so a placement that skipped this would latch
    // `cameraMoving` for the settle window and blank the layers anyway.
    clearControlsMomentum();
    resetCameraSettlePose();
    clearCameraSettleTimer();
    cameraMoving.value = false;
    e.framedOnce = true;
    placedPoseThisVisit = true;
    invalidate();
  }

  /** Has this scene been given a pose since it was created? (A revisit must keep it.) */
  function hasFramedPose(): boolean {
    return e.framedOnce;
  }

  /**
   * Drop the locally cached model (meshes + loaded flag) so the next
   * loadModelFromUrl() fetches and renders a fresh copy. Used after a model
   * replacement upload — the in-memory cached copy must not survive.
   */
  function clearModel() {
    modelLoaded.value = false;
    modelLoading.value = false;
    modelError.value = null;
    modelMeshes.value.forEach((mesh) => {
      if (mesh.parent) mesh.parent.remove(mesh);
    });
    modelMeshes.value = [];
    resetObjectLayer();
    dropModelGroups();
  }

  function dispose() {
    // The house keeps what the operator last set, even as this scene goes: a
    // queued write is sent now rather than left to a page that no longer exists.
    flushHubSave();
    delete (window as unknown as Record<string, unknown>).__bimShaderCheck;
    delete (window as unknown as Record<string, unknown>).__bimLighting;
    delete (window as unknown as Record<string, unknown>).__bimTrace;
    delete (window as unknown as Record<string, unknown>).__bimTrap;
    stopRenderLoop();
    stopCameraSerialFrame();
    stopSunClock();
    stopPrecipitationLoop();
    e.reducedMotionQuery?.removeEventListener?.('change', onReducedMotionChange);
    e.reducedMotionQuery = null;
    e.gradCanvas.remove();
    if (e.flyToAnimationId.value) {
      cancelAnimationFrame(e.flyToAnimationId.value);
      e.flyToAnimationId.value = 0;
    }
    const s = state.value;
    clearCameraInteractionTimeout();
    cameraInteracting.value = false;
    clearCameraSettleTimer();
    cameraMoving.value = false;
    detachKeyboardListeners();
    detachPointerLook();
    if (!s) {
      sceneCache.delete(sceneKey);
      return;
    }
    if (e.wheelListener) {
      s.renderer.domElement.removeEventListener('wheel', e.wheelListener, { capture: true });
      e.wheelListener = null;
    }
    e.pressedKeys.clear();
    e.keyboardListenersAttached = false;
    e.keydownHandler = null;
    e.keyupHandler = null;
    s.controls.dispose();
    // Release the WebGL CONTEXT, not just three's objects (owner, 2026-11-02: after
    // a round trip through another hub the 3D page came back blank and asked the hub
    // for nothing). `renderer.dispose()` frees geometries, textures and programs but
    // keeps the context alive until the GC, and a phone's WKWebView hands out very
    // few contexts: every hub switch builds a NEW renderer, so the third one simply
    // could not be created. `forceContextLoss()` is the only call that gives the
    // context back.
    try {
      s.renderer.forceContextLoss();
    } catch {
      /* a context that is already gone needs no loss */
    }
    s.renderer.dispose();
    e.envTexture?.dispose();
    e.envTexture = null;
    // The sky's own bodies go with the scene, textures and all.
    for (const disc of [e.sunDisc, e.moonDisc]) {
      if (!disc) continue;
      s.skyScene.remove(disc);
      (disc.material as THREE.SpriteMaterial).map?.dispose();
      disc.material.dispose();
    }
    e.sunDisc = null;
    e.moonDisc = null;
    e.moonDiscPhase = null;
    s.renderer.domElement.remove();
    s.labelRenderer.domElement.remove();
    state.value = null;
    // Remove from cache so a fresh scene is created next time
    sceneCache.delete(sceneKey);
  }

  // `disposeAllBimScenes` (a hub switch) reaches this instance's teardown here:
  // the scene outlives the component, so the closure — not the component — owns
  // the only way to release it.
  e.disposeScene = dispose;

  liveBimViews += 1;
  onUnmounted(() => {
    liveBimViews = Math.max(0, liveBimViews - 1);
    suspend();
  });

  /**
   * The facts a restored camera pose is judged on (card t_0dfa5b68), measured on
   * the live scene: the loaded model's own world box, and whether the point lies
   * inside one of the model's meshes — the wall/slab/roof volumes a camera must
   * never sit in. The verdict itself is `utils/bimPoseGuard.judgePose`, which is
   * pure and unit-pinned; this only measures.
   *
   * The box test over-approximates on purpose (a mesh's world box is the convex
   * hull of its bounds) — the failure it prevents is a camera inside a wall,
   * which paints the grey screen the owner reported. Meshes of the LIVE layers
   * (device models, shades, avatars) are not consulted: they are not the model
   * the stored poses are authored against.
   */
  function poseFactsAt(point: { x: number; y: number; z: number }): PoseFacts {
    const meshes = modelMeshes.value;
    const modelBox = modelBoxOf();
    if (meshes.length === 0 || !modelBox) return { modelBox: null, insideSolid: false };
    const p = new THREE.Vector3(point.x, point.y, point.z);
    const meshBox = new THREE.Box3();
    let insideSolid = false;
    for (const mesh of meshes) {
      meshBox.setFromObject(mesh);
      if (!meshBox.isEmpty() && meshBox.containsPoint(p)) {
        insideSolid = true;
        break;
      }
    }
    return { modelBox, insideSolid };
  }

  /**
   * The navigation modes' own numbers, for the rigs and the handoff (card
   * t_9cf7e787): which mode is active, where the camera is, what the walker
   * stands on (the measured surface, the resolved feet and the model's own
   * level list) and how many steps were refused. Read-only, like the device
   * layers' debug hooks.
   */
  function navDebug() {
    const s = state.value;
    return {
      mode: navMode.value,
      pointerLocked: pointerLocked.value,
      pointerLockAllowed: e.pointerLockAllowed.value,
      eyeHeight: NAV_EYE_HEIGHT_M,
      stepUpMax: NAV_STEP_UP_MAX_M,
      camera: s ? { x: s.camera.position.x, y: s.camera.position.y, z: s.camera.position.z } : null,
      target: s ? { x: s.controls.target.x, y: s.controls.target.y, z: s.controls.target.z } : null,
      controlsEnabled: s ? s.controls.enabled : null,
      look: { yaw: e.look.yaw, pitch: e.look.pitch },
      velocity: { ...e.velocity },
      feetY: e.feetY,
      supportY: e.supportY,
      /** What the last probe landed on (card t_e86ce88e) — the rig's probe table. */
      supportHit: e.supportHit ? { ...e.supportHit } : null,
      /** How many meshes the support probe may land on, and how they were chosen. */
      walkSurfaceCount: e.walkSurfaces.length,
      walkSurfaceTyped: e.walkSurfaceTyped.length,
      walkSurfacePlates: e.walkSurfacePlates.length,
      /**
       * The WATER (owner, 2026-10-21): how many water surfaces the model carries, the
       * level they sit at, whether the body's feet are below it (i.e. he is IN the
       * pool), and whether the surface under his feet is a pool floor — the numbers a
       * rig reads to tell "sank into the water" from "stood on the water".
       */
      waterMeshCount: e.waterMeshes.length,
      waterLevelY: e.waterLevelY,
      submerged: e.feetY !== null && e.waterLevelY !== null && e.feetY < e.waterLevelY,
      /**
       * The walls that block the walk (card t_565b83bf): how many meshes the
       * forward probe tests, how many steps they refused, and the last hit it
       * met — the numbers a rig reads to tell "blocked by the facade" from
       * "walked through the doorway".
       */
      wallMeshCount: e.wallMeshes.length,
      wallsBlocked: e.wallsBlocked,
      wallHit: e.wallHit ? { ...e.wallHit } : null,
      /**
       * The walk vs the section cut (owner, 2026-10-18): whether the body's own
       * column is inside a wall, how many times the rescue had to pull it out, where
       * that rescue point is, and whether the walk has widened the operator's band
       * to keep the ground under the feet. The numbers a rig reads to tell "the cut
       * no longer traps the body" from "nothing happened".
       */
      insideWall: e.insideWall,
      rescues: e.rescues,
      lastFree: e.lastFree ? { ...e.lastFree } : null,
      walkCutLifted: e.walkCut.lifted,
      /** The POV camera's eye over the driven feet (owner, 2026-10-20) — the model's own height −5 %. */
      drivenEyeHeight: e.drivenEyeHeight,
      /** SIMULATION's hop (card t_565b83bf): how high the body is over its floor, if at all. */
      jumpOffset: e.jump ? e.jump.offset : 0,
      jumping: Boolean(e.jump && e.jump.active),
      levels: [...e.walkLevels],
      blockedSteps: e.walkBlocked,
      modelRevision: modelRevision.value,
      /**
       * SIMULATION mode's camera view (card t_01ebef40): the user's own stored choice
       * and the rule the camera follows right now (`povCamera` is what the
       * avatar layer hides the driven body for).
       */
      view: simView.value,
      povCamera: isPovCamera(navMode.value, simView.value),
      /** SIMULATION mode's driven body (card t_a41a75f6) — null for every other mode. */
      driven: isThirdPersonMode(navMode.value)
        ? { x: e.driven.x, y: e.driven.y, z: e.driven.z, yawDeg: e.driven.yawDeg, moving: e.driven.moving }
        : null,
      /** What the DRONE camera is asking for — the wheel's own number (owner, 2026-10-13). */
      zoomDistance: e.droneDistance,
      /**
       * The FOLLOW camera's own two numbers (owner, 2026-10-14): the manual orbit
       * the drone is holding, in degrees (0 = directly behind the body), and the
       * look the placement actually hangs the camera on — the body's heading plus
       * that offset. Read-only; a rig asserts "the camera is behind the body" off
       * these and `driven.yawDeg`.
       */
      droneOrbitDeg: (e.droneOrbitOffset * 180) / Math.PI,
      droneLook: isDroneView(navMode.value, simView.value)
        ? droneCameraLook(e.driven.yawDeg, e.droneOrbitOffset, e.look.pitch)
        : null,
      cutHidden: [...e.cutHiddenElements.value],
      /** The device-model layer's standing-in set (card t_07645b98). */
      modelHidden: [...e.modelHiddenElements.value],
    };
  }

  /**
   * The driven avatar's own pose (card t_a41a75f6) — the scene's read for the
   * avatar layer, which draws the body the camera is chasing. Anonymous on
   * purpose: the scene knows where the walker is, the LAYOUT knows whose body
   * that is.
   */
  function drivenPose(): {
    x: number;
    y: number;
    z: number;
    yawDeg: number;
    moving: boolean;
    running: boolean;
  } | null {
    if (!isThirdPersonMode(navMode.value)) return null;
    return {
      x: e.driven.x,
      y: e.driven.y,
      z: e.driven.z,
      yawDeg: e.driven.yawDeg,
      moving: e.driven.moving,
      running: e.driven.running,
    };
  }

  /**
   * The head direction as the pose's own numbers (card t_ace34f0b) — the read a
   * preset stores beside the camera position. In the first-person modes the head
   * is independent of the turntable's target, so it is what the pose cannot
   * derive; in ORBIT it is the target's own direction and the caller need not
   * store it (`storedPoseForLive` drops it there).
   */
  function navLook(): NavLook {
    return { yaw: e.look.yaw, pitch: e.look.pitch };
  }

  /**
   * Adopt a stored head direction (card t_ace34f0b) — the write half of a preset
   * recall: `flyTo` only moves the CAMERA, while a first-person mode re-applies
   * its own `e.look` to the head every frame, so a preset that carries its look
   * is put back on it here, AFTER the mode is restored. In SIMULATION the camera hangs
   * from the look, so the body's placement runs again; nothing else moves, and
   * an unreadable angle is refused rather than turning the head to NaN.
   */
  function adoptNavLook(look: NavLook): void {
    const s = state.value;
    if (!s) return;
    const yaw = Number.isFinite(look.yaw) ? look.yaw : null;
    const pitch = Number.isFinite(look.pitch) ? clampPitch(look.pitch) : null;
    if (yaw === null || pitch === null) return;
    e.look = { yaw, pitch };
    // …and in the DRONE view the look IS the character's facing (the camera hangs
    // behind it), so a recalled pose turns the body rather than leaving the
    // camera pointing at a bearing nothing follows.
    if (isDroneView(navMode.value, e.simView.value)) e.driven.yawDeg = yawDegFromLook(yaw);
    if (!isFirstPersonMode(navMode.value)) return;
    if (isThirdPersonMode(navMode.value)) {
      const placed = placeCameraForView(e.simView.value);
      if (placed) queueCameraSerialUpdate();
    } else {
      applyLookRotation();
    }
    updateCameraAzimuthRefs(s.camera);
    invalidate();
  }

  return {
    state,
    modelLoaded,
    modelLoading,
    modelError,
    modelMeshes,
    modelRevision,
    loadedModelUrl,
    modelFormat,
    cameraSerial,
    cameraInteracting,
    /**
     * The view's motion gate (card t_72106dc1): true while the camera is moving
     * (and for `BIM_CAMERA_SETTLE_MS` after it last did). The layout hides its
     * widget overlay layer off this and stops projecting it while it is up.
     */
    cameraMoving,
    gridVisible,
    toggleGrid,
    ifcElements,
    /**
     * The GLB OBJECT layer (card t_cb0aa19b) — a glTF/USD's own nodes, which are
     * the only tree such a file has, plus the lookups a device anchor needs:
     * `objectMeshesOf` the key → meshes map, `objectKeyAt` the object under a
     * raycast hit and `objectBounds` the box a device model is framed with.
     * Empty for an IFC.
     */
    bimObjects: e.bimObjects,
    objectMeshes: e.objectMeshes,
    objectMeshesOf,
    objectKeyAt,
    objectBounds,
    liveHiddenElements: e.liveHiddenElements,
    setLiveHiddenElements,
    /** The roof cutaway's own bucket (card t_a41a75f6). */
    cutHiddenElements: e.cutHiddenElements,
    setCutHiddenElements,
    /** The device-model layer's own bucket (card t_07645b98). */
    modelHiddenElements: e.modelHiddenElements,
    setModelHiddenElements,
    elementMeshes,
    modelGroup,
    modelBoundsY,
    clipEnabled,
    clipHeightY,
    clipBottomY,
    setClipHeight,
    setClipBottom,
    setClipRange,
    disableClip,
    /**
     * The storeys the drone's cut is capped by (owner, 2026-10-19) — the layout's
     * level document, in metres.
     */
    setStoreySpans,
    /**
     * The model's see-through mode (owner, 2026-10-17) — the cut's own pair: the
     * enabled ref the toolbar and the View tab read, and the two write paths.
     * `setModelTransparency` also keeps the percent for the next model swap.
     */
    transparencyEnabled,
    transparencyPercent,
    setModelTransparency,
    disableModelTransparency,
    /**
     * The two VIEW FILTERS (owner, 2026-10-29: *"maybe we could aslo have show
     * shadows/light filters on both views"*) — the sun's shadows, and the
     * devices' own lights (fixture glow + the pools they throw) — plus the ROOM
     * pages' own filter, one blob for every room page.
     */
    shadowsEnabled: shadowsOn,
    lightsEnabled: lightsOn,
    setShadowsEnabled,
    setLightsEnabled,
    borrowShadows,
    borrowLights,
    /** …and the sun's daylight + the weather sky, the same borrow. */
    sunOn,
    weatherOn,
    borrowSun,
    borrowWeather,
    roomViewFilter: e.roomView,
    patchRoomViewFilter,
    init,
    resize,
    loadModelFromUrl,
    ensureModel,
    raycastFromPointer,
    createLabel,
    removeLabel,
    flyTo,
    /**
     * Put the camera on a pose NOW, no flight and no motion gate — how a LOAD
     * frames itself (the opening view). `flyTo` is the operator's own move.
     */
    placeCamera,
    /** The camera as a view to hand to the 3D page (the Explore doors). */
    captureLiveView,
    navMode,
    groundMode,
    setNavMode,
    enterGravityAt,
    exitNavMode,
    /** Leave a walk for a LOOK — the mode itself is left as the house has it. */
    leaveFirstPersonForLook,
    /**
     * The body's way BACK to its start (owner, 2026-10-20): the layout answers with
     * the person's own spawn, and both the sim bar's button and the R key call it.
     */
    setDrivenResetHandler,
    /**
     * The POV camera's eye over the driven body's feet (owner, 2026-10-20) — the
     * layout writes it from the avatar's own model height.
     */
    setDrivenEyeHeight,
    /**
     * SIMULATION mode's camera view (card t_01ebef40): the stored ref (a Vue ref, so
     * the pill re-renders on a switch), the setter and the toggle.
     */
    simView,
    setSimView,
    toggleSimView,
    /** Open the active view the way it opens (the game's entry and a body switch). */
    openSimView,
    drivenPose,
    placeDrivenAt,
    /** The head direction, and adopting a stored one (card t_ace34f0b). */
    navLook,
    adoptNavLook,
    setPointerLockAllowed,
    pointerLocked,
    navDebug,
    /**
     * A raw support probe — the surface under one point, told by name (card
     * t_e86ce88e). The rigs read it to build the "probe heights" table; nothing
     * in the app calls it, and it moves nothing.
     */
    probeSupportAt: (x: number, z: number, feetY: number) => probeWalkSupportInfo(x, z, feetY),
    /**
     * The facts a restored pose is judged on (card t_0dfa5b68) — the model's own
     * box and whether a point sits inside its meshes. The layout asks this before
     * it flies to a stored preset or picks the opening view; nothing else does.
     */
    poseFactsAt,
    timeOfDay: e.timeOfDay,
    sunEnabled,
    setSunEnabled,
    applySunTime,
    /**
     * The sun's own clock. `sunClockSynced` is the panel's "Synced" state —
     * whether the sun is following the time (server-synced, see
     * `services/clockSync`) rather than a time someone picked —
     * `setSunClockSynced` the toggle and `syncSunClock` a one-shot re-read.
     */
    sunClockSynced: e.sunClockSynced,
    setSunClockSynced,
    syncSunClock,
    /**
     * Auto exposure / interior fill: whether they follow the sun, and the
     * toggle. `utils/bimAutoLighting.ts` holds the rule.
     */
    autoLighting: e.autoLighting,
    setAutoLighting,
    /**
     * The live-weather backdrop. `weatherSkyEnabled` is the switch,
     * `setWeatherSkyEnabled` flips it, and `setWeatherSky` takes the weather the
     * layout read (`useWeather`) — the OWM code and the moon's phase.
     */
    weatherSkyEnabled: e.weatherSkyEnabled,
    setWeatherSkyEnabled,
    setWeatherSky,
    exposure,
    setExposure,
    ambientBoost,
    setAmbientBoost,
    // Low / Mid / High — how hard the fixtures drive the room.
    lightPreset,
    setLightPreset,
    glowScale,
    setGlowScale,
    northAngle,
    setNorthAngle,
    cameraAzimuth,
    /**
     * The ray: a plain line from the sun into the model's centre, drawn when
     * `showSunRay` is on (the Sun panel's "Show ray direction").
     */
    showSunRay,
    toggleSunRay,
    // The compass overlay (the View panel's toggle) and the layout's own half of
    // the view settings — all stored with the rest of the blob, on the hub.
    showCompassOverlay: e.showCompassOverlay,
    viewPrefs: e.uiPrefs,
    patchViewSettings,
    latitude,
    solarDate,
    modelCenterInfo,
    modelSizeInfo,
    setLocation,
    setDate,
    refreshBottomColor,
    invalidate,
    suspend,
    dispose,
    clearModel,
    /** Has this scene been given a pose? The opening view only applies when not. */
    hasFramedPose,
  };
}
